import React, { useState, useEffect, useLayoutEffect, useRef, useContext, useCallback, useMemo } from 'react';
import axios from 'axios';
import { format, addDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday } from 'date-fns';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ChevronLeft, ChevronRight, Users, Home, MapPin, Calendar, Euro, Map as MapIcon, Phone, ClipboardList, GripVertical, Clock, AlertTriangle, X, RefreshCw } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UserContext } from '../../contexts/UserContext';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import AccommodationSummary from './AccommodationSummary';

const API_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';
const API_BASE_URL = `${API_URL}/api`;

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom numbered marker icon creator
const createNumberedIcon = (number, isHighlighted = false) => {
  const size = isHighlighted ? 36 : 28;
  const fontSize = isHighlighted ? 14 : 12;
  const color = isHighlighted ? '#1d4ed8' : '#3b82f6';

  return L.divIcon({
    className: 'custom-numbered-marker',
    html: `<div style="
      background-color: ${color};
      color: white;
      width: ${size}px;
      height: ${size}px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      font-size: ${fontSize}px;
      border: 2px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    ">${number}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2]
  });
};

// Custom project marker icon creator (wind turbine style)
const createProjectIcon = () => {
  const size = 36;
  return L.divIcon({
    className: 'custom-project-marker',
    html: `<div style="
      background-color: #dc2626;
      color: white;
      width: ${size}px;
      height: ${size}px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      font-size: 18px;
      border: 3px solid white;
      box-shadow: 0 3px 6px rgba(0,0,0,0.4);
    ">⚡</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2]
  });
};

// Component to fit map bounds to all markers
const MapFitBounds = ({ coordinates }) => {
  const map = useMap();
  useEffect(() => {
    if (coordinates && coordinates.length > 0) {
      if (coordinates.length === 1) {
        // Single marker - center on it with reasonable zoom
        map.setView([coordinates[0].lat, coordinates[0].lng], 14);
      } else {
        // Multiple markers - fit bounds to show all
        const bounds = L.latLngBounds(coordinates.map(c => [c.lat, c.lng]));
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [coordinates, map]);
  return null;
};

// Component to handle map clicks for placing accommodation marker
const MapClickHandler = ({ onMapClick, disabled }) => {
  const map = useMap();

  useEffect(() => {
    if (disabled) return;

    const handleClick = (e) => {
      const { lat, lng } = e.latlng;
      onMapClick({ lat, lng });
    };

    map.on('click', handleClick);

    // Change cursor to crosshair to indicate clickable
    map.getContainer().style.cursor = 'crosshair';

    return () => {
      map.off('click', handleClick);
      map.getContainer().style.cursor = '';
    };
  }, [map, onMapClick, disabled]);

  return null;
};

const AccommodationCalendar = () => {
  console.log('🏁 AccommodationCalendar component rendering');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useContext(UserContext);
  const companyId = user?.company?.id;

  // URL params for deep linking from alerts
  const urlProjectId = searchParams.get('project');
  const urlUsers = searchParams.get('users');

  const [currentDate, setCurrentDate] = useState(new Date());
  const [accommodations, setAccommodations] = useState([]);
  const [assignments, setAssignments] = useState([]);
  console.log('📊 Component state - assignments length:', assignments.length);
  const [projects, setProjects] = useState([]);
  const [projectAssignments, setProjectAssignments] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showAddAccommodation, setShowAddAccommodation] = useState(false);
  const [draggedUser, setDraggedUser] = useState(null);
  const [editingAccommodation, setEditingAccommodation] = useState(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [assignmentData, setAssignmentData] = useState(null);
  const [resizeState, setResizeState] = useState(null);
  const [showAccommodationModal, setShowAccommodationModal] = useState(false);
  const [selectedAccommodationDetails, setSelectedAccommodationDetails] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showDayDetailsModal, setShowDayDetailsModal] = useState(false);
  const [filteredDay, setFilteredDay] = useState(null);
  const [viewType, setViewType] = useState('monthly'); // 'monthly' or 'weekly'
  const [showMapModal, setShowMapModal] = useState(false);
  const [hoveredAccommodationId, setHoveredAccommodationId] = useState(null);
  const [selectedMapAccommodation, setSelectedMapAccommodation] = useState(null);
  const [geocodedCoordinates, setGeocodedCoordinates] = useState({}); // { accommodationId: { lat, lng } }
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [reservationModalData, setReservationModalData] = useState(null);
  const [activeTab, setActiveTab] = useState('team'); // Only 'team' tab now

  // ============================================================================
  // UNSAVED CHANGES MANAGEMENT
  // ============================================================================
  const [pendingChanges, setPendingChanges] = useState({
    updated: [], // Modified assignments { id, start_date, end_date, room_number, accommodation }
    deleted: [], // Deleted assignment IDs
    created: []  // New assignments (without ID yet)
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [savedAssignments, setSavedAssignments] = useState([]); // Original assignments from server
  const [lastSavedAt, setLastSavedAt] = useState(null); // Timestamp of last save
  const [lastSaveInfo, setLastSaveInfo] = useState(null); // { user, timestamp, changes: {updated, deleted, created} }
  const [showSaveHistoryModal, setShowSaveHistoryModal] = useState(false);
  const [saveHistoryList, setSaveHistoryList] = useState([]); // Full save history from database
  const [showDateRangeModal, setShowDateRangeModal] = useState(false);
  const [tempDateRange, setTempDateRange] = useState({ start: null, end: null });

  // Cell click dropdown state - for quick user assignment
  const [cellClickDropdown, setCellClickDropdown] = useState(null); // { accommodationId, roomNumber, date, x, y }

  // Confirmation modal state - for move/reassign confirmations
  const [confirmationModal, setConfirmationModal] = useState(null); // { title, message, onConfirm, onCancel, confirmText, cancelText }

  // Function to get ISO week number (same as in ResourceBoard)
  const getWeekNumber = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    // Set to nearest Thursday: current date + 4 - current day number
    // Make Sunday's day number 7
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    // Get first day of year
    const yearStart = new Date(d.getFullYear(), 0, 1);
    // Calculate full weeks to nearest Thursday
    const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    return weekNo;
  };

  // Calculate initial 100-day range with today as 3rd day
  const getInitialDateRange = () => {
    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() - 2); // 2 days before today
    const end = new Date(today);
    end.setDate(today.getDate() + 47); // 47 days after today (total: 50 days)

    return { start, end };
  };

  const [customDateRange, setCustomDateRange] = useState(getInitialDateRange());

  const startDate = customDateRange.start;
  const endDate = customDateRange.end;

  // Memoize days array
  const days = useMemo(() =>
    eachDayOfInterval({ start: startDate, end: endDate }),
    [startDate, endDate]
  );

  // Memoize groupDaysByWeek function
  const groupDaysByWeek = useCallback((days) => {
    const weeks = [];
    let currentWeek = [];

    days.forEach((day, index) => {
      currentWeek.push(day);

      // Complete week on Sunday (day 0) after first Sunday, or if it's the last day
      // Skip first Sunday to allow 8-day weeks
      if ((day.getDay() === 0 && index > 0) || index === days.length - 1) {
        weeks.push([...currentWeek]);
        currentWeek = [];
      }
    });

    return weeks;
  }, []);

  // Memoize weeks array
  const weeks = useMemo(() => groupDaysByWeek(days), [days, groupDaysByWeek]);

  // Get check-in people for date range - memoized
  const getCheckInPeople = useCallback(() => {
    if (!projectAssignments) return [];
    
    const checkInPeople = [];
    projectAssignments.forEach(userWithAssignments => {
      if (userWithAssignments.travelDays) {
        userWithAssignments.travelDays.forEach(travel => {
          if (travel.travel_type === 'check_in') {
            const travelDate = new Date(travel.travel_date);
            const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
            const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
            const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
            
            if (travelDateOnly >= startDateOnly && travelDateOnly <= endDateOnly) {
              checkInPeople.push({
                ...userWithAssignments,
                checkInDate: travel.travel_date,
                travelType: 'check_in'
              });
            }
          }
        });
      }
    });
    
    return checkInPeople;
  }, [projectAssignments, startDate, endDate]);

  // Get check-out people for date range - memoized
  const getCheckOutPeople = useCallback(() => {
    if (!projectAssignments) return [];

    const checkOutPeople = [];
    projectAssignments.forEach(userWithAssignments => {
      if (userWithAssignments.travelDays) {
        userWithAssignments.travelDays.forEach(travel => {
          if (travel.travel_type === 'check_out') {
            const travelDate = new Date(travel.travel_date);
            const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
            const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
            const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

            if (travelDateOnly >= startDateOnly && travelDateOnly <= endDateOnly) {
              checkOutPeople.push({
                ...userWithAssignments,
                checkOutDate: travel.travel_date,
                travelType: 'check_out'
              });
            }
          }
        });
      }
    });

    return checkOutPeople;
  }, [projectAssignments, startDate, endDate]);
  
  // Debug: Let's see what the days array contains
  console.log('Days array debug:', {
    startDate: format(startDate, 'yyyy-MM-dd'),
    endDate: format(endDate, 'yyyy-MM-dd'),
    daysCount: days.length,
    firstFewDays: days.slice(0, 15).map((d, i) => ({ index: i, date: format(d, 'yyyy-MM-dd'), dayOfMonth: d.getDate() }))
  });

  useEffect(() => {
    fetchData();
  }, [currentDate, companyId, selectedProject, customDateRange]);

  // Set project from URL parameter when projects are loaded
  useEffect(() => {
    if (urlProjectId && projects.length > 0 && !selectedProject) {
      const projectIdNum = parseInt(urlProjectId);
      const projectExists = projects.find(p => p.id === projectIdNum);
      if (projectExists) {
        console.log('🔗 Setting project from URL:', projectIdNum, projectExists.name);
        setSelectedProject(projectIdNum);
      }
    }
  }, [urlProjectId, projects, selectedProject]);

  // Fetch save history when modal opens
  useEffect(() => {
    if (showSaveHistoryModal) {
      fetchSaveHistory();
    }
  }, [showSaveHistoryModal]);

  const fetchSaveHistory = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_BASE_URL}/accommodation-calendar-save-history/`, { headers });
      setSaveHistoryList(response.data);
    } catch (error) {
      console.error('Error fetching save history:', error);
    }
  };

  // Fetch latest save info on mount
  useEffect(() => {
    const fetchLatestSave = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API_BASE_URL}/accommodation-calendar-save-history/recent/`, { headers });

        if (response.data && response.data.length > 0) {
          const latestSave = response.data[0];
          const fullName = latestSave.saved_by_data?.first_name && latestSave.saved_by_data?.last_name
            ? `${latestSave.saved_by_data.first_name} ${latestSave.saved_by_data.last_name}`
            : latestSave.saved_by_data?.username || latestSave.saved_by_data?.email || 'Unknown User';

          setLastSaveInfo({
            user: fullName,
            timestamp: new Date(latestSave.saved_at),
            changes: {
              updated: latestSave.assignments_updated,
              deleted: latestSave.assignments_deleted,
              created: latestSave.assignments_created
            }
          });
          setLastSavedAt(new Date(latestSave.saved_at));
        }
      } catch (error) {
        console.error('Error fetching latest save:', error);
      }
    };

    fetchLatestSave();
  }, []);

  // Geocode accommodations when map modal opens (using Nominatim - OpenStreetMap)
  // Saves coordinates to database after geocoding for instant loading next time
  useEffect(() => {
    if (!showMapModal) return;

    const geocodeAccommodations = async () => {
      const accommodationsToGeocode = accommodations.filter(acc => {
        // Skip if already has valid coordinates in DB
        if (acc.latitude && acc.longitude &&
            !isNaN(parseFloat(acc.latitude)) && !isNaN(parseFloat(acc.longitude))) {
          return false;
        }
        // Skip if already geocoded in this session
        if (geocodedCoordinates[acc.id]) {
          return false;
        }
        // Need address to geocode
        return acc.address || acc.city;
      });

      if (accommodationsToGeocode.length === 0) return;

      setIsGeocoding(true);
      const newCoordinates = { ...geocodedCoordinates };
      const token = localStorage.getItem('access_token');

      for (const acc of accommodationsToGeocode) {
        const address = `${acc.address || ''} ${acc.city || ''} ${acc.country || ''}`.trim();
        if (!address) continue;

        try {
          // Use Nominatim (OpenStreetMap geocoder) - free, no API key needed
          const response = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
            {
              headers: {
                'Accept': 'application/json',
                'User-Agent': 'WindunoApp/1.0' // Required by Nominatim
              }
            }
          );
          const data = await response.json();

          if (data && data.length > 0) {
            const lat = parseFloat(data[0].lat);
            const lng = parseFloat(data[0].lon);

            newCoordinates[acc.id] = { lat, lng };

            // Save coordinates to database for instant loading next time
            try {
              await axios.patch(
                `${API_BASE_URL}/accommodation/${acc.id}/`,
                { latitude: lat, longitude: lng },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              console.log(`Saved coordinates for ${acc.name}: ${lat}, ${lng}`);

              // Update local state to reflect saved coordinates
              setAccommodations(prev => prev.map(a =>
                a.id === acc.id ? { ...a, latitude: lat, longitude: lng } : a
              ));
            } catch (saveError) {
              console.warn(`Failed to save coordinates for ${acc.name}:`, saveError.message);
              // Continue anyway - coordinates are cached in state
            }
          } else {
            console.warn(`Geocoding failed for ${address}: No results`);
          }
        } catch (error) {
          console.warn(`Geocoding error for ${address}:`, error.message);
        }

        // Nominatim requires max 1 request per second
        await new Promise(resolve => setTimeout(resolve, 1100));
      }

      setGeocodedCoordinates(newCoordinates);
      setIsGeocoding(false);
    };

    geocodeAccommodations();
  }, [showMapModal, accommodations, geocodedCoordinates]);

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('access_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    console.log('📡 fetchData called - token exists:', !!token, 'companyId:', companyId);

    if (!token) {
      console.warn('⚠️ No access token - skipping fetchData');
      setLoading(false);
      return;
    }

    try {
      // Extend travel date range by 30 days in each direction
      // This ensures we capture travel days for assignments that partially overlap with calendar view
      const travelStartDate = new Date(startDate);
      travelStartDate.setDate(travelStartDate.getDate() - 30);
      const travelEndDate = new Date(endDate);
      travelEndDate.setDate(travelEndDate.getDate() + 30);

      const [accommodationsRes, projectsRes, travelRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/accommodation/`, {
          headers,
          params: {
            company: companyId,
            start_date: format(startDate, 'yyyy-MM-dd'),
            end_date: format(endDate, 'yyyy-MM-dd'),
            ...(selectedProject && { project: selectedProject })
          }
        }),
        axios.get(`${API_BASE_URL}/projects/`, { headers }),
        axios.get(`${API_BASE_URL}/travel/`, {
          params: {
            start_date: format(travelStartDate, 'yyyy-MM-dd'),
            end_date: format(travelEndDate, 'yyyy-MM-dd')
          },
          headers
        })
      ]);

      // Extract all assignments from rooms within accommodations
      // and build a flat list with accommodation_id for compatibility
      const allAssignments = [];
      const accommodationsWithAssignments = accommodationsRes.data.map(accommodation => {
        const accommodationAssignments = [];

        if (accommodation.rooms) {
          accommodation.rooms.forEach(room => {
            if (room.current_assignments) {
              room.current_assignments.forEach(assignment => {
                // Add accommodation_id for backward compatibility
                const enrichedAssignment = {
                  ...assignment,
                  accommodation: accommodation.id,
                  accommodation_id: accommodation.id,
                  room_id: room.id,
                  room_number: room.room_number,
                  room_capacity: room.capacity
                };
                accommodationAssignments.push(enrichedAssignment);
                allAssignments.push(enrichedAssignment);
              });
            }
          });
        }

        return {
          ...accommodation,
          current_assignments: accommodationAssignments
        };
      });

      // Backend already filters accommodations by date range (rooms with availability in period)
      // So we can use the data directly
      const filteredAccommodations = accommodationsWithAssignments;

      console.log('🏠 Filtered accommodations:', {
        total: accommodationsRes.data.length,
        filtered: filteredAccommodations.length,
        dateRange: { start: format(startDate, 'yyyy-MM-dd'), end: format(endDate, 'yyyy-MM-dd') }
      });

      console.log('🏠 Accommodations with assignments:', filteredAccommodations.map(acc => ({
        id: acc.id,
        name: acc.name,
        assignmentsCount: acc.current_assignments.length,
        assignments: acc.current_assignments.map(a => ({
          id: a.id,
          startDate: a.start_date,
          endDate: a.end_date,
          user: a.user_details ? `${a.user_details.first_name} ${a.user_details.last_name}` : 'Unknown'
        }))
      })));

      console.log('🚀 Raw travel data from API:', travelRes.data);
      console.log('🔍 Selected project:', selectedProject);

      // Process travel data for check-in/check-out days
      const checkInCheckOutTravels = travelRes.data.filter(travel => 
        travel.travel_type === 'check_in' || travel.travel_type === 'check_out'
      );

      console.log('✈️ Check-in/Check-out travels:', checkInCheckOutTravels.map(travel => ({
        id: travel.id,
        user: travel.user || 'Unknown', // travel.user is StringRelatedField
        user_id: travel.user_id,
        start_date: travel.start_date,
        travel_type: travel.travel_type
      })));
      
      console.log('🔗 Travel API URL called:', `${API_BASE_URL}/travel/?start_date=${format(travelStartDate, 'yyyy-MM-dd')}&end_date=${format(travelEndDate, 'yyyy-MM-dd')} (extended ±30 days from calendar range)`);

      setAccommodations(filteredAccommodations);
      // Filter out inactive projects from the project list
      console.log('📋 Projects API response:', projectsRes.data);
      const activeProjects = projectsRes.data.filter(project => project.is_active !== false);
      console.log('📋 Active projects after filter:', activeProjects.length, activeProjects.map(p => ({ id: p.id, name: p.name, is_active: p.is_active })));
      setProjects(activeProjects);

      // Use the flat list of assignments extracted from accommodations
      console.log('📋 All assignments extracted:', allAssignments.length, allAssignments);
      setAssignments(allAssignments);
      setSavedAssignments(JSON.parse(JSON.stringify(allAssignments))); // Deep copy of original assignments
      setPendingChanges({ updated: [], deleted: [], created: [] }); // Reset pending changes
      setHasUnsavedChanges(false);

      if (selectedProject) {
        const resourceAssignments = await axios.get(`${API_BASE_URL}/resource-planning-assignments/`, {
          params: {
            project: selectedProject,
            start_date: format(startDate, 'yyyy-MM-dd'),
            end_date: format(endDate, 'yyyy-MM-dd')
          },
          headers
        });
        console.log('Resource assignments data:', resourceAssignments.data);
        console.log('Date range for filtering:', {
          startDate: format(startDate, 'yyyy-MM-dd'),
          endDate: format(endDate, 'yyyy-MM-dd')
        });
        console.log('Current month:', format(currentDate, 'MMM yyyy'));
        console.log('API URL called:', `${API_BASE_URL}/resource-planning-assignments/?project=${selectedProject}&start_date=${format(startDate, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}`);
        
        // Each assignment is a separate entry with its own travel days
        const assignmentEntries = resourceAssignments.data.map(assignment => {
          const userId = assignment.user;
          const assignmentStart = new Date(assignment.start_date);
          const assignmentEnd = new Date(assignment.end_date);

          // Find travel days that fall within or around this assignment's date range
          // Travels are typically 1 day before (check-in) or 1 day after (check-out) the assignment
          const dayBeforeStart = new Date(assignmentStart);
          dayBeforeStart.setDate(dayBeforeStart.getDate() - 1);
          const dayAfterEnd = new Date(assignmentEnd);
          dayAfterEnd.setDate(dayAfterEnd.getDate() + 1);

          const relevantTravels = checkInCheckOutTravels
            .filter(travel => {
              if (travel.user_id !== userId) return false;
              // Parse travel date and normalize to date-only (strip time/timezone)
              const travelDateRaw = new Date(travel.start_date);
              const travelDate = new Date(travelDateRaw.getFullYear(), travelDateRaw.getMonth(), travelDateRaw.getDate());
              const beforeStart = new Date(dayBeforeStart.getFullYear(), dayBeforeStart.getMonth(), dayBeforeStart.getDate());
              const afterEnd = new Date(dayAfterEnd.getFullYear(), dayAfterEnd.getMonth(), dayAfterEnd.getDate());
              // Include travels from day before start to day after end
              return travelDate >= beforeStart && travelDate <= afterEnd;
            })
            .map(travel => ({
              travel_date: travel.start_date,
              travel_type: travel.travel_type,
              needsAccommodation: true
            }));

          return {
            user_data: assignment.user_data,
            role: assignment.role,
            assignments: [assignment], // Single assignment in array for compatibility
            travelDays: relevantTravels,
            // Store assignment dates directly for easier access
            start_date: assignment.start_date,
            end_date: assignment.end_date,
            assignment_id: assignment.id
          };
        });

        console.log('👥 Assignment entries (each assignment separate):', assignmentEntries.map(entry => ({
          name: `${entry.user_data.first_name} ${entry.user_data.last_name}`,
          user_id: entry.user_data.id,
          assignment_id: entry.assignment_id,
          dates: `${entry.start_date} - ${entry.end_date}`,
          role: entry.role,
          travelDays: entry.travelDays.length,
          travelDetails: entry.travelDays.map(travel => ({
            date: travel.travel_date,
            type: travel.travel_type
          }))
        })));

        console.log('Full assignment entries:', assignmentEntries);
        setProjectAssignments(assignmentEntries);
      } else {
        setProjectAssignments([]);
      }
    } catch (error) {
      console.error('❌ Error fetching data:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // UNSAVED CHANGES HELPER FUNCTIONS
  // ============================================================================

  const addPendingUpdate = (assignment) => {
    // Update local assignments state immediately for visual feedback
    setAssignments(prev => prev.map(a => a.id === assignment.id ? assignment : a));

    // Check if this is a temp assignment (newly created, not yet saved)
    const isTempId = typeof assignment.id === 'string' && assignment.id.startsWith('temp-');

    // Track this change
    setPendingChanges(prev => {
      if (isTempId) {
        // This is a newly created assignment - update it in the created array
        const existingCreatedIndex = prev.created.findIndex(a => a.id === assignment.id);
        if (existingCreatedIndex >= 0) {
          const newCreated = [...prev.created];
          newCreated[existingCreatedIndex] = assignment;
          return { ...prev, created: newCreated };
        } else {
          // Should not happen, but handle gracefully
          return { ...prev, created: [...prev.created, assignment] };
        }
      } else {
        // This is an existing assignment - track it in updated array
        const existing = prev.updated.findIndex(a => a.id === assignment.id);
        if (existing >= 0) {
          // Update existing pending change
          const newUpdated = [...prev.updated];
          newUpdated[existing] = assignment;
          return { ...prev, updated: newUpdated };
        } else {
          // Add new pending change
          return { ...prev, updated: [...prev.updated, assignment] };
        }
      }
    });
    setHasUnsavedChanges(true);
    // No need to force re-render - React will re-render automatically when assignments state changes
  };

  const addPendingDelete = (assignmentId) => {
    // Remove from local state immediately for visual feedback
    setAssignments(prev => prev.filter(a => a.id !== assignmentId));

    // Check if this is a temp assignment (newly created, not yet saved)
    const isTempId = typeof assignmentId === 'string' && assignmentId.startsWith('temp-');

    // Track deletion
    setPendingChanges(prev => {
      if (isTempId) {
        // This is a newly created assignment that hasn't been saved yet - just remove it from created array
        return {
          ...prev,
          created: prev.created.filter(a => a.id !== assignmentId),
          updated: prev.updated.filter(a => a.id !== assignmentId) // Remove from updates if exists
        };
      } else {
        // This is an existing assignment - track it for deletion
        return {
          ...prev,
          deleted: [...prev.deleted, assignmentId],
          updated: prev.updated.filter(a => a.id !== assignmentId) // Remove from updates if exists
        };
      }
    });
    setHasUnsavedChanges(true);
  };

  const addPendingCreate = (assignment) => {
    // Add temp ID for local tracking
    const tempAssignment = { ...assignment, id: `temp-${Date.now()}`, _isNew: true };

    // Add to local state immediately
    setAssignments(prev => [...prev, tempAssignment]);

    // Track creation
    setPendingChanges(prev => ({
      ...prev,
      created: [...prev.created, tempAssignment]
    }));
    setHasUnsavedChanges(true);
  };

  // Refresh data from backend
  const handleRefresh = async () => {
    // Check for unsaved changes first
    if (hasUnsavedChanges) {
      setConfirmationModal({
        title: 'Unsaved Changes',
        message: (
          <div className="space-y-2">
            <p>You have unsaved changes that will be lost if you refresh.</p>
            <p className="text-slate-500 text-sm">Do you want to continue and discard your changes?</p>
          </div>
        ),
        confirmText: 'Discard & Refresh',
        cancelText: 'Cancel',
        icon: 'warning',
        confirmButtonClass: 'bg-orange-500 hover:bg-orange-600',
        onConfirm: async () => {
          setIsRefreshing(true);
          await fetchData();
          setIsRefreshing(false);
        }
      });
      return;
    }

    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
  };

  const saveAllChanges = async () => {
    const token = localStorage.getItem('access_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    try {
      // Save all changes
      const promises = [];

      // 1. Delete assignments
      for (const id of pendingChanges.deleted) {
        promises.push(
          axios.delete(`${API_BASE_URL}/room-assignments/${id}/`, { headers })
        );
      }

      // 2. Update assignments
      for (const assignment of pendingChanges.updated) {
        // Only send fields that backend accepts
        const updateData = {
          user: assignment.user,
          room: assignment.room || assignment.room_id,
          start_date: assignment.start_date,
          end_date: assignment.end_date
        };
        promises.push(
          axios.put(`${API_BASE_URL}/room-assignments/${assignment.id}/`, updateData, { headers })
        );
      }

      // 3. Create new assignments
      for (const assignment of pendingChanges.created) {
        // Only send fields that backend accepts
        const createData = {
          user: assignment.user,
          room: assignment.room || assignment.room_id,
          start_date: assignment.start_date,
          end_date: assignment.end_date,
          is_reservation: assignment.is_reservation || false,
          notes: assignment.notes || ''
        };
        promises.push(
          axios.post(`${API_BASE_URL}/room-assignments/`, createData, { headers })
        );
      }

      await Promise.all(promises);

      // Save history to database
      try {
        // Helper function to get user name
        const getUserName = (userId) => {
          const assignment = [...assignments, ...pendingChanges.created, ...pendingChanges.updated].find(a => a.user === userId);
          if (assignment?.user_details) {
            return `${assignment.user_details.first_name} ${assignment.user_details.last_name}`;
          }
          return `User #${userId}`;
        };

        // Helper function to get accommodation name
        const getAccommodationName = (accommodationId) => {
          const accommodation = accommodations.find(a => a.id === accommodationId);
          return accommodation ? accommodation.name : `Accommodation #${accommodationId}`;
        };

        const historyResponse = await axios.post(`${API_BASE_URL}/accommodation-calendar-save-history/`, {
          assignments_created: pendingChanges.created.length,
          assignments_updated: pendingChanges.updated.length,
          assignments_deleted: pendingChanges.deleted.length,
          change_details: {
            created: pendingChanges.created.map(a => ({
              user_id: a.user,
              user_name: getUserName(a.user),
              accommodation_id: a.accommodation,
              accommodation_name: getAccommodationName(a.accommodation),
              room_number: a.room_number,
              start_date: a.start_date,
              end_date: a.end_date
            })),
            updated: pendingChanges.updated.map(a => ({
              id: a.id,
              user_id: a.user,
              user_name: getUserName(a.user),
              accommodation_id: a.accommodation,
              accommodation_name: getAccommodationName(a.accommodation),
              room_number: a.room_number
            })),
            deleted: pendingChanges.deleted
          }
        }, { headers });

        // Update last save info
        const fullName = historyResponse.data.saved_by_data?.first_name && historyResponse.data.saved_by_data?.last_name
          ? `${historyResponse.data.saved_by_data.first_name} ${historyResponse.data.saved_by_data.last_name}`
          : historyResponse.data.saved_by_data?.username || historyResponse.data.saved_by_data?.email || 'Current User';

        const now = new Date();
        setLastSaveInfo({
          user: fullName,
          timestamp: now,
          changes: {
            updated: pendingChanges.updated.length,
            deleted: pendingChanges.deleted.length,
            created: pendingChanges.created.length
          }
        });
        setLastSavedAt(now);
      } catch (error) {
        console.error('Error saving history:', error);
        // Continue even if history save fails
      }

      // Reset pending changes
      setPendingChanges({ updated: [], deleted: [], created: [] });
      setHasUnsavedChanges(false);

      // Refresh data from server
      await fetchData();
    } catch (error) {
      console.error('Error saving changes:', error);
      alert('Error saving changes: ' + (error.response?.data?.detail || error.message));
    }
  };

  const cancelAllChanges = () => {
    // Restore original assignments
    setAssignments(JSON.parse(JSON.stringify(savedAssignments)));
    setPendingChanges({ updated: [], deleted: [], created: [] });
    setHasUnsavedChanges(false);
  };

  // Helper function to format date and time
  const formatDateTime = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const dateStr = d.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const timeStr = d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
    return `${dateStr} o ${timeStr}`;
  };

  // Helper function to find room ID by accommodation ID and room number
  const findRoomId = (accommodationId, roomNumber) => {
    const accommodation = accommodations.find(acc => acc.id === accommodationId);
    if (!accommodation || !accommodation.rooms) return null;

    const room = accommodation.rooms.find(r => r.room_number === String(roomNumber) || r.room_number === roomNumber);
    return room ? room.id : null;
  };

  // Helper function to get first available room for an accommodation
  const getFirstRoomId = (accommodationId) => {
    const accommodation = accommodations.find(acc => acc.id === accommodationId);
    if (!accommodation || !accommodation.rooms || accommodation.rooms.length === 0) return null;
    return accommodation.rooms[0].id;
  };

  // Helper function to get all rooms for an accommodation
  const getRoomsForAccommodation = (accommodationId) => {
    const accommodation = accommodations.find(acc => acc.id === accommodationId);
    if (!accommodation || !accommodation.rooms) return [];
    return accommodation.rooms;
  };

  const handleDateChange = (direction) => {
    // Move by 7 days while keeping the 100-day range
    const daysToMove = direction === 'prev' ? -7 : 7;
    const newStart = new Date(customDateRange.start);
    newStart.setDate(newStart.getDate() + daysToMove);
    const newEnd = new Date(customDateRange.end);
    newEnd.setDate(newEnd.getDate() + daysToMove);

    setCustomDateRange({
      start: newStart,
      end: newEnd
    });
  };

  // Helper function to check if a specific room is available for a period
  // Takes into account half-day compatibility (check-out morning + check-in afternoon)
  const isRoomAvailableForPeriod = (accommodationId, roomNumber, checkStartDate, checkEndDate, excludeAssignmentId = null) => {
    const checkStart = new Date(checkStartDate.getFullYear(), checkStartDate.getMonth(), checkStartDate.getDate());
    const checkEnd = new Date(checkEndDate.getFullYear(), checkEndDate.getMonth(), checkEndDate.getDate());

    // Get all existing assignments for this room
    const roomAssignments = assignments.filter(a => {
      if (a.accommodation !== accommodationId) return false;
      if (a.room_number !== roomNumber) return false;
      if (excludeAssignmentId && a.id === excludeAssignmentId) return false;
      return true;
    });

    // Check each day in the requested range
    let currentDay = new Date(checkStart);
    while (currentDay <= checkEnd) {
      const currentDayOnly = new Date(currentDay.getFullYear(), currentDay.getMonth(), currentDay.getDate());

      // Is this the start or end of our new assignment?
      const isNewStart = currentDayOnly.getTime() === checkStart.getTime();
      const isNewEnd = currentDayOnly.getTime() === checkEnd.getTime();
      const isNewMiddle = currentDayOnly > checkStart && currentDayOnly < checkEnd;

      // Check for conflicts with existing assignments
      for (const existing of roomAssignments) {
        const existingStart = new Date(existing.start_date);
        const existingEnd = new Date(existing.end_date);
        const existingStartOnly = new Date(existingStart.getFullYear(), existingStart.getMonth(), existingStart.getDate());
        const existingEndOnly = new Date(existingEnd.getFullYear(), existingEnd.getMonth(), existingEnd.getDate());

        // Check if existing assignment covers this day
        if (currentDayOnly >= existingStartOnly && currentDayOnly <= existingEndOnly) {
          // Day is covered by existing assignment - check for half-day compatibility

          const isExistingStart = currentDayOnly.getTime() === existingStartOnly.getTime();
          const isExistingEnd = currentDayOnly.getTime() === existingEndOnly.getTime();
          const isExistingMiddle = currentDayOnly > existingStartOnly && currentDayOnly < existingEndOnly;

          // If existing has a middle day here = full day occupied, no sharing
          if (isExistingMiddle) return false;

          // If new assignment has a middle day here = needs full day, no sharing
          if (isNewMiddle) return false;

          // Check half-day compatibility:
          // - existing end (check-out morning) + new start (check-in afternoon) = OK
          // - existing start (check-in afternoon) + new end (check-out morning) = OK
          // - Any other combination = conflict
          const existingIsCheckOut = isExistingEnd && !isExistingStart; // End day only (not single-day)
          const existingIsCheckIn = isExistingStart && !isExistingEnd; // Start day only (not single-day)
          const newIsCheckIn = isNewStart && !isNewEnd; // Start day only (not single-day)
          const newIsCheckOut = isNewEnd && !isNewStart; // End day only (not single-day)

          // Compatible combinations:
          // 1. Existing is checking out (morning) + New is checking in (afternoon)
          // 2. Existing is checking in (afternoon) + New is checking out (morning)
          const compatible = (existingIsCheckOut && newIsCheckIn) || (existingIsCheckIn && newIsCheckOut);

          if (!compatible) {
            return false; // Conflict - room not available
          }
        }
      }

      currentDay.setDate(currentDay.getDate() + 1);
    }

    return true; // Room is available
  };

  // Helper function to find the maximum available period for a room within a requested range
  // Returns { isFullPeriodAvailable, maxStart, maxEnd, blockedBy, blockingAssignments, blockPosition }
  // blockPosition: 'before' (blocker at start), 'after' (blocker at end), 'middle' (blocker in middle), null (no blocker)
  const findMaxAvailablePeriod = (accommodationId, roomNumber, requestedStart, requestedEnd, excludeAssignmentId = null) => {
    const reqStart = new Date(requestedStart.getFullYear(), requestedStart.getMonth(), requestedStart.getDate());
    const reqEnd = new Date(requestedEnd.getFullYear(), requestedEnd.getMonth(), requestedEnd.getDate());

    // Get all existing assignments for this room
    const roomAssignments = assignments.filter(a => {
      if (a.accommodation !== accommodationId) return false;
      if (String(a.room_number) !== String(roomNumber)) return false;
      if (excludeAssignmentId && a.id === excludeAssignmentId) return false;
      return true;
    });

    // If no assignments, full period is available
    if (roomAssignments.length === 0) {
      return {
        isFullPeriodAvailable: true,
        maxStart: reqStart,
        maxEnd: reqEnd,
        blockedBy: null,
        blockingAssignments: [],
        blockPosition: null
      };
    }

    // Find blocking assignments within the requested range
    const blockingAssignments = roomAssignments.filter(a => {
      const aStart = new Date(a.start_date);
      const aEnd = new Date(a.end_date);
      const aStartOnly = new Date(aStart.getFullYear(), aStart.getMonth(), aStart.getDate());
      const aEndOnly = new Date(aEnd.getFullYear(), aEnd.getMonth(), aEnd.getDate());

      // Check if assignment overlaps with requested range
      // An assignment blocks if it starts before our end AND ends after our start
      return aStartOnly <= reqEnd && aEndOnly >= reqStart;
    }).sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

    // If no blocking assignments, full period is available
    if (blockingAssignments.length === 0) {
      return {
        isFullPeriodAvailable: true,
        maxStart: reqStart,
        maxEnd: reqEnd,
        blockedBy: null,
        blockingAssignments: [],
        blockPosition: null
      };
    }

    // Analyze blocking assignments to find the best available period
    // Case 1: Blocker starts at or before our start (block at beginning)
    // Case 2: Blocker starts after our start (block at end or middle)

    const firstBlocker = blockingAssignments[0];
    const blockerStart = new Date(firstBlocker.start_date);
    const blockerEnd = new Date(firstBlocker.end_date);
    const blockerStartOnly = new Date(blockerStart.getFullYear(), blockerStart.getMonth(), blockerStart.getDate());
    const blockerEndOnly = new Date(blockerEnd.getFullYear(), blockerEnd.getMonth(), blockerEnd.getDate());

    // Case 1: Blocker covers the start of our requested period
    if (blockerStartOnly <= reqStart) {
      // The blocker starts at or before our start
      // Check if blocker ends before our end - we can use the period after the blocker
      if (blockerEndOnly < reqEnd) {
        // We can use from blocker's end date (half-day: they check out morning, we check in afternoon)
        const maxStart = new Date(blockerEndOnly);

        // Check if there's another blocker after this one
        const nextBlocker = blockingAssignments.find(a => {
          const aStart = new Date(a.start_date);
          const aStartOnly = new Date(aStart.getFullYear(), aStart.getMonth(), aStart.getDate());
          return aStartOnly > blockerEndOnly && aStartOnly <= reqEnd;
        });

        let maxEnd = reqEnd;
        if (nextBlocker) {
          const nextBlockerStart = new Date(nextBlocker.start_date);
          const nextBlockerStartOnly = new Date(nextBlockerStart.getFullYear(), nextBlockerStart.getMonth(), nextBlockerStart.getDate());
          maxEnd = new Date(nextBlockerStartOnly);
        }

        return {
          isFullPeriodAvailable: false,
          maxStart: maxStart,
          maxEnd: maxEnd,
          blockedBy: firstBlocker,
          blockingAssignments: blockingAssignments,
          blockPosition: 'before'
        };
      } else {
        // Blocker covers our entire period - no room available
        return {
          isFullPeriodAvailable: false,
          maxStart: null,
          maxEnd: null,
          blockedBy: firstBlocker,
          blockingAssignments: blockingAssignments,
          blockPosition: 'full'
        };
      }
    }

    // Case 2: Blocker starts after our start (we can use period before the blocker)
    // Max end is the blocker's start date (half-day sharing: we check out morning, they check in afternoon)
    const maxEnd = new Date(blockerStartOnly);

    return {
      isFullPeriodAvailable: false,
      maxStart: reqStart,
      maxEnd: maxEnd,
      blockedBy: firstBlocker,
      blockingAssignments: blockingAssignments,
      blockPosition: 'after'
    };
  };

  // Helper function to check if user has active project assignment during date range
  const isUserAvailableInProject = (userId, checkStartDate, checkEndDate) => {
    // Find ALL user's project assignment entries (user may have multiple assignments)
    const userProjectEntries = projectAssignments.filter(pa => pa.user_data?.id === userId);
    if (userProjectEntries.length === 0) return false;

    // Collect all assignments and travel days from all entries
    const allAssignments = userProjectEntries.flatMap(entry => entry.assignments || []);
    const allTravelDays = userProjectEntries.flatMap(entry => entry.travelDays || []);

    const checkStart = new Date(checkStartDate.getFullYear(), checkStartDate.getMonth(), checkStartDate.getDate());
    const checkEnd = new Date(checkEndDate.getFullYear(), checkEndDate.getMonth(), checkEndDate.getDate());

    // Check each day in the range to see if it's covered by project OR travel
    let currentDay = new Date(checkStart);
    while (currentDay <= checkEnd) {
      const currentDayOnly = new Date(currentDay.getFullYear(), currentDay.getMonth(), currentDay.getDate());

      // Check if this day is covered by any project assignment
      const hasProjectOnDay = allAssignments.some(assignment => {
        const assignmentStart = new Date(assignment.start_date);
        const assignmentEnd = new Date(assignment.end_date);
        const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
        const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());

        return currentDayOnly >= assignmentStartOnly && currentDayOnly <= assignmentEndOnly;
      });

      // Check if this day has a travel day (check-in or check-out)
      const hasTravelOnDay = allTravelDays.some(travel => {
        const travelDate = new Date(travel.travel_date);
        const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());

        return travelDateOnly.getTime() === currentDayOnly.getTime();
      });

      // If this day is not covered by project OR travel, return false
      if (!hasProjectOnDay && !hasTravelOnDay) {
        return false;
      }

      // Move to next day
      currentDay.setDate(currentDay.getDate() + 1);
    }

    // All days are covered
    return true;
  };

  // Helper function to check if user has another accommodation assignment on given dates
  // Returns { hasConflict: boolean, conflictingAssignment: object | null }
  const checkUserAccommodationConflict = (userId, checkStartDate, checkEndDate, excludeAssignmentId = null) => {
    const checkStart = new Date(checkStartDate.getFullYear(), checkStartDate.getMonth(), checkStartDate.getDate());
    const checkEnd = new Date(checkEndDate.getFullYear(), checkEndDate.getMonth(), checkEndDate.getDate());

    // Find any assignment for this user that overlaps with the check period
    const conflictingAssignment = assignments.find(a => {
      // Must be same user
      if (a.user !== userId && a.user_id !== userId) return false;
      // Exclude the assignment we're resizing
      if (excludeAssignmentId && (a.id === excludeAssignmentId || a.id === String(excludeAssignmentId))) return false;

      const assignStart = new Date(a.start_date);
      const assignEnd = new Date(a.end_date);
      const assignStartOnly = new Date(assignStart.getFullYear(), assignStart.getMonth(), assignStart.getDate());
      const assignEndOnly = new Date(assignEnd.getFullYear(), assignEnd.getMonth(), assignEnd.getDate());

      // Check if there's any overlap
      return assignStartOnly <= checkEnd && assignEndOnly >= checkStart;
    });

    if (conflictingAssignment) {
      const conflictAccommodation = accommodations.find(acc => acc.id === conflictingAssignment.accommodation);
      return {
        hasConflict: true,
        conflictingAssignment: {
          ...conflictingAssignment,
          accommodationName: conflictAccommodation?.name || 'Unknown'
        }
      };
    }

    return { hasConflict: false, conflictingAssignment: null };
  };

  // Helper function to check room rental availability for a period
  // Returns { isAvailable: boolean, unavailableDays: Date[] }
  const checkRoomRentalAvailability = (accommodationId, roomNumber, checkStartDate, checkEndDate) => {
    const accommodation = accommodations.find(acc => acc.id === accommodationId);
    if (!accommodation) return { isAvailable: false, unavailableDays: [] };

    const room = accommodation.rooms?.find(r => String(r.room_number) === String(roomNumber));
    if (!room) return { isAvailable: false, unavailableDays: [] };

    const checkStart = new Date(checkStartDate.getFullYear(), checkStartDate.getMonth(), checkStartDate.getDate());
    const checkEnd = new Date(checkEndDate.getFullYear(), checkEndDate.getMonth(), checkEndDate.getDate());

    const unavailableDays = [];
    let currentDay = new Date(checkStart);

    while (currentDay <= checkEnd) {
      if (!isRoomAvailableOnDate(room, currentDay)) {
        unavailableDays.push(new Date(currentDay));
      }
      currentDay.setDate(currentDay.getDate() + 1);
    }

    return {
      isAvailable: unavailableDays.length === 0,
      unavailableDays
    };
  };

  const handleAccommodationDrop = useCallback((userId, accommodationId, startDate, endDate, conflictAssignmentId = null, roomNumber = null) => {
    if (!selectedProject) {
      alert('Please select a project before assigning accommodation.');
      return;
    }

    // Find room ID
    const roomId = roomNumber
      ? findRoomId(accommodationId, roomNumber)
      : getFirstRoomId(accommodationId);

    if (!roomId) {
      alert('No room found for this accommodation. Please add rooms first.');
      return;
    }

    // Get room number if not provided (for validation)
    const effectiveRoomNumber = roomNumber || 1;

    // Validate: Check if room is available for this period
    if (!isRoomAvailableForPeriod(accommodationId, effectiveRoomNumber, startDate, endDate, conflictAssignmentId)) {
      alert('Cannot assign accommodation. Room is already occupied during this period.');
      return;
    }

    // Validate: Check if user has project assignment covering this period
    if (!isUserAvailableInProject(userId, startDate, endDate)) {
      alert('Cannot assign accommodation. User is not assigned to the project during this period.');
      return;
    }

    // If there's a conflict, delete the existing assignment first
    if (conflictAssignmentId) {
      addPendingDelete(conflictAssignmentId);
    }

    const newAssignment = {
      user: userId,
      room: roomId,
      start_date: format(startDate, 'yyyy-MM-dd'),
      end_date: format(endDate, 'yyyy-MM-dd'),
      // Keep these for local display until refresh
      accommodation: accommodationId,
      room_number: roomNumber || '1'
    };

    console.log('Creating accommodation assignment locally:', newAssignment);

    // Add to pending creates for later save
    addPendingCreate(newAssignment);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, findRoomId, getFirstRoomId, addPendingDelete, addPendingCreate, assignments, projectAssignments]);

  const handleMoveAssignment = useCallback((assignment, newAccommodationId, newRoomNumber, newStartDate = null, newEndDate = null) => {
    // Find room ID
    const roomId = newRoomNumber
      ? findRoomId(newAccommodationId, newRoomNumber)
      : getFirstRoomId(newAccommodationId);

    if (!roomId) {
      alert('No room found for this accommodation. Please add rooms first.');
      return;
    }

    // Get room number if not provided (for validation)
    const effectiveRoomNumber = newRoomNumber || 1;

    // Use new dates if provided, otherwise keep original dates
    const startDate = newStartDate || new Date(assignment.start_date);
    const endDate = newEndDate || new Date(assignment.end_date);

    // Format dates for assignment
    const startDateStr = format(startDate, 'yyyy-MM-dd');
    const endDateStr = format(endDate, 'yyyy-MM-dd');

    // Validate: Check if new room is available for this period (exclude current assignment)
    if (!isRoomAvailableForPeriod(newAccommodationId, effectiveRoomNumber, startDate, endDate, assignment.id)) {
      alert('Cannot move assignment. Room is already occupied during this period.');
      return;
    }

    // Update assignment locally
    const updatedAssignment = {
      ...assignment,
      room: roomId,
      accommodation: newAccommodationId,
      room_number: newRoomNumber,
      user: assignment.user,
      start_date: startDateStr,
      end_date: endDateStr
    };

    console.log('Moving assignment locally:', updatedAssignment);

    // Add to pending updates for later save
    addPendingUpdate(updatedAssignment);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [findRoomId, getFirstRoomId, addPendingUpdate, assignments]);

  const handleDeleteAssignment = useCallback((assignmentId) => {
    if (!window.confirm('Are you sure you want to remove this accommodation assignment?')) {
      return;
    }

    // Add to pending deletes instead of deleting immediately
    addPendingDelete(assignmentId);
  }, [addPendingDelete]);

  const moveAccommodation = (dragIndex, hoverIndex) => {
    const newAccommodations = [...accommodations];
    const [draggedItem] = newAccommodations.splice(dragIndex, 1);
    newAccommodations.splice(hoverIndex, 0, draggedItem);
    setAccommodations(newAccommodations);
    // TODO: Add API call to persist the new order
  };

  const handleDayClick = (day) => {
    if (!selectedProject) {
      alert('Please select a project first');
      return;
    }

    // Toggle filtering - if same day clicked, clear filter
    if (filteredDay && filteredDay.getTime() === day.getTime()) {
      setFilteredDay(null);
    } else {
      setFilteredDay(day);
    }
  };

  const getPeopleForDay = useCallback((day) => {
    if (!selectedProject || !projectAssignments) return [];

    const currentDateOnly = new Date(day.getFullYear(), day.getMonth(), day.getDate());
    
    return projectAssignments.filter(userWithAssignments => {
      // Check if user has project assignment on this day
      const hasProjectAssignment = userWithAssignments.assignments.some(assignment => {
        const assignmentStart = new Date(assignment.start_date);
        const assignmentEnd = new Date(assignment.end_date);
        const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
        const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());
        return currentDateOnly >= assignmentStartOnly && currentDateOnly <= assignmentEndOnly;
      });

      // Check if user has travel check-in/check-out on this day
      const hasTravelOnDay = userWithAssignments.travelDays && userWithAssignments.travelDays.some(travel => {
        const travelDate = new Date(travel.travel_date);
        const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
        return travelDateOnly.getTime() === currentDateOnly.getTime() && travel.needsAccommodation;
      });

      return hasProjectAssignment || hasTravelOnDay;
    }).map(userWithAssignments => {
      // Determine the reason (project assignment or travel)
      const currentDateOnly = new Date(day.getFullYear(), day.getMonth(), day.getDate());
      
      const projectAssignment = userWithAssignments.assignments.find(assignment => {
        const assignmentStart = new Date(assignment.start_date);
        const assignmentEnd = new Date(assignment.end_date);
        const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
        const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());
        return currentDateOnly >= assignmentStartOnly && currentDateOnly <= assignmentEndOnly;
      });

      const travelDay = userWithAssignments.travelDays && userWithAssignments.travelDays.find(travel => {
        const travelDate = new Date(travel.travel_date);
        const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
        return travelDateOnly.getTime() === currentDateOnly.getTime() && travel.needsAccommodation;
      });

      return {
        ...userWithAssignments,
        reasonForDay: projectAssignment ? {
          type: 'project',
          role: userWithAssignments.role,
          assignment: projectAssignment
        } : travelDay ? {
          type: 'travel',
          travel_type: travelDay.travel_type,
          travel: travelDay
        } : null
      };
    });
  }, [selectedProject, projectAssignments]);

  // Mouse handling for drag and resize - copied from ResourceBoard
  const timelineRef = useRef(null);
  const rafIdRef = useRef(null); // Ref for requestAnimationFrame optimization

  // Track DOM readiness for precise measurements
  const [renderTrigger, setRenderTrigger] = useState(0);
  const [isDomReady, setIsDomReady] = useState(false);

  // Callback ref to detect when table is mounted and trigger DOM ready check
  const timelineCallbackRef = useCallback((node) => {
    console.log('📌 Callback ref called - node:', !!node, 'assignments:', assignments.length);
    timelineRef.current = node;

    if (node && assignments.length > 0 && !isDomReady) {
      console.log('🎯 Table mounted with assignments, checking DOM readiness');
      // Small delay to ensure DOM is fully painted
      setTimeout(() => {
        const testCell = node.querySelector('tbody tr:first-child td:nth-child(2)');
        if (testCell) {
          const rect = testCell.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            console.log('✅ DOM is ready via callback ref!');
            setIsDomReady(true);
          } else {
            console.log('⏳ DOM not ready yet in callback ref');
            setRenderTrigger(prev => prev + 1);
          }
        } else {
          console.log('⏳ Cell not found in callback ref');
          setRenderTrigger(prev => prev + 1);
        }
      }, 10);
    }
  }, [assignments.length, isDomReady]);

  console.log('🔍 Component render - assignments:', assignments.length, 'isDomReady:', isDomReady);

  // Check if DOM is ready by testing if we can measure cells
  // Using useLayoutEffect to ensure synchronous execution after render
  useLayoutEffect(() => {
    console.log('🔍 DOM CHECK useLayoutEffect triggered - accommodations:', accommodations.length, 'assignments:', assignments.length, 'isDomReady:', isDomReady, 'renderTrigger:', renderTrigger);

    // Don't run if no accommodations (table won't be rendered)
    if (accommodations.length === 0) {
      console.log('⏭️ No accommodations, skipping DOM check');
      return;
    }

    console.log('🔧 DOM ready check - timelineRef:', !!timelineRef.current, 'assignments:', assignments.length, 'isDomReady:', isDomReady, 'renderTrigger:', renderTrigger);

    if (!timelineRef.current) {
      console.log('⏳ timelineRef not available yet, will check again');
      // Schedule another check only if we haven't checked too many times
      if (renderTrigger < 50) { // Limit to prevent infinite loop
        const timer = setTimeout(() => {
          setRenderTrigger(prev => prev + 1); // Force a re-check
        }, 50);
        return () => clearTimeout(timer);
      } else {
        console.log('❌ Gave up waiting for timelineRef after 50 attempts');
        return;
      }
    }

    if (assignments.length > 0 && !isDomReady) {
      // Try to measure a cell to see if DOM is ready (use same selector as getCellPositions)
      const testCell = timelineRef.current.querySelector('tbody tr:first-child td:nth-child(2)');
      if (testCell) {
        const rect = testCell.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          console.log('✅ DOM is ready, cells can be measured:', { width: rect.width, height: rect.height });
          setIsDomReady(true);
        } else {
          console.log('⏳ DOM not ready yet (cell has no dimensions), will check again');
          // Schedule another check
          const timer = setTimeout(() => {
            setRenderTrigger(prev => prev + 1); // Force a re-check
          }, 100);
          return () => clearTimeout(timer);
        }
      } else {
        console.log('⏳ DOM not ready yet (cell not found), will check again');
        // Schedule another check
        const timer = setTimeout(() => {
          setRenderTrigger(prev => prev + 1); // Force a re-check
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [assignments.length, renderTrigger, isDomReady, accommodations.length]);

  // When assignments change, mark DOM as not ready and schedule a re-check
  // This ensures measurements are refreshed after drag/resize operations
  useEffect(() => {
    if (isDomReady && assignments.length > 0) {
      console.log('🔄 Assignments changed, scheduling DOM re-check');
      setIsDomReady(false);
      // Small delay to let React finish rendering
      const timer = setTimeout(() => {
        setRenderTrigger(prev => prev + 1);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [assignments]);

  // Reset DOM ready state when navigating between months
  useEffect(() => {
    setIsDomReady(false);
    setRenderTrigger(0); // Reset counter
    console.log('📅 Month/data changed, reset DOM ready state');
  }, [currentDate.getMonth(), currentDate.getFullYear(), viewType]);
  const [dragState, setDragState] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 }); // Track mouse position for ghost element
  const [dragTargetPosition, setDragTargetPosition] = useState(null); // Track target position during drag (for visual feedback)
  const [resizeTargetPosition, setResizeTargetPosition] = useState(null); // Track target position during resize (for visual feedback)
  
  const handleMouseDown = (e, assignment, action, handle = null) => {
    e.preventDefault();
    e.stopPropagation();

    console.log('🖱️ handleMouseDown called:', { assignment, action, handle });

    // Validate assignment exists
    if (!assignment || !assignment.id) {
      console.error('❌ Invalid assignment in handleMouseDown:', assignment);
      return;
    }

    const rect = timelineRef.current?.getBoundingClientRect();
    if (!rect) {
      console.error('❌ No timeline ref rect');
      return;
    }

    if (action === 'drag') {
      // Calculate offset from the start of assignment (where user clicked)
      const assignmentElement = e.currentTarget;
      const assignmentRect = assignmentElement.getBoundingClientRect();
      const clickOffsetX = e.clientX - assignmentRect.left; // offset from left edge of assignment
      const clickOffsetY = e.clientY - assignmentRect.top; // offset from top edge of assignment

      // DYNAMICALLY calculate grid offsets and cell width from actual DOM
      let gridOffsetX = 200; // fallback
      let gridOffsetY = 80;  // fallback
      let actualDayWidth = 40; // fallback

      try {
        // Find first AccommodationCell (tbody > tr > td that's not the name column)
        const firstDataCell = timelineRef.current?.querySelector('tbody tr td:nth-child(2)');
        if (firstDataCell) {
          const cellRect = firstDataCell.getBoundingClientRect();
          gridOffsetX = cellRect.left - rect.left;
          // For vertical: use td.top but subtract a fine-tune offset for padding/border
          gridOffsetY = cellRect.top - rect.top - 10; // -10px fine-tune adjustment

          // MEASURE actual rendered width (may differ from CSS due to browser rounding)
          actualDayWidth = cellRect.width;

          console.log('📐 Dynamic grid metrics:', {
            gridOffsetX,
            gridOffsetY,
            actualDayWidth,
            expectedWidth: 40,
            difference: actualDayWidth - 40
          });
        }
      } catch (err) {
        console.warn('Could not calculate dynamic offsets, using defaults');
      }

      const dragStateData = {
        assignmentId: assignment.id,
        startX: e.clientX - rect.left,
        startY: e.clientY - rect.top,
        deltaX: 0,
        deltaY: 0,
        isCopy: e.altKey, // ALT key pressed = copy mode
        originalAssignment: { ...assignment },
        clickOffsetX: clickOffsetX, // Store X offset (where user clicked horizontally)
        clickOffsetY: clickOffsetY, // Store Y offset (where user clicked vertically)
        assignmentWidth: assignmentRect.width, // Store assignment width for ghost
        assignmentHeight: assignmentRect.height, // Store assignment height for ghost
        gridOffsetX: gridOffsetX, // Dynamic horizontal offset to first data column
        gridOffsetY: gridOffsetY, // Dynamic vertical offset to first data row
        dayWidth: actualDayWidth  // Actual measured day column width (browser-specific)
      };
      console.log('✅ Setting dragState:', dragStateData);
      setDragState(dragStateData);
    } else if (action === 'resize') {
      // Find assignment element
      const assignmentElement = e.currentTarget.closest('.assignment-bar');
      const assignmentRect = assignmentElement ? assignmentElement.getBoundingClientRect() : null;

      const resizeStateData = {
        assignmentId: assignment.id,
        handle: handle,
        startX: e.clientX - rect.left,
        deltaX: 0,
        originalAssignment: { ...assignment },
        // Store assignment dimensions for ghost rendering
        assignmentWidth: assignmentRect ? assignmentRect.width : 0,
        assignmentHeight: assignmentRect ? assignmentRect.height : 32,
        assignmentLeft: assignmentRect ? assignmentRect.left - rect.left : 0,
        assignmentTop: assignmentRect ? assignmentRect.top - rect.top : 0
      };
      console.log('✅ Setting resizeState:', resizeStateData);
      setResizeState(resizeStateData);
    }
  };

  // Mouse event handlers - copied from ResourceBoard
  const handleMouseMove = (e) => {
    const rect = timelineRef.current?.getBoundingClientRect();
    if (!rect) return;

    // Update mouse position for ghost element
    setMousePosition({ x: e.clientX, y: e.clientY });

    // Cancel any pending animation frame
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }

    // Use requestAnimationFrame to throttle state updates
    rafIdRef.current = requestAnimationFrame(() => {
      if (dragState) {
        const currentX = e.clientX - rect.left;
        const currentY = e.clientY - rect.top;
        const deltaX = currentX - dragState.startX;
        const deltaY = currentY - dragState.startY;
        setDragState(prev => ({ ...prev, deltaX, deltaY }));

        // Calculate target position for visual feedback
        // Use actual measured day width from dragState (browser-specific)
        const dayWidth = dragState.dayWidth || CALENDAR_DIMENSIONS.DAY_WIDTH;

        // Calculate position of ASSIGNMENT's TOP-LEFT CORNER (not mouse position!)
        // IMPORTANT: Subtract dynamic grid offsets (calculated in handleMouseDown)
        const assignmentLeft = (dragState.startX + deltaX) - dragState.clickOffsetX - dragState.gridOffsetX;
        const assignmentTop = (dragState.startY + deltaY) - dragState.clickOffsetY - dragState.gridOffsetY;

        // Calculate which DAY (column) the assignment starts at
        const startDayIndex = Math.max(0, Math.min(
          Math.floor(assignmentLeft / dayWidth),
          days.length - 1
        ));

        // Calculate target ACCOMMODATION and ROOM based on vertical position
        let targetAccommodation = null;
        let targetRoomNumber = null;
        let cumulativeHeight = 0;

        for (let i = 0; i < accommodations.length; i++) {
          const acc = accommodations[i];
          const accHeight = getAccommodationRowHeight(acc.capacity);

          if (assignmentTop >= cumulativeHeight && assignmentTop < cumulativeHeight + accHeight) {
            targetAccommodation = acc;

            // Calculate which ROOM within this accommodation
            const relativeY = assignmentTop - cumulativeHeight;
            const roomTrackHeight = CALENDAR_DIMENSIONS.FIXED_LINE_HEIGHT;
            const roomGap = CALENDAR_DIMENSIONS.ROOM_GAP;
            const roomIndex = Math.floor(relativeY / (roomTrackHeight + roomGap));
            targetRoomNumber = Math.max(1, Math.min(roomIndex + 1, acc.capacity || 1));
            break;
          }
          cumulativeHeight += accHeight;
        }

        // Calculate assignment length in days
        let assignmentLengthInDays = 1;
        if (dragState.originalAssignment) {
          const startDate = new Date(dragState.originalAssignment.start_date);
          const endDate = new Date(dragState.originalAssignment.end_date);
          const diffTime = Math.abs(endDate - startDate);
          assignmentLengthInDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end day
        }

        // Update target position
        setDragTargetPosition({
          startDayIndex: startDayIndex,
          assignmentLengthInDays: assignmentLengthInDays,
          accommodationId: targetAccommodation?.id || null,
          roomNumber: targetRoomNumber,
          userId: dragState.originalAssignment?.user // Store user ID for availability check
        });
      }

      if (resizeState) {
        const currentX = e.clientX - rect.left;
        const deltaX = currentX - resizeState.startX;
        setResizeState(prev => ({ ...prev, deltaX }));

        // Calculate visual feedback for resize
        const dayWidth = getDayWidth(); // Use actual measured width
        const dayShift = Math.round(deltaX / dayWidth);

        if (resizeState.originalAssignment) {
          let startDate = new Date(resizeState.originalAssignment.start_date);
          let endDate = new Date(resizeState.originalAssignment.end_date);

          // Adjust dates based on which handle is being dragged
          if (resizeState.handle === 'left') {
            startDate.setDate(startDate.getDate() + dayShift);
          } else if (resizeState.handle === 'right') {
            endDate.setDate(endDate.getDate() + dayShift);
          }

          // Calculate which days should be highlighted
          const startDayIndex = days.findIndex(day => {
            const dayOnly = new Date(day.getFullYear(), day.getMonth(), day.getDate());
            const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
            return dayOnly.getTime() === startDateOnly.getTime();
          });

          const endDayIndex = days.findIndex(day => {
            const dayOnly = new Date(day.getFullYear(), day.getMonth(), day.getDate());
            const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
            return dayOnly.getTime() === endDateOnly.getTime();
          });

          if (startDayIndex !== -1 && endDayIndex !== -1) {
            const assignmentLengthInDays = endDayIndex - startDayIndex + 1;
            const originalAssignment = resizeState.originalAssignment;

            // Validate the resize in real-time for visual feedback
            let resizeStatus = 'valid'; // 'valid', 'warning', 'blocked'
            let resizeBlockReason = null;

            // Check if this is a reservation (skip project assignment check for reservations)
            const isReservation = originalAssignment.is_reservation || false;

            // Check 1: User must have project assignment for the period (skip for reservations)
            if (!isReservation) {
              const userAvailable = isUserAvailableInProject(originalAssignment.user, startDate, endDate);
              if (!userAvailable) {
                resizeStatus = 'blocked';
                resizeBlockReason = 'outside_project';
              }
            }

            // Check 2: Room must not be occupied by another assignment
            if (resizeStatus !== 'blocked') {
              const roomAvailable = isRoomAvailableForPeriod(
                originalAssignment.accommodation,
                originalAssignment.room_number,
                startDate,
                endDate,
                originalAssignment.id
              );
              if (!roomAvailable) {
                resizeStatus = 'blocked';
                resizeBlockReason = 'room_occupied';
              }
            }

            // Check 3: User must not have another accommodation assignment in this period
            if (resizeStatus !== 'blocked') {
              const userConflict = checkUserAccommodationConflict(
                originalAssignment.user,
                startDate,
                endDate,
                originalAssignment.id
              );
              if (userConflict.hasConflict) {
                resizeStatus = 'blocked';
                resizeBlockReason = 'user_has_other_assignment';
              }
            }

            // Check 4: Room rental availability (warning only, not blocking)
            if (resizeStatus === 'valid') {
              const rentalAvailability = checkRoomRentalAvailability(
                originalAssignment.accommodation,
                originalAssignment.room_number,
                startDate,
                endDate
              );
              if (!rentalAvailability.isAvailable) {
                resizeStatus = 'warning';
                resizeBlockReason = 'room_not_rented';
              }
            }

            setResizeTargetPosition({
              startDayIndex: startDayIndex,
              assignmentLengthInDays: Math.max(1, assignmentLengthInDays),
              accommodationId: originalAssignment.accommodation,
              roomNumber: originalAssignment.room_number,
              userId: originalAssignment.user,
              resizeStatus,
              resizeBlockReason,
              newStartDate: startDate,
              newEndDate: endDate
            });
          }
        }
      }
    });
  };

  // ============================================================================
  // CALENDAR DIMENSIONS - CLEAN & SIMPLE DESIGN
  // ============================================================================
  const CALENDAR_DIMENSIONS = {
    MIN_ROW_HEIGHT: 36,       // Minimum height for 1 room: 32 (room) + 4 (padding)
    ADDITIONAL_ROOM_HEIGHT: 36, // Each additional room adds: 32 (room) + 4 (gap)
    BED_COUNTER_HEIGHT: 0,   // Counter at bottom - TEMPORARILY SET TO 0
    ROOM_GAP: 4,              // Gap between room tracks
    FIXED_LINE_HEIGHT: 32,    // FIXED height for all assignment lines (same everywhere!)
    DAY_WIDTH: 40,            // Width of each day column
    ACCOMMODATION_NAME_COLUMN_WIDTH: 200, // Width of accommodation name column (first column)
    HEADER_HEIGHT: 80         // Approximate height of header rows (week numbers + days)
  };

  // Calculate dynamic row height based on number of rooms/capacity
  const getAccommodationRowHeight = (capacity) => {
    const numberOfRooms = capacity || 1; // Minimum 1 room
    if (numberOfRooms <= 1) return CALENDAR_DIMENSIONS.MIN_ROW_HEIGHT;
    return CALENDAR_DIMENSIONS.MIN_ROW_HEIGHT + (numberOfRooms - 1) * CALENDAR_DIMENSIONS.ADDITIONAL_ROOM_HEIGHT;
  };

  // Calculate available height for room tracks (excluding bed counter)
  const getTracksAreaHeight = (capacity) => {
    return getAccommodationRowHeight(capacity) - CALENDAR_DIMENSIONS.BED_COUNTER_HEIGHT;
  };

  // Calculate height for each room track - based on FIXED line height
  const getRoomTrackHeight = (numberOfRooms) => {
    if (numberOfRooms === 0) return 0;
    // Each track is exactly the fixed line height
    return CALENDAR_DIMENSIONS.FIXED_LINE_HEIGHT;
  };

  // Calculate top position for a room track (0-indexed)
  const getRoomTrackTop = (roomIndex, numberOfRooms) => {
    const trackHeight = getRoomTrackHeight(numberOfRooms);
    return roomIndex * (trackHeight + CALENDAR_DIMENSIONS.ROOM_GAP);
  };

  // Calculate height for assignment line - ALWAYS the same!
  const getAssignmentLineHeight = (numberOfRooms) => {
    return CALENDAR_DIMENSIONS.FIXED_LINE_HEIGHT;
  };

  // Calculate top position for assignment line (0-indexed room)
  const getAssignmentLineTop = (roomIndex, numberOfRooms) => {
    const trackTop = getRoomTrackTop(roomIndex, numberOfRooms);
    return trackTop; // No padding needed, line fills entire track
  };

  const getDayWidth = () => {
    // Try to get actual measured day width from DOM
    if (timelineRef.current) {
      try {
        const firstDataCell = timelineRef.current.querySelector('tbody tr td:nth-child(2)');
        if (firstDataCell) {
          const cellRect = firstDataCell.getBoundingClientRect();
          return cellRect.width; // Return actual browser-rendered width
        }
      } catch (err) {
        console.warn('Could not measure day width, using default');
      }
    }
    // Fallback to constant if measurement fails
    return CALENDAR_DIMENSIONS.DAY_WIDTH;
  };

  // Get actual cell positions from DOM for precise alignment
  const getCellPositions = (startDayIndex, endDayIndex) => {
    console.log('📐 getCellPositions called - isDomReady:', isDomReady, 'startDayIndex:', startDayIndex, 'endDayIndex:', endDayIndex);

    if (!timelineRef.current) {
      console.log('❌ getCellPositions: timelineRef.current is null');
      return null;
    }

    if (!isDomReady) {
      console.log('⏳ getCellPositions: DOM not ready yet, hiding assignment');
      return null;
    }

    try {
      // Get reference rect for relative positioning
      const containerRect = timelineRef.current.getBoundingClientRect();

      // Validate container has dimensions
      if (containerRect.width === 0 || containerRect.height === 0) {
        console.log('❌ getCellPositions: container has no dimensions');
        return null;
      }

      // Find the TD cells directly (not inner divs)
      // We want to measure from the inner content area of the td
      // Structure: tbody > tr > td (with border/padding) > div.h-full (content area)
      const startSelector = `tbody tr:first-child td:nth-child(${startDayIndex + 2})`;
      const endSelector = `tbody tr:first-child td:nth-child(${endDayIndex + 2})`;

      console.log('🔍 getCellPositions selectors:', { startSelector, endSelector, startDayIndex, endDayIndex });

      const startTd = timelineRef.current.querySelector(startSelector);
      const endTd = timelineRef.current.querySelector(endSelector);

      console.log('🔍 Found cells:', { startTd: !!startTd, endTd: !!endTd });

      if (!startTd || !endTd) {
        console.log('❌ getCellPositions: cells not found - selectors:', { startSelector, endSelector });
        return null;
      }

      const startRect = startTd.getBoundingClientRect();
      const endRect = endTd.getBoundingClientRect();

      // Validate rects have dimensions
      if (startRect.width === 0 || endRect.width === 0) {
        console.log('❌ getCellPositions: cells have no dimensions');
        return null;
      }

      // Get computed style to check for borders and padding
      const startStyle = window.getComputedStyle(startTd);
      const endStyle = window.getComputedStyle(endTd);

      const startBorderLeft = parseFloat(startStyle.borderLeftWidth) || 0;
      const startPaddingLeft = parseFloat(startStyle.paddingLeft) || 0;
      const endBorderRight = parseFloat(endStyle.borderRightWidth) || 0;
      const endPaddingRight = parseFloat(endStyle.paddingRight) || 0;

      // Calculate positions relative to container
      // Point A = left edge INSIDE border+padding of start cell (where content begins)
      // Point B = right edge INSIDE border+padding of end cell (where content ends)
      const left = (startRect.left - containerRect.left) + startBorderLeft + startPaddingLeft;
      const right = (endRect.right - containerRect.left) - endBorderRight - endPaddingRight;
      const width = right - left;

      console.log('✅ getCellPositions result:', {
        left,
        width,
        startRect,
        endRect,
        containerRect,
        borders: { startBorderLeft, endBorderRight },
        paddings: { startPaddingLeft, endPaddingRight }
      });

      // Additional validation
      if (width <= 0) {
        console.log('❌ getCellPositions: calculated width is invalid:', width);
        return null;
      }

      return { left, width };
    } catch (err) {
      console.warn('❌ Could not measure cell positions:', err);
      return null;
    }
  };

  // Calculate assignment position and width as continuous line
  const getAssignmentStyle = (assignment, trackNumber = 0, numberOfRooms = 0) => {
    let assignmentStart = new Date(assignment.start_date);
    let assignmentEnd = new Date(assignment.end_date);

    const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
    const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());

    // Validate the assignment
    const validationResult = validateAssignment(assignment);

    // Find the index of start and end day in current visible days
    const startDayIndex = days.findIndex(day => {
      const dayOnly = new Date(day.getFullYear(), day.getMonth(), day.getDate());
      return dayOnly.getTime() === assignmentStartOnly.getTime();
    });

    const endDayIndex = days.findIndex(day => {
      const dayOnly = new Date(day.getFullYear(), day.getMonth(), day.getDate());
      return dayOnly.getTime() === assignmentEndOnly.getTime();
    });

    // Determine if start/end are visible (for clip-path diagonal cuts)
    // If start is not visible (started before calendar range), don't show diagonal on left
    // If end is not visible (ends after calendar range), don't show diagonal on right
    const isStartVisible = startDayIndex !== -1;
    const isEndVisible = endDayIndex !== -1;

    // If assignment is completely outside visible range, hide it
    if (startDayIndex === -1 && endDayIndex === -1) {
      // Check if assignment spans across entire visible range
      const firstVisibleDay = new Date(days[0].getFullYear(), days[0].getMonth(), days[0].getDate());
      const lastVisibleDay = new Date(days[days.length - 1].getFullYear(), days[days.length - 1].getMonth(), days[days.length - 1].getDate());

      if (assignmentStartOnly <= firstVisibleDay && assignmentEndOnly >= lastVisibleDay) {
        // Assignment spans entire visible range - neither start nor end is visible
        const effectiveStartDay = 0;
        const effectiveEndDay = days.length - 1;

        // ONLY use DOM measurements - no fallback
        const cellPositions = getCellPositions(effectiveStartDay, effectiveEndDay);
        if (!cellPositions) {
          console.log('⏳ DOM not ready for FULL-SPAN assignment, hiding until ready');
          return { display: 'none' };
        }

        console.log('📏 FULL-SPAN: Using DOM measurements (Point A to B):', {
          left: cellPositions.left,
          width: cellPositions.width,
          effectiveStartDay,
          effectiveEndDay
        });

        // Get single cell width for clip-path calculation
        const singleCellPositions = getCellPositions(0, 0);
        const cellWidth = singleCellPositions ? singleCellPositions.width : 0;

        // Both start and end are outside visible range - no diagonal cuts
        return calculatePositionStyle(cellPositions.left, cellPositions.width, trackNumber, assignment, numberOfRooms, cellWidth, false, false, validationResult);
      }

      return { display: 'none' };
    }

    // Allow partial display
    const effectiveStartDay = startDayIndex === -1 ? 0 : startDayIndex;
    const effectiveEndDay = endDayIndex === -1 ? days.length - 1 : endDayIndex;

    // ONLY use DOM measurements - no fallback
    const cellPositions = getCellPositions(effectiveStartDay, effectiveEndDay);
    if (!cellPositions) {
      console.log('⏳ DOM not ready for assignment, hiding until ready');
      return { display: 'none' };
    }

    // Get single cell width for clip-path calculation
    const singleCellPositions = getCellPositions(effectiveStartDay, effectiveStartDay);
    const cellWidth = singleCellPositions ? singleCellPositions.width : 0;

    console.log('📏 Using DOM measurements (Point A to B):', {
      left: cellPositions.left,
      width: cellPositions.width,
      effectiveStartDay,
      effectiveEndDay,
      cellWidth,
      isStartVisible,
      isEndVisible
    });

    let left = cellPositions.left;

    // Apply drag offset if this assignment is being dragged
    if (dragState && dragState.assignmentId === assignment.id) {
      left += dragState.deltaX;
    }

    return calculatePositionStyle(left, cellPositions.width, trackNumber, assignment, numberOfRooms, cellWidth, isStartVisible, isEndVisible, validationResult);
  };

  const calculatePositionStyle = (left, width, trackNumber, assignment, numberOfRooms, cellWidth = 0, isStartVisible = true, isEndVisible = true, validationResult = null) => {
    // Use NEW calculation functions for clean alignment
    let assignmentHeight, topOffset;

    if (numberOfRooms > 0) {
      // Use new functions that match room tracks perfectly
      assignmentHeight = getAssignmentLineHeight(numberOfRooms);
      topOffset = getAssignmentLineTop(trackNumber, numberOfRooms);
    } else {
      // For accommodations without rooms, use stacking
      assignmentHeight = 18;
      const trackPadding = 2;
      topOffset = 2 + (trackNumber * (assignmentHeight + trackPadding));
    }

    // Calculate clip-path for diagonal cuts on first and last day
    // Both diagonals go from bottom-left to top-right (same direction)
    // Creates parallelogram shape: /████████/
    // BUT: only show diagonal cut if that end is actually visible (is a check-in/check-out day)
    // If assignment started before visible range, don't show left diagonal (it's not a check-in)
    // If assignment ends after visible range, don't show right diagonal (it's not a check-out)
    let clipPath = 'none';
    if (cellWidth > 0 && width > 0) {
      // Determine left and right points based on visibility
      const leftTop = isStartVisible ? `${cellWidth}px` : '0%';
      const leftBottom = '0%';
      const rightTop = '100%';
      const rightBottom = isEndVisible ? `calc(100% - ${cellWidth}px)` : '100%';

      // Polygon points (clockwise):
      // 1. Top-left (with or without diagonal cut)
      // 2. Top-right (100%, 0)
      // 3. Bottom-right (with or without diagonal cut)
      // 4. Bottom-left (0, 100%)
      clipPath = `polygon(${leftTop} 0%, ${rightTop} 0%, ${rightBottom} 100%, ${leftBottom} 100%)`;
    }

    // Determine colors based on validation result and reservation status
    const isValid = validationResult ? validationResult.isValid : true;
    const isReservation = assignment.is_reservation || false;

    // Color logic:
    // - Invalid (any type): red gradient - validation errors take priority
    // - Valid reservation: purple
    // - Valid assignment: blue gradient (dark to light, bottom-left to top-right)
    let bgStyle, boxShadowColor;
    if (!isValid) {
      // Red gradient: from darker (bottom-left) to lighter (top-right)
      bgStyle = 'linear-gradient(to top right, #b91c1c, #f87171)'; // red-700 to red-400
      boxShadowColor = 'rgba(185, 28, 28, 1)'; // Red-700
    } else if (isReservation) {
      // Purple gradient: from darker (bottom-left) to lighter (top-right)
      bgStyle = 'linear-gradient(to top right, #7e22ce, #c084fc)'; // purple-700 to purple-400
      boxShadowColor = 'rgba(126, 34, 206, 1)'; // Purple-700
    } else {
      // Blue gradient: from darker (bottom-left) to lighter (top-right)
      bgStyle = 'linear-gradient(to top right, #1d4ed8, #60a5fa)'; // blue-700 to blue-400
      boxShadowColor = 'rgba(29, 78, 216, 1)'; // Blue-700
    }

    return {
      left: `${left}px`,
      width: `${width}px`,
      position: 'absolute',
      top: `${topOffset}px`,
      height: `${assignmentHeight}px`,
      className: 'assignment-bar text-white text-xs flex items-center cursor-move select-none relative group',
      background: bgStyle,
      display: width > 0 ? 'flex' : 'none',
      borderRadius: '0px', // No border radius for precise alignment
      boxShadow: `inset 4px 0 0 0 ${boxShadowColor}`, // Blue-700, Red-700, or Purple-700 as left indicator
      paddingLeft: '30px', // Extra padding for diagonal cut on left
      paddingRight: '30px', // Extra padding for diagonal cut on right
      boxSizing: 'border-box', // Include padding in width
      clipPath: clipPath,
      isValid: isValid,
      isReservation: isReservation,
      validationErrors: validationResult ? validationResult.errors : []
    };
  };

  // Helper function to check if a room is available on a specific date
  const isRoomAvailableOnDate = (room, date) => {
    if (!room.is_active) return false;
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    // Check availability_periods first
    if (room.availability_periods && room.availability_periods.length > 0) {
      return room.availability_periods.some(period => {
        const periodStart = new Date(period.start_date);
        const periodEnd = new Date(period.end_date);
        const startOnly = new Date(periodStart.getFullYear(), periodStart.getMonth(), periodStart.getDate());
        const endOnly = new Date(periodEnd.getFullYear(), periodEnd.getMonth(), periodEnd.getDate());
        return dateOnly >= startOnly && dateOnly <= endOnly;
      });
    }

    // Fallback to old available_from/to fields
    const availableFrom = room.available_from ? new Date(room.available_from) : null;
    const availableTo = room.available_to ? new Date(room.available_to) : null;
    if (!availableFrom || !availableTo) return false;

    const fromOnly = new Date(availableFrom.getFullYear(), availableFrom.getMonth(), availableFrom.getDate());
    const toOnly = new Date(availableTo.getFullYear(), availableTo.getMonth(), availableTo.getDate());
    return dateOnly >= fromOnly && dateOnly <= toOnly;
  };

  // ============================================================================
  // ASSIGNMENT VALIDATION
  // ============================================================================
  // Validates an assignment and returns list of issues (if any)
  // Returns: { isValid: boolean, errors: string[] }
  const validateAssignment = (assignment) => {
    const errors = [];

    if (!assignment) return { isValid: false, errors: ['Assignment data missing'] };

    const assignmentStart = new Date(assignment.start_date);
    const assignmentEnd = new Date(assignment.end_date);
    const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
    const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());

    // Find the accommodation and room for this assignment
    const accommodation = accommodations.find(acc => acc.id === assignment.accommodation);
    if (!accommodation) {
      errors.push('Accommodation not found');
      return { isValid: false, errors };
    }

    const rooms = accommodation.rooms || [];
    const room = rooms.find(r =>
      r.id === assignment.room_id ||
      r.id === assignment.room ||
      r.room_number === assignment.room_number ||
      String(r.room_number) === String(assignment.room_number)
    );

    if (!room) {
      errors.push(`Room ${assignment.room_number} not found in ${accommodation.name}`);
      return { isValid: false, errors };
    }

    // Check 1: Room availability for each day of the assignment
    let currentDay = new Date(assignmentStartOnly);
    const unavailableDays = [];
    while (currentDay <= assignmentEndOnly) {
      if (!isRoomAvailableOnDate(room, currentDay)) {
        unavailableDays.push(format(currentDay, 'MMM d'));
      }
      currentDay.setDate(currentDay.getDate() + 1);
    }

    if (unavailableDays.length > 0) {
      if (unavailableDays.length <= 3) {
        errors.push(`Room not available on: ${unavailableDays.join(', ')}`);
      } else {
        errors.push(`Room not available on ${unavailableDays.length} days (${unavailableDays[0]} - ${unavailableDays[unavailableDays.length - 1]})`);
      }
    }

    // Check 2: Conflicting assignments in the same room
    const conflictingAssignments = assignments.filter(otherAssignment => {
      // Skip self
      if (otherAssignment.id === assignment.id) return false;

      // Must be same accommodation and room
      if (otherAssignment.accommodation !== assignment.accommodation) return false;
      if (String(otherAssignment.room_number) !== String(assignment.room_number)) return false;

      const otherStart = new Date(otherAssignment.start_date);
      const otherEnd = new Date(otherAssignment.end_date);
      const otherStartOnly = new Date(otherStart.getFullYear(), otherStart.getMonth(), otherStart.getDate());
      const otherEndOnly = new Date(otherEnd.getFullYear(), otherEnd.getMonth(), otherEnd.getDate());

      // Check for overlap
      // Two ranges overlap if: start1 <= end2 AND end1 >= start2
      const hasOverlap = assignmentStartOnly <= otherEndOnly && assignmentEndOnly >= otherStartOnly;

      if (!hasOverlap) return false;

      // Check if this is a valid half-day scenario (check-out + check-in on same day)
      // Find the overlapping day(s)
      const overlapStart = assignmentStartOnly > otherStartOnly ? assignmentStartOnly : otherStartOnly;
      const overlapEnd = assignmentEndOnly < otherEndOnly ? assignmentEndOnly : otherEndOnly;

      // If overlap is exactly 1 day, check half-day compatibility
      if (overlapStart.getTime() === overlapEnd.getTime()) {
        const overlapDay = overlapStart;

        // Check if one assignment ends on this day and the other starts
        const thisEndsOnOverlap = assignmentEndOnly.getTime() === overlapDay.getTime() && assignmentStartOnly < overlapDay;
        const thisStartsOnOverlap = assignmentStartOnly.getTime() === overlapDay.getTime() && assignmentEndOnly > overlapDay;
        const otherEndsOnOverlap = otherEndOnly.getTime() === overlapDay.getTime() && otherStartOnly < overlapDay;
        const otherStartsOnOverlap = otherStartOnly.getTime() === overlapDay.getTime() && otherEndOnly > overlapDay;

        // Valid scenario 1: This assignment ends (check-out), other starts (check-in)
        if (thisEndsOnOverlap && otherStartsOnOverlap) return false;

        // Valid scenario 2: This assignment starts (check-in), other ends (check-out)
        if (thisStartsOnOverlap && otherEndsOnOverlap) return false;
      }

      // Real conflict
      return true;
    });

    if (conflictingAssignments.length > 0) {
      conflictingAssignments.forEach(conflict => {
        const conflictUserName = conflict.user_details
          ? (conflict.user_details.first_name && conflict.user_details.last_name
            ? `${conflict.user_details.first_name} ${conflict.user_details.last_name}`
            : conflict.user_details.username || 'Unknown')
          : 'Unknown';
        errors.push(`Conflicts with ${conflictUserName} (${format(new Date(conflict.start_date), 'MMM d')} - ${format(new Date(conflict.end_date), 'MMM d')})`);
      });
    }

    // Check 3: User has project assignment or travel covering these dates
    // Skip this check for reservations - they don't need project assignment coverage
    if (assignment.is_reservation) {
      return { isValid: errors.length === 0, errors };
    }

    const userId = assignment.user;
    // Get ALL project assignment entries for this user (user may have multiple assignments)
    const userProjectEntries = projectAssignments.filter(pa => pa.user_data?.id === userId);

    if (userProjectEntries.length > 0) {
      // Collect all project assignments and travel days for this user
      const allUserAssignments = userProjectEntries.flatMap(entry => entry.assignments || []);
      const allUserTravelDays = userProjectEntries.flatMap(entry => entry.travelDays || []);

      // Check each day of the accommodation assignment
      let dayToCheck = new Date(assignmentStartOnly);
      const uncoveredDays = [];

      while (dayToCheck <= assignmentEndOnly) {
        const dayOnly = new Date(dayToCheck.getFullYear(), dayToCheck.getMonth(), dayToCheck.getDate());

        // Check if covered by any project assignment
        const coveredByProject = allUserAssignments.some(projAssign => {
          const projStart = new Date(projAssign.start_date);
          const projEnd = new Date(projAssign.end_date);
          const projStartOnly = new Date(projStart.getFullYear(), projStart.getMonth(), projStart.getDate());
          const projEndOnly = new Date(projEnd.getFullYear(), projEnd.getMonth(), projEnd.getDate());
          return dayOnly >= projStartOnly && dayOnly <= projEndOnly;
        });

        // Check if covered by any travel day (note: travelDays is camelCase in projectAssignments)
        const coveredByTravel = allUserTravelDays.some(travel => {
          const travelDate = new Date(travel.travel_date);
          const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
          return dayOnly.getTime() === travelDateOnly.getTime();
        });

        if (!coveredByProject && !coveredByTravel) {
          uncoveredDays.push(new Date(dayToCheck));
        }

        dayToCheck.setDate(dayToCheck.getDate() + 1);
      }

      if (uncoveredDays.length > 0) {
        // Group consecutive days into ranges for cleaner display
        const formatDateRanges = (dates) => {
          if (dates.length === 0) return '';

          const ranges = [];
          let rangeStart = dates[0];
          let rangeEnd = dates[0];

          for (let i = 1; i < dates.length; i++) {
            const prevDate = dates[i - 1];
            const currDate = dates[i];
            const diffDays = (currDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24);

            if (diffDays === 1) {
              // Consecutive day - extend range
              rangeEnd = currDate;
            } else {
              // Gap - save current range and start new one
              if (rangeStart.getTime() === rangeEnd.getTime()) {
                ranges.push(format(rangeStart, 'MMM d'));
              } else {
                ranges.push(`${format(rangeStart, 'MMM d')}-${format(rangeEnd, 'd')}`);
              }
              rangeStart = currDate;
              rangeEnd = currDate;
            }
          }

          // Add last range
          if (rangeStart.getTime() === rangeEnd.getTime()) {
            ranges.push(format(rangeStart, 'MMM d'));
          } else {
            ranges.push(`${format(rangeStart, 'MMM d')}-${format(rangeEnd, 'd')}`);
          }

          return ranges.join(', ');
        };

        const dateRangesStr = formatDateRanges(uncoveredDays);
        errors.push(`No project assignment on: ${dateRangesStr} (${uncoveredDays.length} day${uncoveredDays.length > 1 ? 's' : ''})`);
      }
    } else {
      errors.push('User not assigned to this project');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  // Helper function to check if accommodation is available during date range
  const isAccommodationAvailable = (accommodationId, checkStartDate, checkEndDate) => {
    const accommodation = accommodations.find(acc => acc.id === accommodationId);
    if (!accommodation) return false;

    // Check if at least one room is available for the entire period
    const rooms = accommodation.rooms || [];
    if (rooms.length === 0) return false;

    const checkStart = new Date(checkStartDate.getFullYear(), checkStartDate.getMonth(), checkStartDate.getDate());
    const checkEnd = new Date(checkEndDate.getFullYear(), checkEndDate.getMonth(), checkEndDate.getDate());

    return rooms.some(room => {
      if (!room.is_active) return false;

      // Check each day in the range
      let currentDay = new Date(checkStart);
      while (currentDay <= checkEnd) {
        if (!isRoomAvailableOnDate(room, currentDay)) {
          return false;
        }
        currentDay.setDate(currentDay.getDate() + 1);
      }
      return true;
    });
  };

  // ============================================================================
  // GET AVAILABLE USERS FOR A SPECIFIC DAY
  // ============================================================================
  // Returns list of users who have project assignment or travel on the given date
  // and don't already have accommodation assignment for that day in the given room
  const getAvailableUsersForDay = (date, accommodationId, roomNumber) => {
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    // Get all users who have project assignment or travel on this day
    const availableUsers = [];

    projectAssignments.forEach(entry => {
      const userId = entry.user_data?.id;
      const userName = entry.user_data?.first_name && entry.user_data?.last_name
        ? `${entry.user_data.first_name} ${entry.user_data.last_name}`
        : entry.user_data?.username || 'Unknown';

      // Check if user has project assignment on this day
      const hasProjectOnDay = entry.assignments?.some(assignment => {
        const assignmentStart = new Date(assignment.start_date);
        const assignmentEnd = new Date(assignment.end_date);
        const startOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
        const endOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());
        return dateOnly >= startOnly && dateOnly <= endOnly;
      }) || false;

      // Check if user has travel on this day
      const travelOnDay = entry.travelDays?.find(travel => {
        const travelDate = new Date(travel.travel_date);
        const travelOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
        return dateOnly.getTime() === travelOnly.getTime();
      });

      const hasTravelOnDay = !!travelOnDay;

      if (!hasProjectOnDay && !hasTravelOnDay) return;

      // Check if user already has accommodation assignment for this day - SKIP if they do
      const existingAccommodation = assignments.find(a => {
        if (a.user !== userId) return false;
        const aStart = new Date(a.start_date);
        const aEnd = new Date(a.end_date);
        const aStartOnly = new Date(aStart.getFullYear(), aStart.getMonth(), aStart.getDate());
        const aEndOnly = new Date(aEnd.getFullYear(), aEnd.getMonth(), aEnd.getDate());
        return dateOnly >= aStartOnly && dateOnly <= aEndOnly;
      });

      // Skip users who already have accommodation on this day
      if (existingAccommodation) return;

      // Determine the SPECIFIC date range that contains the clicked date
      // (not all periods combined - only the one relevant to this click)
      let startDate = null;
      let endDate = null;

      // Get all assignments and travel days for this user
      const allUserEntries = projectAssignments.filter(pa => pa.user_data?.id === userId);
      const allAssignments = allUserEntries.flatMap(e => e.assignments || []);
      const allTravelDays = allUserEntries.flatMap(e => e.travelDays || []);

      // Find the specific assignment that contains the clicked date
      const matchingAssignment = allAssignments.find(assignment => {
        const aStart = new Date(assignment.start_date);
        const aEnd = new Date(assignment.end_date);
        const aStartOnly = new Date(aStart.getFullYear(), aStart.getMonth(), aStart.getDate());
        const aEndOnly = new Date(aEnd.getFullYear(), aEnd.getMonth(), aEnd.getDate());
        return dateOnly >= aStartOnly && dateOnly <= aEndOnly;
      });

      if (matchingAssignment) {
        // Found assignment containing the clicked date - use its range
        startDate = new Date(matchingAssignment.start_date);
        endDate = new Date(matchingAssignment.end_date);

        // Extend with travel days that are adjacent to this assignment (within 1 day)
        const startOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const endOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

        allTravelDays.forEach(travel => {
          const travelDate = new Date(travel.travel_date);
          const travelOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());

          // Check if travel day is adjacent to assignment (1 day before start or 1 day after end)
          const dayBefore = new Date(startOnly);
          dayBefore.setDate(dayBefore.getDate() - 1);
          const dayAfter = new Date(endOnly);
          dayAfter.setDate(dayAfter.getDate() + 1);

          if (travelOnly.getTime() === dayBefore.getTime()) {
            startDate = travelDate;
          }
          if (travelOnly.getTime() === dayAfter.getTime()) {
            endDate = travelDate;
          }
        });
      } else if (hasTravelOnDay) {
        // Click was on a travel day - find the assignment this travel is connected to
        const clickedTravelDate = new Date(travelOnDay.travel_date);

        // Look for an assignment that starts right after this travel day (arrival)
        // or ends right before this travel day (departure)
        let connectedAssignment = null;

        allAssignments.forEach(assignment => {
          const aStart = new Date(assignment.start_date);
          const aEnd = new Date(assignment.end_date);
          const aStartOnly = new Date(aStart.getFullYear(), aStart.getMonth(), aStart.getDate());
          const aEndOnly = new Date(aEnd.getFullYear(), aEnd.getMonth(), aEnd.getDate());

          const dayAfterTravel = new Date(dateOnly);
          dayAfterTravel.setDate(dayAfterTravel.getDate() + 1);
          const dayBeforeTravel = new Date(dateOnly);
          dayBeforeTravel.setDate(dayBeforeTravel.getDate() - 1);

          // Travel is arrival (day before assignment starts)
          if (aStartOnly.getTime() === dayAfterTravel.getTime()) {
            connectedAssignment = assignment;
          }
          // Travel is departure (day after assignment ends)
          if (aEndOnly.getTime() === dayBeforeTravel.getTime()) {
            connectedAssignment = assignment;
          }
        });

        if (connectedAssignment) {
          startDate = new Date(connectedAssignment.start_date);
          endDate = new Date(connectedAssignment.end_date);

          // Now also extend with adjacent travel days for this assignment
          const startOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
          const endOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

          allTravelDays.forEach(travel => {
            const travelDate = new Date(travel.travel_date);
            const travelOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());

            const dayBefore = new Date(startOnly);
            dayBefore.setDate(dayBefore.getDate() - 1);
            const dayAfter = new Date(endOnly);
            dayAfter.setDate(dayAfter.getDate() + 1);

            if (travelOnly.getTime() === dayBefore.getTime()) {
              startDate = travelDate;
            }
            if (travelOnly.getTime() === dayAfter.getTime()) {
              endDate = travelDate;
            }
          });
        } else {
          // Standalone travel day (no adjacent assignment found) - use just the travel day
          startDate = clickedTravelDate;
          endDate = clickedTravelDate;
        }
      }

      availableUsers.push({
        id: userId,
        name: userName,
        user_data: entry.user_data,
        hasProjectOnDay,
        hasTravelOnDay,
        travelType: travelOnDay?.travel_type || null,
        // Full date range for this user (needed accommodation period)
        fullStartDate: startDate,
        fullEndDate: endDate,
        // Entry data for creating assignment
        allAssignments: allAssignments,
        travelDays: allTravelDays
      });
    });

    // Remove duplicates (user might appear in multiple project assignment entries)
    const uniqueUsers = availableUsers.reduce((acc, user) => {
      if (!acc.find(u => u.id === user.id)) {
        acc.push(user);
      }
      return acc;
    }, []);

    // Sort alphabetically by name
    uniqueUsers.sort((a, b) => a.name.localeCompare(b.name));

    return uniqueUsers;
  };

  // ============================================================================
  // HALF-DAY COMPATIBILITY CHECK
  // ============================================================================
  // Check if a new assignment can share a day with an existing assignment
  // Rule: check-out (morning) + check-in (afternoon) can share the same day
  //
  // existingAssignment - the assignment already in the room
  // newStartDate - start date of the new assignment being dropped
  // newEndDate - end date of the new assignment being dropped
  // currentDate - the specific day we're checking
  //
  // Returns: true if the day can be shared, false if there's a conflict
  const canShareDay = (existingAssignment, newStartDate, newEndDate, currentDate) => {
    if (!existingAssignment) return true; // No existing assignment = no conflict

    const currentDateOnly = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());

    // Existing assignment dates
    const existingStart = new Date(existingAssignment.start_date);
    const existingEnd = new Date(existingAssignment.end_date);
    const existingStartOnly = new Date(existingStart.getFullYear(), existingStart.getMonth(), existingStart.getDate());
    const existingEndOnly = new Date(existingEnd.getFullYear(), existingEnd.getMonth(), existingEnd.getDate());

    // New assignment dates
    const newStart = new Date(newStartDate);
    const newEnd = new Date(newEndDate);
    const newStartOnly = new Date(newStart.getFullYear(), newStart.getMonth(), newStart.getDate());
    const newEndOnly = new Date(newEnd.getFullYear(), newEnd.getMonth(), newEnd.getDate());

    // Check what type of day this is for the existing assignment
    const isExistingStartDate = currentDateOnly.getTime() === existingStartOnly.getTime();
    const isExistingEndDate = currentDateOnly.getTime() === existingEndOnly.getTime();
    const isExistingMiddleDate = currentDateOnly > existingStartOnly && currentDateOnly < existingEndOnly;

    // Check what type of day this is for the new assignment
    const isNewStartDate = currentDateOnly.getTime() === newStartOnly.getTime();
    const isNewEndDate = currentDateOnly.getTime() === newEndOnly.getTime();
    const isNewMiddleDate = currentDateOnly > newStartOnly && currentDateOnly < newEndOnly;

    // If existing assignment has a middle date on this day = full day occupied, no sharing
    if (isExistingMiddleDate) return false;

    // If new assignment would have a middle date on this day = needs full day, no sharing
    if (isNewMiddleDate) return false;

    // COMPATIBLE SCENARIO 1: Existing is check-out (end date only, not start), new is check-in (start date only, not end)
    // This means: existing person leaves in the morning, new person arrives in the afternoon
    if (isExistingEndDate && !isExistingStartDate && isNewStartDate && !isNewEndDate) {
      return true;
    }

    // COMPATIBLE SCENARIO 2: Existing is check-in (start date only, not end), new is check-out (end date only, not start)
    // This means: existing person arrived in the afternoon, new person leaves in the morning
    // (This scenario is less common but theoretically possible if assignments overlap in reverse)
    if (isExistingStartDate && !isExistingEndDate && isNewEndDate && !isNewStartDate) {
      return true;
    }

    // EDGE CASE: Both are single-day assignments on the same day
    // If existing is single day (start=end) and new is single day (start=end) on same day = conflict
    if (isExistingStartDate && isExistingEndDate && isNewStartDate && isNewEndDate) {
      return false;
    }

    // Any other overlap = conflict
    return false;
  };

  // Helper to determine if dropping user on this date would be a check-in day for them
  const isCheckInDayForUser = (item, currentDate) => {
    if (!item) return false;
    const currentDateOnly = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());

    // Check travel days first - explicit check-in travel
    if (item.travelDays && item.travelDays.length > 0) {
      const hasCheckInTravel = item.travelDays.some(travel => {
        if (travel.travel_type !== 'check_in') return false;
        const travelDate = new Date(travel.travel_date);
        const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
        return travelDateOnly.getTime() === currentDateOnly.getTime();
      });
      if (hasCheckInTravel) return true;
    }

    // Check if this is the start date of any assignment
    if (item.allAssignments && item.allAssignments.length > 0) {
      const isStartOfAssignment = item.allAssignments.some(assignment => {
        const assignmentStart = new Date(assignment.start_date);
        const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
        return currentDateOnly.getTime() === assignmentStartOnly.getTime();
      });
      if (isStartOfAssignment) return true;
    }

    return false;
  };

  // Helper to determine if dropping user on this date would be a check-out day for them
  const isCheckOutDayForUser = (item, currentDate) => {
    if (!item) return false;
    const currentDateOnly = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());

    // Check travel days first - explicit check-out travel
    if (item.travelDays && item.travelDays.length > 0) {
      const hasCheckOutTravel = item.travelDays.some(travel => {
        if (travel.travel_type !== 'check_out') return false;
        const travelDate = new Date(travel.travel_date);
        const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
        return travelDateOnly.getTime() === currentDateOnly.getTime();
      });
      if (hasCheckOutTravel) return true;
    }

    // Check if this is the end date of any assignment
    if (item.allAssignments && item.allAssignments.length > 0) {
      const isEndOfAssignment = item.allAssignments.some(assignment => {
        const assignmentEnd = new Date(assignment.end_date);
        const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());
        return currentDateOnly.getTime() === assignmentEndOnly.getTime();
      });
      if (isEndOfAssignment) return true;
    }

    return false;
  };

  const handleMouseUp = async (e) => {
    // Cancel any pending animation frame
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    if (dragState || resizeState) {
      // Use actual measured day width from dragState (browser-specific)
      const dayWidth = dragState?.dayWidth || getDayWidth();

      if (dragState) {
        const originalAssignment = dragState.originalAssignment;

        // Validate originalAssignment exists
        if (!originalAssignment) {
          console.error('Missing originalAssignment in dragState');
          setDragState(null);
          return;
        }

        // Calculate position of ASSIGNMENT's TOP-LEFT CORNER (not mouse position!)
        // IMPORTANT: Subtract dynamic grid offsets (calculated in handleMouseDown)
        const assignmentLeft = (dragState.startX + dragState.deltaX) - dragState.clickOffsetX - dragState.gridOffsetX;
        const assignmentTop = (dragState.startY + dragState.deltaY) - dragState.clickOffsetY - dragState.gridOffsetY;

        console.log('📍 Assignment position:', {
          assignmentLeft,
          assignmentTop,
          mouseX: dragState.startX + dragState.deltaX,
          mouseY: dragState.startY + dragState.deltaY,
          clickOffsetX: dragState.clickOffsetX,
          clickOffsetY: dragState.clickOffsetY,
          gridOffsetX: dragState.gridOffsetX,
          gridOffsetY: dragState.gridOffsetY
        });

        // Calculate which DAY (column) the assignment starts at
        const startDayIndex = Math.max(0, Math.min(
          Math.floor(assignmentLeft / dayWidth),
          days.length - 1
        ));
        const dayShift = startDayIndex - days.findIndex(day => {
          const dayOnly = new Date(day.getFullYear(), day.getMonth(), day.getDate());
          const origStartOnly = new Date(originalAssignment.start_date);
          const origStartDateOnly = new Date(origStartOnly.getFullYear(), origStartOnly.getMonth(), origStartOnly.getDate());
          return dayOnly.getTime() === origStartDateOnly.getTime();
        });

        console.log('📅 Day calculation:', { startDayIndex, dayShift, totalDays: days.length });

        // Calculate target ACCOMMODATION and ROOM based on vertical position
        let targetAccommodation = null;
        let targetRoomNumber = null;
        let accommodationChanged = false;

        if (accommodations && accommodations.length > 0) {
          // Find target accommodation by iterating through cumulative heights
          let cumulativeHeight = 0;

          for (let i = 0; i < accommodations.length; i++) {
            const acc = accommodations[i];
            const accHeight = getAccommodationRowHeight(acc.capacity);

            // Check if assignment top is within this accommodation
            if (assignmentTop >= cumulativeHeight && assignmentTop < cumulativeHeight + accHeight) {
              targetAccommodation = acc;

              // Calculate which ROOM within this accommodation
              const relativeY = assignmentTop - cumulativeHeight;
              const tracksAreaHeight = accHeight - CALENDAR_DIMENSIONS.BED_COUNTER_HEIGHT;
              const roomTrackHeight = CALENDAR_DIMENSIONS.FIXED_LINE_HEIGHT;
              const roomGap = CALENDAR_DIMENSIONS.ROOM_GAP;

              // Calculate room index (0-based)
              const roomIndex = Math.floor(relativeY / (roomTrackHeight + roomGap));
              targetRoomNumber = Math.max(1, Math.min(roomIndex + 1, acc.capacity || 1));

              console.log('🏠 Room calculation:', {
                accommodation: acc.name,
                relativeY,
                roomTrackHeight,
                roomGap,
                roomIndex,
                targetRoomNumber,
                capacity: acc.capacity
              });

              break;
            }

            cumulativeHeight += accHeight;
          }

          // If we didn't find one (below all), use last accommodation
          if (!targetAccommodation && accommodations.length > 0) {
            targetAccommodation = accommodations[accommodations.length - 1];
            targetRoomNumber = targetAccommodation.capacity || 1;
          }

          // Determine if accommodation changed
          if (targetAccommodation) {
            accommodationChanged = targetAccommodation.id !== originalAssignment.accommodation;
          }
        }

        // If no valid target accommodation found, use the original one
        if (!targetAccommodation && originalAssignment.accommodation) {
          // Find the original accommodation in the list
          targetAccommodation = accommodations?.find(acc => acc.id === originalAssignment.accommodation);
        }

        // If still not found, bail out
        if (!targetAccommodation) {
          console.error('Could not find target accommodation');
          setDragState(null);
          return;
        }

        // Calculate new dates
        const newStartDate = new Date(originalAssignment.start_date);
        const newEndDate = new Date(originalAssignment.end_date);
        if (Math.abs(dayShift) > 0) {
          newStartDate.setDate(newStartDate.getDate() + dayShift);
          newEndDate.setDate(newEndDate.getDate() + dayShift);
        }

        // Check if room changed
        const roomChanged = targetRoomNumber && targetRoomNumber !== originalAssignment.room_number;

        // Determine operation type:
        // - COPY: ALT key pressed (dragState.isCopy)
        // - MOVE to different accommodation: accommodation changed but NO ALT key
        // - UPDATE in same accommodation: only dates/room changed
        const isCopyOperation = dragState.isCopy;
        const isMoveToNewAccommodation = accommodationChanged && !dragState.isCopy;

        // Only proceed if there's a change (date shift, accommodation change, or room change)
        if (Math.abs(dayShift) > 0 || accommodationChanged || roomChanged) {
          // Validate date range, room availability, and user project assignment
          const userAvailable = isUserAvailableInProject(originalAssignment.user, newStartDate, newEndDate);

          // Get the effective target room number for validation
          const effectiveTargetRoomNumber = targetRoomNumber || originalAssignment.room_number || 1;

          // Check if the specific room is available (exclude current assignment when moving within same accommodation)
          const excludeAssignmentId = !accommodationChanged ? dragState.assignmentId : null;
          const roomAvailable = isRoomAvailableForPeriod(
            targetAccommodation.id,
            effectiveTargetRoomNumber,
            newStartDate,
            newEndDate,
            excludeAssignmentId
          );

          if (newStartDate >= startDate && newEndDate <= endDate &&
              roomAvailable &&
              userAvailable) {
            try {
              const token = localStorage.getItem('access_token');
              const headers = token ? { Authorization: `Bearer ${token}` } : {};

              if (isCopyOperation) {
                // COPY: Create new assignment, keep old one (ALT key)
                const roomId = targetRoomNumber
                  ? findRoomId(targetAccommodation.id, targetRoomNumber)
                  : getFirstRoomId(targetAccommodation.id);

                if (!roomId) {
                  alert('No room found for this accommodation. Please add rooms first.');
                  return;
                }

                const postData = {
                  user: originalAssignment.user,
                  room: roomId,
                  start_date: format(newStartDate, 'yyyy-MM-dd'),
                  end_date: format(newEndDate, 'yyyy-MM-dd')
                };
                console.log('Creating copy with data:', postData);
                await axios.post(`${API_BASE_URL}/room-assignments/`, postData, { headers });
                console.log('Created copy of assignment');
              } else if (isMoveToNewAccommodation) {
                // MOVE to different accommodation: Create new + Delete old
                const roomId = targetRoomNumber
                  ? findRoomId(targetAccommodation.id, targetRoomNumber)
                  : getFirstRoomId(targetAccommodation.id);

                if (!roomId) {
                  alert('No room found for this accommodation. Please add rooms first.');
                  return;
                }

                const postData = {
                  user: originalAssignment.user,
                  room: roomId,
                  start_date: format(newStartDate, 'yyyy-MM-dd'),
                  end_date: format(newEndDate, 'yyyy-MM-dd')
                };
                console.log('Moving to new accommodation - creating new assignment:', postData);
                await axios.post(`${API_BASE_URL}/room-assignments/`, postData, { headers });

                // Delete old assignment
                console.log('Deleting old assignment:', dragState.assignmentId);
                await axios.delete(`${API_BASE_URL}/room-assignments/${dragState.assignmentId}/`, { headers });
                console.log('Moved assignment to new accommodation');
              } else {
                // UPDATE: Same accommodation, just update dates/room
                const roomId = targetRoomNumber
                  ? findRoomId(targetAccommodation.id, targetRoomNumber)
                  : (originalAssignment.room_id || originalAssignment.room || getFirstRoomId(targetAccommodation.id));

                if (!roomId) {
                  alert('No room found for this accommodation. Please add rooms first.');
                  return;
                }

                const putData = {
                  user: originalAssignment.user,
                  room: roomId,
                  start_date: format(newStartDate, 'yyyy-MM-dd'),
                  end_date: format(newEndDate, 'yyyy-MM-dd')
                };
                console.log('Updating with data:', putData);
                await axios.put(`${API_BASE_URL}/room-assignments/${dragState.assignmentId}/`, putData, { headers });
                console.log('Updated assignment dates/room');
              }

              await fetchData();
              // No need to force re-render - fetchData updates assignments state which triggers re-render
            } catch (error) {
              console.error('Error updating/creating assignment:', error);
              console.error('Error response:', error.response?.data);
              console.error('Error status:', error.response?.status);
              const operationType = isCopyOperation ? 'copy' : (isMoveToNewAccommodation ? 'move' : 'update');
              alert(`Failed to ${operationType} assignment.`);
            }
          } else {
            const operationType = isCopyOperation ? 'copy' : (isMoveToNewAccommodation ? 'move' : 'move');
            let errorMessage = `Cannot ${operationType} assignment to this period. `;
            if (!roomAvailable) {
              errorMessage += 'Room is already occupied during this period.';
            } else if (!userAvailable) {
              errorMessage += 'User is not assigned to the project during this period.';
            } else {
              errorMessage += 'Please check the date range.';
            }
            alert(errorMessage);
          }
        }
        setDragState(null);
        setDragTargetPosition(null);
      }

      if (resizeState) {
        const dayShift = Math.round(resizeState.deltaX / dayWidth);
        if (Math.abs(dayShift) > 0) {
          const originalAssignment = resizeState.originalAssignment;
          let newStartDate = new Date(originalAssignment.start_date);
          let newEndDate = new Date(originalAssignment.end_date);

          console.log(`RESIZE ${resizeState.handle.toUpperCase()}: dayShift=${dayShift}, original: ${originalAssignment.start_date} to ${originalAssignment.end_date}`);

          if (resizeState.handle === 'left') {
            newStartDate.setDate(newStartDate.getDate() + dayShift);
            console.log(`  → NEW START DATE: ${format(newStartDate, 'yyyy-MM-dd')}, END stays: ${format(newEndDate, 'yyyy-MM-dd')}`);
          } else if (resizeState.handle === 'right') {
            newEndDate.setDate(newEndDate.getDate() + dayShift);
            console.log(`  → START stays: ${format(newStartDate, 'yyyy-MM-dd')}, NEW END DATE: ${format(newEndDate, 'yyyy-MM-dd')}`);
          }

          // Basic date validation
          if (newEndDate < newStartDate) {
            setConfirmationModal({
              title: 'Invalid Date Range',
              message: 'End date cannot be before start date.',
              confirmText: 'OK',
              cancelText: null,
              icon: 'error',
              onConfirm: () => {}
            });
            setResizeState(null);
            setResizeTargetPosition(null);
            return;
          }

          // Check if this is a reservation (skip project assignment check for reservations)
          const isReservation = originalAssignment.is_reservation || false;

          // Check 1: User must have project assignment for the period (skip for reservations)
          if (!isReservation) {
            const userAvailable = isUserAvailableInProject(originalAssignment.user, newStartDate, newEndDate);
            if (!userAvailable) {
              setConfirmationModal({
                title: 'Cannot Resize Assignment',
                message: (
                  <div className="space-y-2">
                    <p>User is not assigned to the project during this period.</p>
                    <p className="text-slate-500 text-sm">
                      The accommodation assignment cannot extend beyond the project assignment dates.
                    </p>
                  </div>
                ),
                confirmText: 'OK',
                cancelText: null,
                icon: 'error',
                onConfirm: () => {}
              });
              setResizeState(null);
              setResizeTargetPosition(null);
              return;
            }
          }

          // Check 2: Room must not be occupied by another assignment
          const roomAvailable = isRoomAvailableForPeriod(
            originalAssignment.accommodation,
            originalAssignment.room_number,
            newStartDate,
            newEndDate,
            originalAssignment.id
          );
          if (!roomAvailable) {
            // Find who is blocking
            const availability = findMaxAvailablePeriod(
              originalAssignment.accommodation,
              originalAssignment.room_number,
              newStartDate,
              newEndDate,
              originalAssignment.id
            );
            const blockerName = availability.blockedBy?.user_details
              ? `${availability.blockedBy.user_details.first_name || ''} ${availability.blockedBy.user_details.last_name || ''}`.trim()
              : 'another user';

            setConfirmationModal({
              title: 'Room Already Occupied',
              message: (
                <div className="space-y-2">
                  <p>Cannot extend assignment - room is already occupied by <strong>{blockerName}</strong>.</p>
                  {availability.blockedBy && (
                    <div className="bg-steel-100 p-2 rounded text-sm">
                      <div>From: {format(new Date(availability.blockedBy.start_date), 'MMM d, yyyy')}</div>
                      <div>To: {format(new Date(availability.blockedBy.end_date), 'MMM d, yyyy')}</div>
                    </div>
                  )}
                </div>
              ),
              confirmText: 'OK',
              cancelText: null,
              icon: 'error',
              onConfirm: () => {}
            });
            setResizeState(null);
            setResizeTargetPosition(null);
            return;
          }

          // Check 3: User must not have another accommodation assignment in this period
          const userConflict = checkUserAccommodationConflict(
            originalAssignment.user,
            newStartDate,
            newEndDate,
            originalAssignment.id
          );
          if (userConflict.hasConflict) {
            const conflictAssign = userConflict.conflictingAssignment;
            setConfirmationModal({
              title: 'User Has Another Assignment',
              message: (
                <div className="space-y-2">
                  <p>Cannot extend - user already has accommodation at:</p>
                  <div className="bg-steel-100 p-2 rounded text-sm">
                    <div className="font-medium">{conflictAssign.accommodationName}</div>
                    <div>Room {conflictAssign.room_number}</div>
                    <div className="text-slate-500">
                      {format(new Date(conflictAssign.start_date), 'MMM d')} - {format(new Date(conflictAssign.end_date), 'MMM d, yyyy')}
                    </div>
                  </div>
                </div>
              ),
              confirmText: 'OK',
              cancelText: null,
              icon: 'error',
              onConfirm: () => {}
            });
            setResizeState(null);
            setResizeTargetPosition(null);
            return;
          }

          // Check 4: Room rental availability (warning only, allow with confirmation)
          const rentalAvailability = checkRoomRentalAvailability(
            originalAssignment.accommodation,
            originalAssignment.room_number,
            newStartDate,
            newEndDate
          );

          const applyResize = () => {
            const updatedAssignment = {
              ...originalAssignment,
              start_date: format(newStartDate, 'yyyy-MM-dd'),
              end_date: format(newEndDate, 'yyyy-MM-dd')
            };
            console.log('Resizing assignment locally:', updatedAssignment);
            requestAnimationFrame(() => {
              addPendingUpdate(updatedAssignment);
            });
          };

          if (!rentalAvailability.isAvailable) {
            // Show warning but allow to proceed
            setConfirmationModal({
              title: 'Room Not Rented',
              message: (
                <div className="space-y-2">
                  <p>The room is not rented for some days in this period.</p>
                  <p className="text-slate-500 text-sm">
                    The assignment will be marked with a warning. Do you want to proceed anyway?
                  </p>
                </div>
              ),
              confirmText: 'Proceed Anyway',
              cancelText: 'Cancel',
              confirmButtonClass: 'bg-orange-500 hover:bg-orange-600',
              icon: 'warning',
              onConfirm: applyResize
            });
          } else {
            // All checks passed - apply resize directly
            applyResize();
          }
        }
        // Always reset resize state at the end
        setResizeState(null);
        setResizeTargetPosition(null);
      }
    }
  };

  useEffect(() => {
    if (dragState || resizeState) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        // Cancel any pending animation frame
        if (rafIdRef.current) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
      };
    }
  }, [dragState, resizeState, startDate, endDate]);

  const ReservationCard = () => {
    const [{ isDragging }, drag] = useDrag({
      type: 'RESERVATION',
      item: {
        type: 'RESERVATION'
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging()
      })
    });

    return (
      <button
        ref={drag}
        className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-150 flex items-center ${
          isDragging
            ? 'opacity-60 transform scale-105 z-50 bg-purple-700 border-2 border-purple-900'
            : 'bg-purple-600 cursor-move hover:bg-purple-700 text-white'
        }`}
        style={isDragging ? { zIndex: 1000 } : {}}
      >
        <ClipboardList className="w-4 h-4 mr-2" />
        Reservation
      </button>
    );
  };

  // ============================================================================
  // DRAGGABLE ASSIGNMENT BAR - Uses react-dnd for consistent drag behavior with UserCard
  // ============================================================================
  const DraggableAssignmentBar = ({ assignment, style, tooltipText, onDelete, onResize }) => {
    const [{ isDragging }, drag] = useDrag({
      type: 'ASSIGNMENT',
      item: () => {
        // Calculate date range from assignment (same as in RoomCell useDrag)
        const assignmentStart = new Date(assignment.start_date);
        const assignmentEnd = new Date(assignment.end_date);

        return {
          type: 'ASSIGNMENT',
          assignment: assignment,
          sourceAccommodationId: assignment.accommodation,
          sourceRoomNumber: assignment.room_number,
          // Add data similar to USER for consistent canDrop/drop handling
          user: assignment.user_details || { id: assignment.user },
          allAssignments: [{
            start_date: assignment.start_date,
            end_date: assignment.end_date
          }],
          travelDays: [], // Existing assignments don't have separate travel days
          startDate: assignmentStart,
          endDate: assignmentEnd
        };
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging()
      })
    });

    return (
      <div
        ref={drag}
        className={`${style.className} ${isDragging ? 'opacity-50' : ''}`}
        style={{
          position: 'absolute',
          left: style.left,
          width: style.width,
          top: style.top,
          height: style.height,
          borderRadius: style.borderRadius,
          boxShadow: style.boxShadow,
          background: style.background,
          zIndex: isDragging ? 100 : 10,
          overflow: 'hidden',
          boxSizing: 'border-box',
          clipPath: style.clipPath,
          cursor: 'move'
        }}
        title={tooltipText}
      >
        {/* Move handle */}
        <div
          className="move-handle absolute left-0 top-0 bottom-0 w-6 cursor-move opacity-0 group-hover:opacity-100 flex items-center justify-center"
          style={{ zIndex: 15 }}
        >
          <GripVertical className="h-3 w-3 text-white drop-shadow-lg" strokeWidth={3} />
        </div>

        {/* Resize handle - left side to change start date */}
        <div
          className={`resize-handle-left absolute left-0 top-0 bottom-0 w-[20px] cursor-ew-resize opacity-0 group-hover:opacity-100 ${!style.isValid ? 'bg-red-700' : (style.isReservation ? 'bg-purple-700' : 'bg-brand-700')} rounded-l`}
          onMouseDown={(e) => {
            e.stopPropagation();
            onResize(e, assignment, 'left');
          }}
          style={{ zIndex: 16 }}
          title="Drag to change start date"
        />

        {/* Assignment text */}
        <span className="truncate pointer-events-none flex items-center pl-[30px] pr-[30px]">
          {(() => {
            if (!assignment.user_details) return 'U';

            const firstName = assignment.user_details.first_name || '';
            const lastName = assignment.user_details.last_name || '';
            const isReservation = assignment.is_reservation;

            // Get numeric width (remove 'px' if present)
            const widthPx = typeof style.width === 'string'
              ? parseFloat(style.width)
              : style.width;

            // Calculate available text width (subtract padding: 30px left + 30px right + some buffer)
            const availableWidth = widthPx - 70;

            // Approximate character widths (avg ~7px per char for this font size)
            const fullName = `${firstName} ${lastName}`;
            const fullWithRes = `${fullName} (reservation)`;
            const initials = `${firstName.charAt(0)} ${lastName.charAt(0)}`;
            const initialsWithRes = `${initials} (res)`;

            // Choose appropriate format based on available width
            if (isReservation) {
              if (availableWidth >= fullWithRes.length * 7) {
                return fullWithRes;
              } else if (availableWidth >= fullName.length * 7) {
                return `${fullName} (res)`;
              } else if (availableWidth >= initialsWithRes.length * 7) {
                return initialsWithRes;
              } else {
                return initials;
              }
            } else {
              if (availableWidth >= fullName.length * 7) {
                return fullName;
              } else {
                return initials;
              }
            }
          })()}
        </span>

        {/* Delete button */}
        <button
          className="delete-btn absolute right-[25px] top-1 w-3 h-3 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 hover:bg-red-600 flex items-center justify-center"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDelete(assignment.id);
          }}
          style={{ zIndex: 20, fontSize: '8px', lineHeight: '8px' }}
          title="Remove assignment"
        >
          ×
        </button>

        {/* Resize handle - only right side to extend end date */}
        <div
          className={`resize-handle absolute right-0 top-0 bottom-0 w-[20px] cursor-ew-resize opacity-0 group-hover:opacity-100 ${!style.isValid ? 'bg-red-700' : (style.isReservation ? 'bg-purple-700' : 'bg-brand-700')} rounded-r`}
          onMouseDown={(e) => {
            e.stopPropagation();
            onResize(e, assignment, 'right');
          }}
          style={{ zIndex: 15 }}
          title="Drag to change end date"
        />
      </div>
    );
  };

  // ============================================================================
  // CELL CLICK HANDLER - Opens dropdown for quick user assignment
  // ============================================================================
  const handleCellClick = (e, accommodationId, roomNumber, date) => {
    // Get position for dropdown
    const rect = e.currentTarget.getBoundingClientRect();
    const x = rect.left;
    const y = rect.bottom + 2; // Position below the cell

    setCellClickDropdown({
      accommodationId,
      roomNumber,
      date,
      x,
      y,
      accommodationName: accommodations.find(a => a.id === accommodationId)?.name || 'Unknown'
    });
  };

  // Handle user selection from dropdown
  const handleUserSelectFromDropdown = (user) => {
    if (!cellClickDropdown || !user) return;

    const { accommodationId, roomNumber, date } = cellClickDropdown;
    const accommodation = accommodations.find(a => a.id === accommodationId);

    // Close dropdown
    setCellClickDropdown(null);

    // Find the specific assignment that matches the clicked date
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const matchingAssignment = user.allAssignments?.find(assignment => {
      const aStart = new Date(assignment.start_date);
      const aEnd = new Date(assignment.end_date);
      const aStartOnly = new Date(aStart.getFullYear(), aStart.getMonth(), aStart.getDate());
      const aEndOnly = new Date(aEnd.getFullYear(), aEnd.getMonth(), aEnd.getDate());

      // Check if date falls within assignment range
      if (dateOnly >= aStartOnly && dateOnly <= aEndOnly) return true;

      // Also check if date is a travel day adjacent to this assignment
      const dayBefore = new Date(aStartOnly);
      dayBefore.setDate(dayBefore.getDate() - 1);
      const dayAfter = new Date(aEndOnly);
      dayAfter.setDate(dayAfter.getDate() + 1);

      if (dateOnly.getTime() === dayBefore.getTime() || dateOnly.getTime() === dayAfter.getTime()) {
        // Check if there's actually a travel day on this date
        const hasTravelOnDate = user.travelDays?.some(t => {
          const tDate = new Date(t.travel_date);
          const tDateOnly = new Date(tDate.getFullYear(), tDate.getMonth(), tDate.getDate());
          return tDateOnly.getTime() === dateOnly.getTime();
        });
        return hasTravelOnDate;
      }

      return false;
    }) || null;

    // Calculate uncovered periods for this user (similar logic as in UserCard)
    const calculateUncoveredPeriods = () => {
      const allAssignments = user.allAssignments || [];
      const travelDays = user.travelDays || [];

      if (allAssignments.length === 0) {
        return { periods: [], hasAnyCoverage: false, relevantAssignmentIds: [] };
      }

      // Calculate the full date range for this user's card (rotation)
      let cardStartDate = user.fullStartDate;
      let cardEndDate = user.fullEndDate;

      if (!cardStartDate || !cardEndDate) {
        return { periods: [], hasAnyCoverage: false, relevantAssignmentIds: [] };
      }

      const cardStartOnly = new Date(cardStartDate.getFullYear(), cardStartDate.getMonth(), cardStartDate.getDate());
      const cardEndOnly = new Date(cardEndDate.getFullYear(), cardEndDate.getMonth(), cardEndDate.getDate());

      // Get accommodation assignments for this user that overlap with this card's period
      const relevantAccommodationAssignments = assignments.filter(a => {
        if (a.user !== user.id && a.user_id !== user.id) return false;
        const accStart = new Date(a.start_date);
        const accEnd = new Date(a.end_date);
        const accStartOnly = new Date(accStart.getFullYear(), accStart.getMonth(), accStart.getDate());
        const accEndOnly = new Date(accEnd.getFullYear(), accEnd.getMonth(), accEnd.getDate());
        return accStartOnly <= cardEndOnly && accEndOnly >= cardStartOnly;
      });

      if (relevantAccommodationAssignments.length === 0) {
        return { periods: [], hasAnyCoverage: false, relevantAssignmentIds: [] };
      }

      // Build set of days that need accommodation
      const needsAccommodationDays = new Set();

      // Add all assignment days
      allAssignments.forEach(assignment => {
        const start = new Date(assignment.start_date);
        const end = new Date(assignment.end_date);
        let current = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        const endOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());
        while (current <= endOnly) {
          needsAccommodationDays.add(current.getTime());
          current.setDate(current.getDate() + 1);
        }
      });

      // Add travel days that need accommodation
      travelDays.forEach(travel => {
        if (travel.needsAccommodation) {
          const travelDate = new Date(travel.travel_date);
          const travelOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
          needsAccommodationDays.add(travelOnly.getTime());
        }
      });

      // Mark covered days
      relevantAccommodationAssignments.forEach(a => {
        const accStart = new Date(a.start_date);
        const accEnd = new Date(a.end_date);
        let current = new Date(accStart.getFullYear(), accStart.getMonth(), accStart.getDate());
        const endOnly = new Date(accEnd.getFullYear(), accEnd.getMonth(), accEnd.getDate());
        while (current <= endOnly) {
          needsAccommodationDays.delete(current.getTime());
          current.setDate(current.getDate() + 1);
        }
      });

      // Convert remaining days to sorted array
      const uncoveredDaysArray = Array.from(needsAccommodationDays).sort((a, b) => a - b);

      if (uncoveredDaysArray.length === 0) {
        return { periods: [], hasAnyCoverage: true, relevantAssignmentIds: relevantAccommodationAssignments.map(a => a.id) };
      }

      // Group consecutive days into periods
      const periods = [];
      let periodStart = null;
      let periodEnd = null;

      uncoveredDaysArray.forEach((dayTime, index) => {
        const day = new Date(dayTime);
        if (periodStart === null) {
          periodStart = day;
          periodEnd = day;
        } else {
          const expectedNext = new Date(periodEnd);
          expectedNext.setDate(expectedNext.getDate() + 1);
          if (day.getTime() === expectedNext.getTime()) {
            periodEnd = day;
          } else {
            periods.push({ start: periodStart, end: periodEnd });
            periodStart = day;
            periodEnd = day;
          }
        }
        if (index === uncoveredDaysArray.length - 1) {
          periods.push({ start: periodStart, end: periodEnd });
        }
      });

      return { periods, hasAnyCoverage: true, relevantAssignmentIds: relevantAccommodationAssignments.map(a => a.id) };
    };

    const uncoveredPeriodsData = calculateUncoveredPeriods();

    // Open assignment modal with pre-filled data
    setAssignmentData({
      userId: user.id,
      userName: user.name,
      accommodationId: accommodationId,
      accommodationName: accommodation?.name || 'Unknown',
      startDate: user.fullStartDate || date,
      endDate: user.fullEndDate || date,
      hasConflict: user.hasExistingAccommodation,
      userProjectAssignment: matchingAssignment,
      roomNumber: roomNumber,
      conflictAssignmentId: null,
      // Add missing data for modal options
      dropDate: date,
      uncoveredPeriods: uncoveredPeriodsData.periods,
      hasAnyCoverage: uncoveredPeriodsData.hasAnyCoverage,
      existingAssignmentIds: uncoveredPeriodsData.relevantAssignmentIds
    });
    setShowAssignmentModal(true);
  };

  const UserCard = ({ user, projectAssignment, allAssignments, travelDays, travelInfo, accommodationAssignments }) => {
    // Calculate uncovered periods for this user
    // Returns { periods: [], hasAnyCoverage: boolean }
    // periods is empty if user has NO coverage (uncovered = full period, no point showing it)
    // or if user has FULL coverage (nothing uncovered)
    const getUncoveredPeriodsData = () => {
      if (!allAssignments || allAssignments.length === 0) {
        return { periods: [], hasAnyCoverage: false, relevantAssignmentIds: [] };
      }

      // First, calculate the full date range for THIS card (rotation)
      let cardStartDate = null;
      let cardEndDate = null;

      allAssignments.forEach(assignment => {
        const start = new Date(assignment.start_date);
        const end = new Date(assignment.end_date);
        if (!cardStartDate || start < cardStartDate) cardStartDate = start;
        if (!cardEndDate || end > cardEndDate) cardEndDate = end;
      });

      // Include travel days in the range
      if (travelDays && travelDays.length > 0) {
        travelDays.forEach(travel => {
          const travelDate = new Date(travel.travel_date);
          if (!cardStartDate || travelDate < cardStartDate) cardStartDate = travelDate;
          if (!cardEndDate || travelDate > cardEndDate) cardEndDate = travelDate;
        });
      }

      if (!cardStartDate || !cardEndDate) {
        return { periods: [], hasAnyCoverage: false, relevantAssignmentIds: [] };
      }

      const cardStartOnly = new Date(cardStartDate.getFullYear(), cardStartDate.getMonth(), cardStartDate.getDate());
      const cardEndOnly = new Date(cardEndDate.getFullYear(), cardEndDate.getMonth(), cardEndDate.getDate());

      // Filter accommodation assignments to only those that overlap with THIS card's period
      // User may have multiple rotations (cards) in different periods
      const relevantAccommodationAssignments = accommodationAssignments?.filter(acc => {
        if (acc.user !== user.id && acc.user_id !== user.id) return false;

        const accStart = new Date(acc.start_date);
        const accEnd = new Date(acc.end_date);
        const accStartOnly = new Date(accStart.getFullYear(), accStart.getMonth(), accStart.getDate());
        const accEndOnly = new Date(accEnd.getFullYear(), accEnd.getMonth(), accEnd.getDate());

        // Check if accommodation overlaps with card period
        return accStartOnly <= cardEndOnly && accEndOnly >= cardStartOnly;
      }) || [];

      // If no relevant accommodation assignments exist, return empty - uncovered equals full period
      if (relevantAccommodationAssignments.length === 0) {
        return { periods: [], hasAnyCoverage: false, relevantAssignmentIds: [] };
      }

      // Collect all days that need accommodation
      const needsAccommodationDays = new Set();

      // Add days from project assignments
      allAssignments.forEach(assignment => {
        const start = new Date(assignment.start_date);
        const end = new Date(assignment.end_date);
        let current = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        const endOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());

        while (current <= endOnly) {
          needsAccommodationDays.add(current.getTime());
          current.setDate(current.getDate() + 1);
        }
      });

      // Add travel days that need accommodation
      if (travelDays && travelDays.length > 0) {
        travelDays.forEach(travel => {
          if (travel.needsAccommodation !== false) { // Include if needsAccommodation is true or undefined
            const travelDate = new Date(travel.travel_date);
            const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
            needsAccommodationDays.add(travelDateOnly.getTime());
          }
        });
      }

      // Remove days that are already covered by RELEVANT accommodation assignments
      // Half-day logic for accommodation:
      // - start_date (check-in): user checks in afternoon, sleeps that night → COVERED
      // - days between: user sleeps each night → COVERED
      // - end_date (check-out): user checks out morning, does NOT sleep that night
      //   → UNCOVERED (needs new place from afternoon) UNLESS:
      //   a) It's the last day of the card (user is leaving the project)
      //   b) Another assignment starts on this day (half-day handoff)
      if (relevantAccommodationAssignments.length > 0) {
        // First, collect all check-in dates (start_dates) for this user within relevant period
        const checkInDays = new Set();
        relevantAccommodationAssignments.forEach(acc => {
          const start = new Date(acc.start_date);
          const startOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
          checkInDays.add(startOnly.getTime());
        });

        // Now remove covered days (only from relevant assignments)
        relevantAccommodationAssignments.forEach(acc => {
          const start = new Date(acc.start_date);
          const end = new Date(acc.end_date);
          let current = new Date(start.getFullYear(), start.getMonth(), start.getDate());
          const endOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());

          while (current <= endOnly) {
            const currentTime = current.getTime();
            const isCheckOutDay = currentTime === endOnly.getTime();
            const isCheckInDay = currentTime === new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();

            if (isCheckOutDay && !isCheckInDay) {
              // Check-out day (and not same-day check-in/out):
              // User checks out in morning, needs accommodation from afternoon UNLESS:
              // 1. It's the last day of the card (user is leaving)
              // 2. Another assignment starts on this day (half-day handoff)
              const isLastDayOfCard = currentTime === cardEndOnly.getTime();
              const hasAnotherCheckIn = checkInDays.has(currentTime) && !isCheckInDay;

              if (isLastDayOfCard || hasAnotherCheckIn) {
                // No need for accommodation - user is leaving OR has another booking
                needsAccommodationDays.delete(currentTime);
              }
              // Otherwise, keep in uncovered - user needs new accommodation from afternoon
            } else {
              // Check-in day or middle days - user sleeps that night → covered
              needsAccommodationDays.delete(currentTime);
            }
            current.setDate(current.getDate() + 1);
          }
        });
      }

      // Convert remaining days to sorted array
      const uncoveredDaysArray = Array.from(needsAccommodationDays).sort((a, b) => a - b);

      // User has coverage, but check if fully covered (no uncovered days)
      if (uncoveredDaysArray.length === 0) {
        return { periods: [], hasAnyCoverage: true, relevantAssignmentIds: relevantAccommodationAssignments.map(a => a.id) }; // Fully covered!
      }

      // Group consecutive days into periods
      // IMPORTANT: These periods represent suggested ASSIGNMENT dates (with check-in and check-out)
      // If uncovered days are 1-4, the assignment should be 1-5 (check-out on 5th morning)
      // to properly connect with an existing assignment starting on 5th (check-in afternoon)
      // BUT: check-out date cannot exceed the card's end date
      const periods = [];
      let periodStart = new Date(uncoveredDaysArray[0]);
      let periodEnd = new Date(uncoveredDaysArray[0]);

      for (let i = 1; i < uncoveredDaysArray.length; i++) {
        const currentDay = new Date(uncoveredDaysArray[i]);
        const prevDay = new Date(uncoveredDaysArray[i - 1]);

        // Check if consecutive (difference of 1 day)
        const diffDays = (currentDay.getTime() - prevDay.getTime()) / (1000 * 60 * 60 * 24);

        if (diffDays === 1) {
          // Extend current period
          periodEnd = currentDay;
        } else {
          // Save current period (add 1 day to end for check-out date, but not beyond card end)
          const checkOutDate = new Date(periodEnd);
          checkOutDate.setDate(checkOutDate.getDate() + 1);
          // Don't exceed card end date
          const effectiveEnd = checkOutDate > cardEndOnly ? cardEndOnly : checkOutDate;
          periods.push({ start: new Date(periodStart), end: new Date(effectiveEnd) });
          periodStart = currentDay;
          periodEnd = currentDay;
        }
      }

      // Add last period (add 1 day to end for check-out date, but not beyond card end)
      const lastCheckOutDate = new Date(periodEnd);
      lastCheckOutDate.setDate(lastCheckOutDate.getDate() + 1);
      // Don't exceed card end date
      const effectiveLastEnd = lastCheckOutDate > cardEndOnly ? cardEndOnly : lastCheckOutDate;
      periods.push({ start: new Date(periodStart), end: new Date(effectiveLastEnd) });

      return { periods, hasAnyCoverage: true, relevantAssignmentIds: relevantAccommodationAssignments.map(a => a.id) }; // Partial coverage
    };

    const uncoveredPeriodsData = getUncoveredPeriodsData();
    const uncoveredPeriods = uncoveredPeriodsData.periods;
    const hasAnyCoverage = uncoveredPeriodsData.hasAnyCoverage;
    const existingAssignmentIds = uncoveredPeriodsData.relevantAssignmentIds || [];

    const [{ isDragging }, drag] = useDrag({
      type: 'USER',
      item: {
        user: {
          ...user,
          // Keep reference to original user data for consistent access
          user_data: user,
          // Also preserve original user ID for backwards compatibility
          id: user.id
        },
        projectAssignment,
        allAssignments: allAssignments, // Pass all assignments for date checking
        travelDays: travelDays || [], // Pass travel days for date checking
        uncoveredPeriods: uncoveredPeriods, // Pass uncovered periods for smart modal defaults
        hasAnyCoverage: hasAnyCoverage, // Flag indicating if user has any accommodation coverage for this card
        existingAssignmentIds: existingAssignmentIds, // IDs of existing accommodation assignments for this card (for override)
        accommodationAssignments: accommodationAssignments || [] // Pass existing accommodations
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging()
      })
    });

    // Calculate rotation start/end including travel days
    let rotationStart = allAssignments.length > 0 ? new Date(Math.min(...allAssignments.map(a => new Date(a.start_date)))) : null;
    let rotationEnd = allAssignments.length > 0 ? new Date(Math.max(...allAssignments.map(a => new Date(a.end_date)))) : null;

    // Include travel days in the range
    if (travelDays && travelDays.length > 0) {
      const travelDates = travelDays.map(t => new Date(t.travel_date));
      const earliestTravel = new Date(Math.min(...travelDates));
      const latestTravel = new Date(Math.max(...travelDates));

      if (!rotationStart || earliestTravel < rotationStart) {
        rotationStart = earliestTravel;
      }
      if (!rotationEnd || latestTravel > rotationEnd) {
        rotationEnd = latestTravel;
      }
    }

    // Generate accommodation timeline data
    const getAccommodationTimeline = () => {
      if (!accommodationAssignments) return { days: [], coverage: 0, coveredDays: 0, totalDays: 0 };

      const days = [];
      let coveredDays = 0;
      const coveredDaysList = []; // Track which days are covered

      // Only count days when user has project assignments or travel days
      const projectDays = new Set();

      // Add all days from project assignments
      if (allAssignments && allAssignments.length > 0) {
        allAssignments.forEach(assignment => {
          const assignStart = new Date(assignment.start_date);
          const assignEnd = new Date(assignment.end_date);

          let currentDay = new Date(assignStart.getFullYear(), assignStart.getMonth(), assignStart.getDate());
          const endDay = new Date(assignEnd.getFullYear(), assignEnd.getMonth(), assignEnd.getDate());

          while (currentDay <= endDay) {
            projectDays.add(currentDay.getTime());
            currentDay.setDate(currentDay.getDate() + 1);
          }
        });
      }

      // Add travel days to projectDays - they need accommodation too
      if (travelDays && travelDays.length > 0) {
        travelDays.forEach(travel => {
          const travelDate = new Date(travel.travel_date);
          const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
          projectDays.add(travelDateOnly.getTime());
        });
      }

      // Now check each project day for accommodation
      projectDays.forEach(dayTime => {
        const dayOnly = new Date(dayTime);

        // Check if this is a travel day
        const travelDay = travelDays && travelDays.find(t => {
          const travelDate = new Date(t.travel_date);
          const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
          return travelDateOnly.getTime() === dayOnly.getTime();
        });

        const isTravelDay = !!travelDay;
        const needsAccommodationForTravel = travelDay?.needsAccommodation || false;

        // Check if user has accommodation on this day
        const hasAccommodation = accommodationAssignments.some(a => {
          if (a.user !== user.id && a.user_id !== user.id) return false;

          const assignStart = new Date(a.start_date);
          const assignEnd = new Date(a.end_date);
          const assignStartOnly = new Date(assignStart.getFullYear(), assignStart.getMonth(), assignStart.getDate());
          const assignEndOnly = new Date(assignEnd.getFullYear(), assignEnd.getMonth(), assignEnd.getDate());

          return dayOnly >= assignStartOnly && dayOnly <= assignEndOnly;
        });

        // Count as covered if has accommodation (for all days) or if it's a travel day that doesn't need accommodation
        const isCovered = hasAccommodation || (isTravelDay && !needsAccommodationForTravel);
        if (isCovered) {
          coveredDays++;
          coveredDaysList.push(format(dayOnly, 'yyyy-MM-dd'));
        }

        days.push({
          date: new Date(dayOnly),
          hasAccommodation,
          isTravelDay,
          needsAccommodationForTravel
        });
      });

      const totalDays = projectDays.size;
      const coverage = totalDays > 0 ? Math.round((coveredDays / totalDays) * 100) : 0;

      // Log for debugging if coveredDays doesn't match expectations
      if (coveredDays > 0 && coveredDays !== totalDays) {
        console.log(`⚠️ User ${user.id} - Partial coverage:`, {
          totalDays,
          coveredDays,
          coverage,
          coveredDaysList,
          allAssignments: allAssignments ? allAssignments.map(a => `${a.start_date} to ${a.end_date}`) : []
        });
      }

      return { days, coverage, coveredDays, totalDays };
    };

    const timelineData = getAccommodationTimeline();

    // Check if user has accommodation for their full project period
    const isFullyAccommodated = () => {
      if (!rotationStart || !rotationEnd || !accommodationAssignments) return false;

      // Normalize dates to day only (remove time)
      const rotationStartOnly = new Date(rotationStart.getFullYear(), rotationStart.getMonth(), rotationStart.getDate());
      const rotationEndOnly = new Date(rotationEnd.getFullYear(), rotationEnd.getMonth(), rotationEnd.getDate());

      // Get all accommodation assignments for this user
      const userAccommodationAssignments = accommodationAssignments.filter(a =>
        a.user === user.id || a.user_id === user.id
      );

      if (userAccommodationAssignments.length === 0) return false;

      // Sort assignments by start date
      const sortedAssignments = userAccommodationAssignments.sort((a, b) =>
        new Date(a.start_date) - new Date(b.start_date)
      );

      // Check if assignments cover the full project period
      let currentCoverage = new Date(rotationStartOnly);

      for (const assignment of sortedAssignments) {
        const assignStartRaw = new Date(assignment.start_date);
        const assignEndRaw = new Date(assignment.end_date);

        // Normalize assignment dates to day only
        const assignStart = new Date(assignStartRaw.getFullYear(), assignStartRaw.getMonth(), assignStartRaw.getDate());
        const assignEnd = new Date(assignEndRaw.getFullYear(), assignEndRaw.getMonth(), assignEndRaw.getDate());

        // If there's a gap before this assignment starts
        if (assignStart > currentCoverage) {
          return false;
        }

        // Extend coverage
        if (assignEnd > currentCoverage) {
          currentCoverage = new Date(assignEnd);
        }

        // If we've covered the entire project period
        if (currentCoverage >= rotationEndOnly) {
          return true;
        }
      }

      // Check if we covered until the end
      return currentCoverage >= rotationEndOnly;
    };

    const fullyAccommodated = isFullyAccommodated();

    // Check if any accommodation assignment (relevant to THIS card) extends beyond the project period
    // This happens when project dates are shortened but accommodation remains
    // IMPORTANT: Only check assignments that overlap with THIS card's period, not all user assignments
    const getAccommodationOverflow = () => {
      if (!rotationStart || !rotationEnd || !accommodationAssignments) {
        return { hasOverflow: false, overflowAssignments: [] };
      }

      const rotationStartOnly = new Date(rotationStart.getFullYear(), rotationStart.getMonth(), rotationStart.getDate());
      const rotationEndOnly = new Date(rotationEnd.getFullYear(), rotationEnd.getMonth(), rotationEnd.getDate());

      // Get only accommodation assignments that are RELEVANT to this card
      // (i.e., they overlap with this card's rotation period)
      const relevantAccommodationAssignments = accommodationAssignments.filter(a => {
        if (a.user !== user.id && a.user_id !== user.id) return false;

        const accStart = new Date(a.start_date);
        const accEnd = new Date(a.end_date);
        const accStartOnly = new Date(accStart.getFullYear(), accStart.getMonth(), accStart.getDate());
        const accEndOnly = new Date(accEnd.getFullYear(), accEnd.getMonth(), accEnd.getDate());

        // Check if accommodation overlaps with this card's period
        return accStartOnly <= rotationEndOnly && accEndOnly >= rotationStartOnly;
      });

      // Now check if any of these relevant assignments extend beyond this card's period
      // Skip reservations - they don't need project assignment coverage
      const overflowAssignments = relevantAccommodationAssignments.filter(a => {
        // Reservations are exempt from project assignment validation
        if (a.is_reservation) return false;

        const assignStartRaw = new Date(a.start_date);
        const assignEndRaw = new Date(a.end_date);
        const assignStart = new Date(assignStartRaw.getFullYear(), assignStartRaw.getMonth(), assignStartRaw.getDate());
        const assignEnd = new Date(assignEndRaw.getFullYear(), assignEndRaw.getMonth(), assignEndRaw.getDate());

        // Check if accommodation starts before project or ends after project
        return assignStart < rotationStartOnly || assignEnd > rotationEndOnly;
      });

      return {
        hasOverflow: overflowAssignments.length > 0,
        overflowAssignments: overflowAssignments.map(a => ({
          id: a.id,
          start: new Date(a.start_date),
          end: new Date(a.end_date),
          accommodationName: accommodations.find(acc => acc.id === a.accommodation)?.name || 'Unknown',
          roomNumber: a.room_number,
          overflowBefore: new Date(a.start_date) < rotationStartOnly,
          overflowAfter: new Date(a.end_date) > rotationEndOnly
        }))
      };
    };

    const accommodationOverflow = getAccommodationOverflow();

    // Get user name - don't show anything if no name available
    const getUserName = () => {
      if (user.first_name && user.last_name) {
        return `${user.last_name} ${user.first_name}`;
      }
      if (user.username) {
        return user.username;
      }
      if (user.email) {
        return user.email;
      }
      return null;
    };

    const userName = getUserName();

    // Don't render if no name available
    if (!userName) return null;

    // Build tooltip for overflow warning
    const getOverflowTooltip = () => {
      if (!accommodationOverflow.hasOverflow) return '';
      return accommodationOverflow.overflowAssignments.map(a => {
        const parts = [];
        if (a.overflowBefore) parts.push(`starts before project (${format(a.start, 'MMM d')})`);
        if (a.overflowAfter) parts.push(`ends after project (${format(a.end, 'MMM d')})`);
        return `${a.accommodationName} Room ${a.roomNumber}: ${parts.join(', ')}`;
      }).join('\n');
    };

    return (
      <div
        ref={drag}
        className={`py-1 px-1.5 rounded-lg text-xs transition-all duration-150 border ${
          isDragging
            ? 'opacity-60 transform scale-105 z-50 border-2 border-brand-500 bg-white'
            : accommodationOverflow.hasOverflow
              ? 'bg-red-50 text-slate-900 cursor-move hover:bg-red-100 border-red-400 border-2'
              : fullyAccommodated
                ? 'bg-steel-200 text-slate-500 cursor-move hover:bg-steel-300 border-steel-300 opacity-60'
                : 'bg-white text-slate-900 cursor-move hover:bg-brand-50 border-brand-200'
        }`}
        style={isDragging ? { zIndex: 1000 } : {}}
      >
        <div className="flex items-start justify-between gap-1">
          <div className="flex-1 min-w-0">
            {/* Name and dates in same line */}
            {rotationStart && rotationEnd ? (
              <div className="flex items-center gap-1 flex-wrap">
                {/* Warning icon for accommodation overflow */}
                {accommodationOverflow.hasOverflow && (
                  <div
                    className="text-red-500 flex-shrink-0"
                    title={getOverflowTooltip()}
                  >
                    <AlertTriangle className="w-3 h-3" />
                  </div>
                )}
                <div className="font-semibold text-xs truncate" title={userName}>
                  {userName}
                </div>
                <div className={`text-xs ${fullyAccommodated ? 'text-slate-400' : 'text-slate-500'}`}>
                  {format(rotationStart, 'M/d')}-{format(rotationEnd, 'M/d')}
                </div>
              </div>
            ) : (
              <div className="font-semibold text-xs truncate" title={userName}>
                {userName}
              </div>
            )}

            {rotationStart && rotationEnd && (
              <>
                {/* Single line with travel info and coverage - only for Project Team tab */}
                {!travelInfo && (
                  <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
                    {/* Travel information */}
                    {travelDays && travelDays.length > 0 && travelDays.map((travel, idx) => {
                      const travelDate = new Date(travel.travel_date);
                      const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());

                      // Check if user has accommodation on travel day
                      const hasAccommodation = accommodationAssignments.some(a => {
                        if (a.user !== user.id && a.user_id !== user.id) return false;

                        const assignStart = new Date(a.start_date);
                        const assignEnd = new Date(a.end_date);
                        const assignStartOnly = new Date(assignStart.getFullYear(), assignStart.getMonth(), assignStart.getDate());
                        const assignEndOnly = new Date(assignEnd.getFullYear(), assignEnd.getMonth(), assignEnd.getDate());

                        return travelDateOnly >= assignStartOnly && travelDateOnly <= assignEndOnly;
                      });

                      return (
                        <div key={idx} className={`inline-flex items-center gap-0.5 ${
                          travel.travel_type === 'check_in' ? 'text-green-700' : 'text-red-700'
                        }`}>
                          <span style={{ fontSize: '10px' }}>{travel.travel_type === 'check_in' ? '✈️' : '🛫'}</span>
                          <span style={{ fontSize: '10px' }} className="font-medium">{format(travelDate, 'M/d')}</span>
                          <span
                            className={`w-1 h-1 rounded-full ${hasAccommodation ? 'bg-green-500' : 'bg-steel-300'}`}
                            title={`${hasAccommodation ? 'Has accommodation' : 'No accommodation'}`}
                          />
                        </div>
                      );
                    })}

                    {/* Coverage percentage */}
                    {timelineData.totalDays > 0 && (
                      <div className={`font-medium ${
                        timelineData.coverage === 100
                          ? 'text-green-600'
                          : timelineData.coverage >= 50
                            ? 'text-orange-600'
                            : 'text-red-600'
                      }`} style={{ fontSize: '10px' }}>
                        {timelineData.coveredDays}/{timelineData.totalDays}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
            {travelInfo && (
              <>
                <div className={`font-medium mt-0.5 inline-flex items-center gap-1 ${
                  travelInfo.type === 'check_in' ? 'text-green-700' : 'text-red-700'
                }`} style={{ fontSize: '10px' }}>
                  <span>{travelInfo.type === 'check_in' ? '✈️' : '🛫'} {format(new Date(travelInfo.date), 'M/d')}</span>
                  {(() => {
                    const travelDate = new Date(travelInfo.date);
                    const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());

                    // Check if user has accommodation on travel day
                    const hasAccommodation = accommodationAssignments.some(a => {
                      if (a.user !== user.id && a.user_id !== user.id) return false;

                      const assignStart = new Date(a.start_date);
                      const assignEnd = new Date(a.end_date);
                      const assignStartOnly = new Date(assignStart.getFullYear(), assignStart.getMonth(), assignStart.getDate());
                      const assignEndOnly = new Date(assignEnd.getFullYear(), assignEnd.getMonth(), assignEnd.getDate());

                      return travelDateOnly >= assignStartOnly && travelDateOnly <= assignEndOnly;
                    });

                    return (
                      <span className="inline-flex items-center gap-0.5">
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${hasAccommodation ? 'bg-green-500' : 'bg-steel-300'}`}
                          title={`${hasAccommodation ? 'Has accommodation' : 'No accommodation'}`}
                        />
                      </span>
                    );
                  })()}
                </div>
              </>
            )}

            {/* Warning message for accommodation overflow */}
            {accommodationOverflow.hasOverflow && (
              <div className="mt-0.5 text-red-600 font-medium" style={{ fontSize: '10px' }}>
                ⚠️ Beyond dates
              </div>
            )}
          </div>
          {fullyAccommodated && !accommodationOverflow.hasOverflow && (
            <div className="flex-shrink-0">
              <span className="inline-flex items-center px-1 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                ✓
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };


  const RoomCell = ({ accommodation, date, dayIndex, room, roomNumber, isRentalPeriod, dayAssignments, numberOfRooms, dragState, dragTargetPosition, resizeTargetPosition, onCellClick }) => {
    // Find assignment for this specific room (by room_id or room_number)
    const roomAssignment = dayAssignments.find(a =>
      (room && a.room_id === room.id) ||
      (a.room === room?.id) ||
      a.room_number === roomNumber ||
      String(a.room_number) === String(roomNumber)
    );
    const assignmentStart = roomAssignment ? new Date(roomAssignment.start_date) : null;
    const assignmentEnd = roomAssignment ? new Date(roomAssignment.end_date) : null;
    const currentDateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const assignmentStartOnly = assignmentStart ? new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate()) : null;
    const assignmentEndOnly = assignmentEnd ? new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate()) : null;

    const isStartDate = assignmentStartOnly && currentDateOnly.getTime() === assignmentStartOnly.getTime();
    const isEndDate = assignmentEndOnly && currentDateOnly.getTime() === assignmentEndOnly.getTime();
    const isMiddleDate = assignmentStartOnly && assignmentEndOnly && currentDateOnly > assignmentStartOnly && currentDateOnly < assignmentEndOnly;
    const hasAssignment = roomAssignment && (isStartDate || isEndDate || isMiddleDate);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;

    // Check if this is a reservation (using is_reservation flag)
    const isReservation = roomAssignment?.is_reservation || false;

    // Calculate precise height for this room track using shared function
    const roomHeight = getRoomTrackHeight(numberOfRooms);

    // useDrag for moving assignments between rooms
    // Uses same data structure as USER drag for consistent behavior
    const [{ isDragging }, drag] = useDrag({
      type: 'ASSIGNMENT',
      item: () => {
        if (!hasAssignment || !isStartDate) return null;

        // Calculate date range from assignment (similar to USER card logic)
        const assignmentStart = new Date(roomAssignment.start_date);
        const assignmentEnd = new Date(roomAssignment.end_date);

        return {
          type: 'ASSIGNMENT',
          assignment: roomAssignment,
          sourceAccommodationId: accommodation.id,
          sourceRoomNumber: roomNumber,
          // Add data similar to USER for consistent canDrop/drop handling
          user: roomAssignment.user_details || { id: roomAssignment.user },
          allAssignments: [{
            start_date: roomAssignment.start_date,
            end_date: roomAssignment.end_date
          }],
          travelDays: [], // Existing assignments don't have separate travel days
          startDate: assignmentStart,
          endDate: assignmentEnd
        };
      },
      canDrag: () => hasAssignment && isStartDate,
      collect: (monitor) => ({
        isDragging: monitor.isDragging()
      })
    });

    const [{ isOver, canDrop, draggedItem }, drop] = useDrop({
      accept: ['USER', 'ASSIGNMENT', 'RESERVATION'],
      canDrop: (item) => {
        // Reservations can be dropped if room is not occupied
        // (we allow drop even if room is not rented - will show warning)
        if (item.type === 'RESERVATION') {
          return !hasAssignment;
        }

        // Assignments use same logic as USER for consistent behavior
        // Allow drop if: date is in range AND (room has no other assignment OR can share day)
        // Note: We allow drop even if room is not rented yet (isRentalPeriod=false) - assignment will show as red warning
        if (item.type === 'ASSIGNMENT') {
          const currentDateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

          // Check if current date is within the assignment's date range
          const hasAssignmentOnDay = item.allAssignments && item.allAssignments.some(assignment => {
            const assignmentStart = new Date(assignment.start_date);
            const assignmentEnd = new Date(assignment.end_date);
            const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
            const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());

            return currentDateOnly >= assignmentStartOnly && currentDateOnly <= assignmentEndOnly;
          });

          // Assignment doesn't cover this day
          if (!hasAssignmentOnDay) {
            return false;
          }

          // If it's the source room, allow (user is moving within same room)
          if (item.sourceAccommodationId === accommodation.id && item.sourceRoomNumber === roomNumber) {
            return true;
          }

          // Room is completely free (no other assignment) - allow drop
          // This includes rooms not yet rented (will show as red warning)
          const roomHasOtherAssignment = hasAssignment && roomAssignment?.id !== item.assignment?.id;
          if (!roomHasOtherAssignment) {
            return true;
          }

          // Room has an existing assignment - check if we can share the day (half-day compatibility)
          if (roomAssignment && roomAssignment.id !== item.assignment?.id) {
            const userStartDate = item.startDate || new Date(item.assignment.start_date);
            const userEndDate = item.endDate || new Date(item.assignment.end_date);

            const canShare = canShareDay(roomAssignment, userStartDate, userEndDate, date);
            return canShare;
          }

          // Default: if there's an assignment and we can't determine sharing, don't allow
          return false;
        }

        // For users: allow drop if date is in range AND (room has no other assignment OR can share day)
        // Note: We allow drop even if room is not rented yet (isRentalPeriod=false) - assignment will show as red warning
        if (item.type === 'USER' || item.user) {
          const currentDateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

          // Check if user has project assignment on this day
          const hasProjectAssignment = item.allAssignments && item.allAssignments.some(assignment => {
            const assignmentStart = new Date(assignment.start_date);
            const assignmentEnd = new Date(assignment.end_date);
            const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
            const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());

            return currentDateOnly >= assignmentStartOnly && currentDateOnly <= assignmentEndOnly;
          });

          // Check if user has travel check-in/check-out on this day
          const hasTravelOnDay = item.travelDays && item.travelDays.some(travel => {
            const travelDate = new Date(travel.travel_date);
            const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
            return travelDateOnly.getTime() === currentDateOnly.getTime();
          });

          // User doesn't need accommodation on this day
          if (!hasProjectAssignment && !hasTravelOnDay) {
            return false;
          }

          // Room is completely free (no other assignment) - allow drop
          // This includes rooms not yet rented (will show as red warning)
          if (!hasAssignment) {
            return true;
          }

          // Room has an existing assignment - check if we can share the day (half-day compatibility)
          // This allows check-out (morning) + check-in (afternoon) on the same day
          if (roomAssignment) {
            // Determine the user's assignment date range
            let userStartDate = null;
            let userEndDate = null;

            // Get dates from travel days or assignments
            if (item.travelDays && item.travelDays.length > 0) {
              const checkInTravel = item.travelDays.find(t => t.travel_type === 'check_in');
              const checkOutTravel = item.travelDays.find(t => t.travel_type === 'check_out');
              if (checkInTravel) userStartDate = new Date(checkInTravel.travel_date);
              if (checkOutTravel) userEndDate = new Date(checkOutTravel.travel_date);
            }

            if (item.allAssignments && item.allAssignments.length > 0) {
              const firstAssignment = item.allAssignments[0];
              if (!userStartDate) userStartDate = new Date(firstAssignment.start_date);
              if (!userEndDate) userEndDate = new Date(firstAssignment.end_date);

              // Expand range if travel days extend it
              if (item.travelDays) {
                item.travelDays.forEach(travel => {
                  const travelDate = new Date(travel.travel_date);
                  if (travel.travel_type === 'check_in' && (!userStartDate || travelDate < userStartDate)) {
                    userStartDate = travelDate;
                  }
                  if (travel.travel_type === 'check_out' && (!userEndDate || travelDate > userEndDate)) {
                    userEndDate = travelDate;
                  }
                });
              }
            }

            // If we have user's date range, check if days can be shared
            if (userStartDate && userEndDate) {
              const canShare = canShareDay(roomAssignment, userStartDate, userEndDate, date);
              return canShare;
            }
          }

          // Default: if there's an assignment and we can't determine sharing, don't allow
          return false;
        }

        return true;
      },
      drop: (item) => {
        // Handle dropping a reservation
        if (item.type === 'RESERVATION') {
          // Check 2: Room must not be occupied by another assignment on this day
          if (hasAssignment) {
            setConfirmationModal({
              title: 'Room Already Occupied',
              message: `Room ${roomNumber} is already occupied on ${format(date, 'MMM d, yyyy')}. Please choose another date or room.`,
              confirmText: 'OK',
              cancelText: null,
              icon: 'error',
              onConfirm: () => {}
            });
            return;
          }

          // Check 4: Room rental availability (warning, but allow to proceed)
          const nextDay = new Date(date);
          nextDay.setDate(nextDay.getDate() + 1);

          if (!isRentalPeriod) {
            // Show warning modal but allow to proceed
            setConfirmationModal({
              title: 'Room Not Rented',
              message: (
                <div className="space-y-2">
                  <p>This room is not rented on <strong>{format(date, 'MMM d, yyyy')}</strong>.</p>
                  <p className="text-slate-500 text-sm">
                    You can still create a reservation, but it will be marked as a warning until the room is rented for this period.
                  </p>
                </div>
              ),
              confirmText: 'Create Anyway',
              cancelText: 'Cancel',
              icon: 'warning',
              confirmButtonClass: 'bg-orange-500 hover:bg-orange-600',
              onConfirm: () => {
                setShowReservationModal(true);
                setReservationModalData({
                  accommodationId: accommodation.id,
                  accommodationName: accommodation.name,
                  roomNumber: roomNumber,
                  startDate: date,
                  hasRentalWarning: true
                });
              }
            });
            return;
          }

          // All checks passed - open modal normally
          setShowReservationModal(true);
          setReservationModalData({
            accommodationId: accommodation.id,
            accommodationName: accommodation.name,
            roomNumber: roomNumber,
            startDate: date,
            hasRentalWarning: false
          });
          return;
        }

        // Handle dropping an existing assignment (moving between rooms)
        // Uses same modal flow as USER for consistent behavior
        if (item.type === 'ASSIGNMENT') {
          const assignment = item.assignment;

          // Don't allow dropping on the same room
          if (item.sourceAccommodationId === accommodation.id && item.sourceRoomNumber === roomNumber) {
            return;
          }

          // Note: We allow drop even if room is not rented yet (isRentalPeriod=false)
          // Assignment will show as red warning to indicate room needs to be rented

          // Get assignment dates
          const assignmentStart = new Date(assignment.start_date);
          const assignmentEnd = new Date(assignment.end_date);

          // Check if room has other assignment (not the one being moved) with half-day compatibility
          const roomHasOtherAssignment = hasAssignment && roomAssignment?.id !== assignment.id;
          if (roomHasOtherAssignment && roomAssignment) {
            const canShare = canShareDay(roomAssignment, assignmentStart, assignmentEnd, date);
            if (!canShare) {
              alert(`Room ${roomNumber} is already occupied on ${format(date, 'MMM d, yyyy')}.`);
              return;
            }
          }

          // Get user details from assignment
          const userId = assignment.user;
          const userDetails = assignment.user_details;
          const userName = userDetails?.first_name && userDetails?.last_name
            ? `${userDetails.first_name} ${userDetails.last_name}`
            : (userDetails?.username || 'User');

          // Show assignment modal with edit mode data
          setAssignmentData({
            userId,
            userName,
            accommodationId: accommodation.id,
            accommodationName: accommodation.name,
            startDate: assignmentStart,
            endDate: assignmentEnd,
            dropDate: date, // The specific date where card was dropped
            hasConflict: false,
            userProjectAssignment: null,
            roomNumber: roomNumber,
            conflictAssignmentId: null,
            // Edit mode specific data
            isEditMode: true,
            existingAssignment: assignment,
            sourceAccommodationId: item.sourceAccommodationId,
            sourceRoomNumber: item.sourceRoomNumber,
            // Warning flag if room is not rented yet
            isRoomNotRented: !isRentalPeriod
          });
          setShowAssignmentModal(true);
          return;
        }

        // Handle dropping a new user (existing logic)
        if (item.type === 'USER' || item.user) {
          // Note: We allow drop even if room is not rented yet (isRentalPeriod=false)
          // Assignment will show as red warning to indicate room needs to be rented

          // Determine user's date range first (needed for half-day check)
          let userStartDate = null;
          let userEndDate = null;

          // Get dates from travel days or assignments
          if (item.travelDays && item.travelDays.length > 0) {
            const checkInTravel = item.travelDays.find(t => t.travel_type === 'check_in');
            const checkOutTravel = item.travelDays.find(t => t.travel_type === 'check_out');
            if (checkInTravel) userStartDate = new Date(checkInTravel.travel_date);
            if (checkOutTravel) userEndDate = new Date(checkOutTravel.travel_date);
          }

          if (item.allAssignments && item.allAssignments.length > 0) {
            const firstAssignment = item.allAssignments[0];
            if (!userStartDate) userStartDate = new Date(firstAssignment.start_date);
            if (!userEndDate) userEndDate = new Date(firstAssignment.end_date);

            // Expand range if travel days extend it
            if (item.travelDays) {
              item.travelDays.forEach(travel => {
                const travelDate = new Date(travel.travel_date);
                if (travel.travel_type === 'check_in' && (!userStartDate || travelDate < userStartDate)) {
                  userStartDate = travelDate;
                }
                if (travel.travel_type === 'check_out' && (!userEndDate || travelDate > userEndDate)) {
                  userEndDate = travelDate;
                }
              });
            }
          }

          // Check if room is already occupied (with half-day compatibility)
          if (hasAssignment && roomAssignment) {
            const canShare = userStartDate && userEndDate
              ? canShareDay(roomAssignment, userStartDate, userEndDate, date)
              : false;

            if (!canShare) {
              alert(`Room ${roomNumber} is already occupied on ${format(date, 'MMM d, yyyy')}.`);
              return;
            }
            // If canShare is true, we allow the drop (half-day sharing scenario)
          }

          const userId = item.user.id;
          const userName = item.user.first_name && item.user.last_name
            ? `${item.user.first_name} ${item.user.last_name}`
            : (item.user.username || 'User');

          // Check if user is assigned to ANY accommodation on this date (not just current one)
          const currentDateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
          const existingAssignmentAnywhere = assignments.find(a => {
            if (a.user !== userId) return false;
            // Skip if it's the same room in the same accommodation we're dropping to
            if (a.accommodation === accommodation.id && a.room_number === roomNumber) return false;

            const assignmentStart = new Date(a.start_date);
            const assignmentEnd = new Date(a.end_date);
            const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
            const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());

            return currentDateOnly >= assignmentStartOnly && currentDateOnly <= assignmentEndOnly;
          });

          // Helper function to proceed with showing assignment modal
          const proceedWithAssignment = () => {
            // Find user's current project assignment for this date (for display purposes only)
            let userProjectAssignment = item.allAssignments && item.allAssignments.length > 0
              ? item.allAssignments.find(assignment => {
                  const assignmentStart = new Date(assignment.start_date);
                  const assignmentEnd = new Date(assignment.end_date);
                  const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
                  const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());
                  return currentDateOnly >= assignmentStartOnly && currentDateOnly <= assignmentEndOnly;
                })
              : null;

            if (!userProjectAssignment && item.travelDays) {
              const travelOnDay = item.travelDays.find(travel => {
                const travelDate = new Date(travel.travel_date);
                const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
                return travelDateOnly.getTime() === currentDateOnly.getTime() && travel.needsAccommodation;
              });

              if (travelOnDay) {
                userProjectAssignment = {
                  start_date: travelOnDay.travel_date,
                  end_date: travelOnDay.travel_date,
                  role: `${travelOnDay.travel_type === 'check_in' ? 'Check-in' : 'Check-out'} Travel`,
                  user: item.user.id,
                  project: selectedProject
                };
              }
            }

            // Calculate full date range from assignment and travel days
            let fullRangeStart = null;
            let fullRangeEnd = null;

            // Get dates from assignment
            if (item.start_date && item.end_date) {
              fullRangeStart = new Date(item.start_date);
              fullRangeEnd = new Date(item.end_date);
            } else if (item.allAssignments && item.allAssignments.length > 0) {
              // Fallback to allAssignments
              const firstAssign = item.allAssignments[0];
              fullRangeStart = new Date(firstAssign.start_date);
              fullRangeEnd = new Date(firstAssign.end_date);
            }

            // Extend range to include travel days
            if (item.travelDays && item.travelDays.length > 0) {
              item.travelDays.forEach(travel => {
                const travelDate = new Date(travel.travel_date);
                if (!fullRangeStart || travelDate < fullRangeStart) {
                  fullRangeStart = new Date(travelDate);
                }
                if (!fullRangeEnd || travelDate > fullRangeEnd) {
                  fullRangeEnd = new Date(travelDate);
                }
              });
            }

            // Fallback to clicked date if no range found
            if (!fullRangeStart) fullRangeStart = date;
            if (!fullRangeEnd) fullRangeEnd = date;

            // Get uncovered periods from drag item (calculated in UserCard)
            const uncoveredPeriods = item.uncoveredPeriods || [];
            const hasAnyCoverage = item.hasAnyCoverage || false;
            const existingAssignmentIds = item.existingAssignmentIds || [];

            // Show assignment modal with room number and full date range
            setAssignmentData({
              userId,
              userName,
              accommodationId: accommodation.id,
              accommodationName: accommodation.name,
              startDate: fullRangeStart,
              endDate: fullRangeEnd,
              dropDate: date, // The specific date where card was dropped
              hasConflict: !!existingAssignmentAnywhere,
              userProjectAssignment,
              roomNumber: roomNumber,
              conflictAssignmentId: existingAssignmentAnywhere ? existingAssignmentAnywhere.id : null,
              // Warning flag if room is not rented yet
              isRoomNotRented: !isRentalPeriod,
              // Uncovered periods for smart defaults
              uncoveredPeriods: uncoveredPeriods,
              // Flag indicating if user has any accommodation coverage for this rotation
              hasAnyCoverage: hasAnyCoverage,
              // IDs of existing accommodation assignments for this card (for full period override)
              existingAssignmentIds: existingAssignmentIds,
              // Flag indicating this is a move from existing assignment (user confirmed move)
              // When true: default to "Full period" and always show "Custom period" option
              isMovingExistingAssignment: !!existingAssignmentAnywhere
            });
            setShowAssignmentModal(true);
          };

          // Check if user already has assignment - show confirmation modal
          if (existingAssignmentAnywhere) {
            const existingAccommodation = accommodations.find(acc => acc.id === existingAssignmentAnywhere.accommodation);
            const existingAccommodationName = existingAccommodation?.name || 'Unknown';
            const isSameAccommodation = existingAssignmentAnywhere.accommodation === accommodation.id;

            setConfirmationModal({
              title: 'Move Accommodation Assignment',
              message: (
                <div className="space-y-2">
                  <p>
                    <strong>{userName}</strong> is already assigned to:
                  </p>
                  <div className="bg-steel-100 p-2 rounded">
                    <div className="font-medium">{existingAccommodationName}</div>
                    <div className="text-slate-500">Room {existingAssignmentAnywhere.room_number}</div>
                  </div>
                  <p className="pt-2">
                    Move to <strong>{accommodation.name}</strong> (Room {roomNumber})?
                  </p>
                </div>
              ),
              confirmText: 'Move',
              cancelText: 'Cancel',
              confirmButtonClass: 'bg-brand-500 hover:bg-brand-500',
              icon: 'warning',
              onConfirm: proceedWithAssignment
            });
            return;
          }

          // No conflict - proceed directly
          proceedWithAssignment();
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
        draggedItem: monitor.getItem()
      })
    });

    // Check if dragged user/assignment is available on this date
    const isUserAvailableOnDate = () => {
      // Check if resizeState exists (resizing assignments)
      if (resizeState && resizeState.originalAssignment) {
        const currentDateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

        // Use dates directly from the resizing assignment
        const assignmentStart = new Date(resizeState.originalAssignment.start_date);
        const assignmentEnd = new Date(resizeState.originalAssignment.end_date);
        const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
        const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());

        return currentDateOnly >= assignmentStartOnly && currentDateOnly <= assignmentEndOnly;
      }

      // Check if dragState exists (custom drag for assignments)
      if (dragState && dragState.originalAssignment) {
        const currentDateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

        // Use dates directly from the dragged assignment
        const assignmentStart = new Date(dragState.originalAssignment.start_date);
        const assignmentEnd = new Date(dragState.originalAssignment.end_date);
        const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
        const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());

        return currentDateOnly >= assignmentStartOnly && currentDateOnly <= assignmentEndOnly;
      }

      // Check if draggedItem exists (react-dnd drag for users from sidebar or assignments from calendar)
      if (!draggedItem) return false;

      // Handle dragged USER or ASSIGNMENT (both use same logic now)
      if (!(draggedItem.type === 'USER' || draggedItem.type === 'ASSIGNMENT' || draggedItem.user)) return false;

      const currentDateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

      // Check if user/assignment has assignment data covering this day
      const hasAssignmentOnDay = draggedItem.allAssignments && draggedItem.allAssignments.some(assignment => {
        const assignmentStart = new Date(assignment.start_date);
        const assignmentEnd = new Date(assignment.end_date);
        const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
        const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());

        return currentDateOnly >= assignmentStartOnly && currentDateOnly <= assignmentEndOnly;
      });

      // Check if user has travel check-in/check-out on this day (only for USER type)
      const hasTravelOnDay = draggedItem.travelDays && draggedItem.travelDays.some(travel => {
        const travelDate = new Date(travel.travel_date);
        const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
        return travelDateOnly.getTime() === currentDateOnly.getTime();
      });

      return hasAssignmentOnDay || hasTravelOnDay;
    };

    // Check if we are in RESIZE mode for THIS specific row (accommodation + room)
    const isResizingThisRow = resizeTargetPosition &&
      resizeTargetPosition.accommodationId === accommodation.id &&
      resizeTargetPosition.roomNumber === roomNumber;

    const userAvailable = isUserAvailableOnDate();

    // Check if this date is in an uncovered period (for USER drag highlighting)
    // Returns: 'uncovered' | 'covered' | null (if not dragging USER or date not in card range)
    const getDateCoverageStatus = () => {
      // USER drags have 'user' property but no 'type' property
      // ASSIGNMENT drags have both 'user' AND 'type: ASSIGNMENT'
      // We only want to show coverage highlighting for USER drags (not ASSIGNMENT drags)
      if (!draggedItem || !draggedItem.user) return null;
      if (draggedItem.type === 'ASSIGNMENT') return null; // Skip for ASSIGNMENT drags
      if (!userAvailable) return null; // Date not in user's card range

      const currentDateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const hasAnyCoverage = draggedItem.hasAnyCoverage || false;
      const uncoveredPeriods = draggedItem.uncoveredPeriods || [];

      // If user has no accommodation coverage, all dates are uncovered
      if (!hasAnyCoverage) return 'uncovered';

      // If no uncovered periods but has coverage, all dates in range are covered
      if (uncoveredPeriods.length === 0) return 'covered';

      // Check if date falls within any uncovered period
      // Note: period.start/end may be Date objects or strings (after serialization)
      const isInUncoveredPeriod = uncoveredPeriods.some(period => {
        const startDate = period.start instanceof Date ? period.start : new Date(period.start);
        const endDate = period.end instanceof Date ? period.end : new Date(period.end);
        const periodStart = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const periodEnd = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
        return currentDateOnly >= periodStart && currentDateOnly <= periodEnd;
      });

      return isInUncoveredPeriod ? 'uncovered' : 'covered';
    };
    const dateCoverageStatus = getDateCoverageStatus();
    const isDateUncovered = dateCoverageStatus === 'uncovered';
    const isDateCovered = dateCoverageStatus === 'covered';

    // Check if cell is free for drop (excluding the dragged assignment itself)
    // This allows visual feedback to show green on target cells even if the source assignment is there
    const isCellFreeForDrop = () => {
      if (!hasAssignment) return true;
      // If dragging an ASSIGNMENT, check if current cell's assignment is the one being dragged
      if (draggedItem?.type === 'ASSIGNMENT' && draggedItem.assignment) {
        return roomAssignment?.id === draggedItem.assignment.id;
      }
      return false;
    };
    const cellFreeForDrop = isCellFreeForDrop();

    // Check if this is a target cell during RESIZE (for visual feedback of new range)
    const isResizeTargetCell = isResizingThisRow &&
      dayIndex >= resizeTargetPosition.startDayIndex &&
      dayIndex < resizeTargetPosition.startDayIndex + resizeTargetPosition.assignmentLengthInDays;

    // Check if this is a target cell during DRAG (for visual feedback)
    // Target cells include ALL days covered by the assignment being dragged
    const isTargetCell = dragTargetPosition &&
      dragTargetPosition.accommodationId === accommodation.id &&
      dragTargetPosition.roomNumber === roomNumber &&
      dayIndex >= dragTargetPosition.startDayIndex &&
      dayIndex < dragTargetPosition.startDayIndex + dragTargetPosition.assignmentLengthInDays;

    // For resize target cells, determine if user is available on THIS specific day
    let resizeTargetCellUserAvailable = true;
    if (isResizeTargetCell && resizeTargetPosition.userId) {
      const currentDateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

      // Find ALL user's project assignment entries (user may have multiple assignments)
      const userProjectEntries = projectAssignments.filter(pa =>
        pa.user_data?.id === resizeTargetPosition.userId
      );

      if (userProjectEntries.length > 0) {
        // Collect all assignments and travel days from all entries
        const allAssignments = userProjectEntries.flatMap(entry => entry.assignments || []);
        const allTravelDays = userProjectEntries.flatMap(entry => entry.travelDays || []);

        // Check if user has project assignment on this day
        const hasProjectAssignment = allAssignments.some(assignment => {
          const assignmentStart = new Date(assignment.start_date);
          const assignmentEnd = new Date(assignment.end_date);
          const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
          const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());

          return currentDateOnly >= assignmentStartOnly && currentDateOnly <= assignmentEndOnly;
        });

        // Check if user has travel check-in/check-out on this day
        const hasTravelOnDay = allTravelDays.some(travel => {
          const travelDate = new Date(travel.travel_date);
          const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
          return travelDateOnly.getTime() === currentDateOnly.getTime();
        });

        resizeTargetCellUserAvailable = hasProjectAssignment || hasTravelOnDay;
      } else {
        resizeTargetCellUserAvailable = false;
      }
    }

    // For drag target cells, determine if user is available on THIS specific day
    let targetCellUserAvailable = true;
    if (isTargetCell && dragTargetPosition.userId) {
      const currentDateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

      // Find ALL user's project assignment entries (user may have multiple assignments)
      const userProjectEntries = projectAssignments.filter(pa =>
        pa.user_data?.id === dragTargetPosition.userId
      );

      if (userProjectEntries.length > 0) {
        // Collect all assignments and travel days from all entries
        const allAssignments = userProjectEntries.flatMap(entry => entry.assignments || []);
        const allTravelDays = userProjectEntries.flatMap(entry => entry.travelDays || []);

        // Check if user has project assignment on this day
        const hasProjectAssignment = allAssignments.some(assignment => {
          const assignmentStart = new Date(assignment.start_date);
          const assignmentEnd = new Date(assignment.end_date);
          const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
          const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());

          return currentDateOnly >= assignmentStartOnly && currentDateOnly <= assignmentEndOnly;
        });

        // Check if user has travel check-in/check-out on this day
        const hasTravelOnDay = allTravelDays.some(travel => {
          const travelDate = new Date(travel.travel_date);
          const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
          return travelDateOnly.getTime() === currentDateOnly.getTime();
        });

        targetCellUserAvailable = hasProjectAssignment || hasTravelOnDay;
      } else {
        targetCellUserAvailable = false;
      }
    }

    // Combine drag and drop refs
    const combinedRef = (el) => {
      drag(el);
      drop(el);
    };

    // Get resize status from resizeTargetPosition for coloring
    const resizeStatus = isResizeTargetCell ? resizeTargetPosition?.resizeStatus : null;

    return (
      <div
        ref={combinedRef}
        key={roomNumber}
        className={`text-xs text-center p-1 relative ${
          isDragging ? 'opacity-50' : ''
        } ${
          // PRIORITY 1: Show highlight for detected target position during RESIZE
          // Color based on resize validation status
          isResizeTargetCell
            ? (resizeStatus === 'blocked'
                ? 'bg-red-600 border-2 border-red-800'      // Blocked - red
                : resizeStatus === 'warning'
                  ? 'bg-orange-500 border-2 border-orange-700' // Warning (room not rented) - orange
                  : 'bg-green-600 border-2 border-green-800')  // Valid - green
            :
          // PRIORITY 2: Show highlight for detected target position during DRAG
          // Dark green if user available, red if not available
          isTargetCell
            ? (targetCellUserAvailable
                ? 'bg-green-700 border-2 border-green-900'  // User available - dark green
                : 'bg-red-700 border-2 border-red-900')     // User NOT available - red warning
            :
          // When RESIZING in this row - show availability
          isResizingThisRow
            ? userAvailable && !hasAssignment
              ? isRentalPeriod
                ? isOver
                  ? 'bg-green-200 border-2 border-green-600'   // Room free & rented - dark green when hovering
                  : 'bg-green-50 border border-green-400'      // Room free & rented - light green
                : isOver
                  ? 'bg-orange-200 border-2 border-orange-600' // Room free but NOT rented - dark orange when hovering (warning)
                  : 'bg-orange-50 border border-orange-400'    // Room free but NOT rented - light orange (warning)
              // Note: No red highlight for occupied cells - they're covered by assignment bars anyway
              : isWeekend
                ? 'bg-amber-100 border border-steel-700' // Not in assignment range or occupied + weekend = amber
                : 'bg-white border border-steel-300' // Not in assignment range or occupied = white
            :
          // When dragging RESERVATION - show availability based on room occupancy and rental status
          (draggedItem && draggedItem.type === 'RESERVATION')
            ? cellFreeForDrop
              ? isRentalPeriod
                // Room is free and rented - green (good to drop)
                ? isOver
                  ? 'bg-green-200 border-2 border-green-600'   // Free & rented - dark green when hovering
                  : 'bg-green-50 border border-green-400'      // Free & rented - light green
                // Room is free but NOT rented - orange (warning, but can drop)
                : isOver
                  ? 'bg-orange-200 border-2 border-orange-600' // Free but NOT rented - dark orange when hovering
                  : 'bg-orange-50 border border-orange-400'    // Free but NOT rented - light orange
              // Room is occupied - show red (blocked)
              : isOver
                ? 'bg-red-200 border-2 border-red-600'
                : 'bg-red-100 border border-red-400'
            :
          // When dragging assignment (dragState) OR user/assignment (draggedItem) - show availability
          (dragState || (draggedItem && (draggedItem.type === 'USER' || draggedItem.type === 'ASSIGNMENT' || draggedItem.user)))
            ? userAvailable && cellFreeForDrop
              ? isDateCovered
                // Date is already covered by existing accommodation - show yellow (info, not needed)
                ? isOver
                  ? 'bg-yellow-300 border-2 border-yellow-600'   // Already covered - dark yellow when hovering
                  : 'bg-yellow-100 border border-yellow-400'     // Already covered - light yellow
                : isRentalPeriod
                  // Date is uncovered and room is rented - show green (good to drop)
                  ? isOver
                    ? 'bg-green-200 border-2 border-green-600'   // Uncovered & rented - dark green when hovering
                    : 'bg-green-50 border border-green-400'      // Uncovered & rented - light green
                  // Date is uncovered but room NOT rented - show orange (warning)
                  : isOver
                    ? 'bg-orange-200 border-2 border-orange-600' // Uncovered but NOT rented - dark orange when hovering
                    : 'bg-orange-50 border border-orange-400'    // Uncovered but NOT rented - light orange
              // Note: No red highlight for occupied cells - they're covered by assignment bars anyway
              : isWeekend
                ? 'bg-amber-100 border border-steel-700' // Not in assignment range or occupied + weekend = amber
                : 'bg-white border border-steel-300' // Not in assignment range or occupied = white
            : (dragState || draggedItem)
              ? canDrop
                ? isOver
                  ? 'bg-green-200 border-2 border-green-600'
                  : 'bg-green-50 border border-green-400'
                : 'bg-steel-100 border border-steel-400 opacity-50'
              : // No dragging - normal state
                isOver && canDrop
                ? 'bg-green-100 border border-green-500'
                : isOver && !canDrop
                  ? 'bg-red-100 border border-red-500'
                  : isWeekend
                    ? 'bg-amber-100 border border-steel-700' // Weekend = amber
                    : !isRentalPeriod
                      ? 'bg-white border border-steel-300' // X = white
                      : 'bg-white border border-steel-300' // Normal = white
        }`}
        style={{
          fontSize: '8px',
          lineHeight: '14px',
          height: `${roomHeight}px`,
          minHeight: `${roomHeight}px`,
          maxHeight: `${roomHeight}px`,
          boxSizing: 'border-box',
          cursor: isRentalPeriod && !dragState && !draggedItem ? 'pointer' : 'default'
        }}
        title={hasAssignment && roomAssignment ? `${roomAssignment.user_details ?
          (roomAssignment.user_details.first_name && roomAssignment.user_details.last_name ?
            `${roomAssignment.user_details.first_name} ${roomAssignment.user_details.last_name}` :
            roomAssignment.user_details.username || roomAssignment.user_details.email || 'User') :
          'User'} - ${roomAssignment.start_date} to ${roomAssignment.end_date}` : `Room ${roomNumber} - Click to assign`}
        onClick={(e) => {
          // Only trigger if room is available and no dragging is happening
          if (isRentalPeriod && !dragState && !draggedItem && onCellClick) {
            e.stopPropagation();
            onCellClick(e, accommodation.id, roomNumber, date);
          }
        }}
      >
        {/* Red background overlay for unavailable days */}
        {!isRentalPeriod && (
          (() => {
            // Check if there's an assignment on this unavailable day
            const dateStr = format(date, 'yyyy-MM-dd');
            const hasAssignmentOnThisDay = dayAssignments.some(a =>
              String(a.room_number) === String(roomNumber)
            );

            return hasAssignmentOnThisDay ? (
              // Dark red with higher z-index to show through assignment
              <div className="absolute inset-0 bg-red-600 opacity-60 pointer-events-none z-[15]" />
            ) : (
              // Light red for unavailable days without assignment
              <div className="absolute inset-0 bg-red-200 pointer-events-none z-[5]" />
            );
          })()
        )}
      </div>
    );
  };

  const AccommodationCell = ({ accommodation, date, dayIndex, totalDays, assignments, numberOfRooms, dragState, dragTargetPosition, resizeTargetPosition }) => {
    // Compare dates only (not time) - reset time to 00:00:00 for accurate comparison
    const currentDateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    // Get actual rooms from accommodation
    const actualRooms = accommodation.rooms || [];

    // Check if at least one room is available on this date using the helper function
    const isAnyRoomAvailable = actualRooms.some(room => isRoomAvailableOnDate(room, date));

    // For backward compatibility
    const isRentalPeriod = isAnyRoomAvailable;

    const [{ isOver, draggedItem, canDrop }, drop] = useDrop({
      accept: 'USER',
      canDrop: () => false, // Disable drop on main cell - force users to drop on specific rooms
      drop: (item) => {
        console.log('AccommodationCell drop - date received:', {
          date: date,
          dateFormatted: format(date, 'yyyy-MM-dd'),
          dayOfMonth: date.getDate(),
          month: date.getMonth() + 1
        });

        console.log('🎯 DROP DEBUG - Item being dropped:', {
          user: item.user,
          allAssignments: item.allAssignments?.length || 0,
          travelDays: item.travelDays?.length || 0,
          accommodation: accommodation.name
        });
        
        if (!isRentalPeriod) {
          console.log('❌ DROP FAILED - Not in rental period');
          alert('This accommodation is not available on this date.');
          return;
        }
        
        // Check if user is available on this specific date (project assignments OR travel check-in/check-out)
        const currentDateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        
        // Check if user has project assignment on this day
        const hasProjectAssignment = item.allAssignments.some(assignment => {
          const assignmentStart = new Date(assignment.start_date);
          const assignmentEnd = new Date(assignment.end_date);
          const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
          const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());
          
          return currentDateOnly >= assignmentStartOnly && currentDateOnly <= assignmentEndOnly;
        });

        // Check if user has travel check-in/check-out on this day
        const hasTravelOnDay = item.travelDays && item.travelDays.some(travel => {
          const travelDate = new Date(travel.travel_date);
          const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
          return travelDateOnly.getTime() === currentDateOnly.getTime() && travel.needsAccommodation;
        });

        const userAvailable = hasProjectAssignment || hasTravelOnDay;

        console.log('🔍 DROP AVAILABILITY CHECK:', {
          date: format(date, 'MMM d, yyyy'),
          user: `${item.user.first_name} ${item.user.last_name}`,
          hasProjectAssignment,
          hasTravelOnDay,
          userAvailable,
          travelDaysCount: item.travelDays?.length || 0
        });
        
        if (!userAvailable) {
          const userName = item.user.first_name && item.user.last_name 
            ? `${item.user.first_name} ${item.user.last_name}`
            : (item.user.username || 'User');
          alert(`${userName} is not available on ${format(date, 'MMM d, yyyy')}.`);
          return;
        }
        
        // Check for accommodation conflicts
        const userId = item.user.id;
        const userName = item.user.first_name && item.user.last_name 
          ? `${item.user.first_name} ${item.user.last_name}`
          : (item.user.username || 'User');
        
        // Check if user is already assigned to THIS accommodation on this date
        const alreadyInThisAccommodation = assignments.find(a => 
          a.user === userId &&
          a.accommodation === accommodation.id &&
          new Date(a.start_date) <= currentDateOnly &&
          new Date(a.end_date) >= currentDateOnly
        );
        
        if (alreadyInThisAccommodation) {
          alert(`${userName} is already assigned to "${accommodation.name}" on ${format(date, 'MMM d, yyyy')}.`);
          return;
        }
        
        // Check if user is assigned to ANOTHER accommodation on this date
        const existingAssignment = assignments.find(a => 
          a.user === userId &&
          new Date(a.start_date) <= currentDateOnly &&
          new Date(a.end_date) >= currentDateOnly &&
          a.accommodation !== accommodation.id
        );
        
        if (existingAssignment) {
          const conflictAccommodation = accommodations.find(acc => acc.id === existingAssignment.accommodation);
          const conflictName = conflictAccommodation?.name || 'Unknown accommodation';
          
          if (window.confirm(`${userName} is already assigned to "${conflictName}" on ${format(date, 'MMM d, yyyy')}. Do you want to reassign them to "${accommodation.name}"?`)) {
            // Find user's current project assignment for this date
            let userProjectAssignment = item.allAssignments.find(assignment => {
              const assignmentStart = new Date(assignment.start_date);
              const assignmentEnd = new Date(assignment.end_date);
              const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
              const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());
              
              return currentDateOnly >= assignmentStartOnly && currentDateOnly <= assignmentEndOnly;
            });

            // If no project assignment found, check if this is a travel check-in/check-out day
            if (!userProjectAssignment && item.travelDays) {
              const travelOnDay = item.travelDays.find(travel => {
                const travelDate = new Date(travel.travel_date);
                const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
                return travelDateOnly.getTime() === currentDateOnly.getTime() && travel.needsAccommodation;
              });

              // Create a pseudo project assignment for travel days
              if (travelOnDay && travelOnDay.travel_date) {
                userProjectAssignment = {
                  start_date: travelOnDay.travel_date,
                  end_date: travelOnDay.travel_date, // Same day
                  role: `${travelOnDay.travel_type === 'check_in' ? 'Check-in' : 'Check-out'} Travel`,
                  user: item.user.id,
                  project: selectedProject
                };
              }
            }
            
            // Show assignment modal to set days
            setAssignmentData({
              userId,
              userName,
              accommodationId: accommodation.id,
              accommodationName: accommodation.name,
              startDate: date,
              hasConflict: true,
              conflictAssignmentId: existingAssignment.id,
              userProjectAssignment,
              roomNumber: null
            });
            setShowAssignmentModal(true);
          }
          return;
        }
        
        // Find user's current project assignment for this date
        let userProjectAssignment = item.allAssignments.find(assignment => {
          const assignmentStart = new Date(assignment.start_date);
          const assignmentEnd = new Date(assignment.end_date);
          const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
          const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());
          
          return currentDateOnly >= assignmentStartOnly && currentDateOnly <= assignmentEndOnly;
        });

        // If no project assignment found, check if this is a travel check-in/check-out day
        if (!userProjectAssignment && item.travelDays) {
          const travelOnDay = item.travelDays.find(travel => {
            const travelDate = new Date(travel.travel_date);
            const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
            return travelDateOnly.getTime() === currentDateOnly.getTime() && travel.needsAccommodation;
          });

          // Create a pseudo project assignment for travel days
          if (travelOnDay && travelOnDay.travel_date) {
            userProjectAssignment = {
              start_date: travelOnDay.travel_date,
              end_date: travelOnDay.travel_date, // Same day
              role: `${travelOnDay.travel_type === 'check_in' ? 'Check-in' : 'Check-out'} Travel`,
              user: item.user.id,
              project: selectedProject
            };
          }
        }
        
        // Show assignment modal to set days
        setAssignmentData({
          userId,
          userName,
          accommodationId: accommodation.id,
          accommodationName: accommodation.name,
          startDate: date,
          hasConflict: false,
          userProjectAssignment,
          roomNumber: null
        });
        setShowAssignmentModal(true);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
        draggedItem: monitor.getItem()
      })
    });
    
    // Calculate available beds for this specific date
    const dayAssignments = assignments.filter(a => {
      if (a.accommodation !== accommodation.id) return false;

      const assignmentStart = new Date(a.start_date);
      const assignmentEnd = new Date(a.end_date);
      const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
      const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());

      return currentDateOnly >= assignmentStartOnly && currentDateOnly <= assignmentEndOnly;
    });

    // Calculate capacity only from rooms available on this date
    let dayCapacity = 0;
    actualRooms.forEach(room => {
      if (isRoomAvailableOnDate(room, date)) {
        dayCapacity += room.capacity || 1;
      }
    });

    const availableBeds = dayCapacity - dayAssignments.length;

    return (
      <td
        ref={drop}
        className={`border p-1 relative transition-all duration-150 ${
          (date.getDay() === 0 || date.getDay() === 6)
            ? 'bg-amber-100'
            : !isRentalPeriod
            ? 'bg-white'
            : !isSameMonth(date, customDateRange.start)
            ? 'bg-steel-50'
            : 'bg-white'
        }`}
        style={{
          width: '40px',
          minWidth: '40px',
          maxWidth: '40px',
          height: `${getAccommodationRowHeight(accommodation.capacity)}px`,
          boxSizing: 'border-box' // Border is included in width, not added to it
        }}
      >
        <div className="h-full flex flex-col">
          {numberOfRooms > 0 ? (
            <div
              className="relative"
              style={{
                height: `${getTracksAreaHeight(accommodation.capacity)}px`,
                boxSizing: 'border-box'
              }}
            >
              <div
                className="flex flex-col"
                style={{
                  gap: `${CALENDAR_DIMENSIONS.ROOM_GAP}px`,
                  height: '100%'
                }}
              >
                {actualRooms.map((room, index) => {
                  // Check if this specific room is available on this date using helper function
                  const isRoomAvailable = isRoomAvailableOnDate(room, date);

                  return (
                    <RoomCell
                      key={room.id || index}
                      accommodation={accommodation}
                      date={date}
                      dayIndex={dayIndex}
                      room={room}
                      roomNumber={room.room_number}
                      isRentalPeriod={isRoomAvailable}
                      dayAssignments={dayAssignments}
                      numberOfRooms={numberOfRooms}
                      dragState={dragState}
                      dragTargetPosition={dragTargetPosition}
                      resizeTargetPosition={resizeTargetPosition}
                      onCellClick={handleCellClick}
                    />
                  );
                })}
              </div>

              {/* Render continuous lines for assignments that start on this day OR started before visible range */}
              {(() => {
                const filtered = assignments.filter(assignment => {
                  const assignmentStart = new Date(assignment.start_date);
                  const assignmentEnd = new Date(assignment.end_date);
                  const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
                  const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());

                  // Case 1: Assignment starts exactly on this day
                  const startsOnThisDay = currentDateOnly.getTime() === assignmentStartOnly.getTime();

                  // Case 2: Assignment started BEFORE the visible range but extends into it
                  // We should render it on the first visible day (dayIndex === 0)
                  const firstVisibleDay = days[0];
                  const firstVisibleDayOnly = new Date(firstVisibleDay.getFullYear(), firstVisibleDay.getMonth(), firstVisibleDay.getDate());
                  const startedBeforeVisibleRange = assignmentStartOnly < firstVisibleDayOnly;
                  const extendsIntoVisibleRange = assignmentEndOnly >= firstVisibleDayOnly;
                  const isFirstVisibleDay = dayIndex === 0;

                  const shouldRenderOnFirstDay = isFirstVisibleDay && startedBeforeVisibleRange && extendsIntoVisibleRange;

                  const matches = startsOnThisDay || shouldRenderOnFirstDay;

                  if (matches && assignment.user === 79) {
                    console.log('📍 Rendering assignment for user 79:', {
                      assignmentId: assignment.id,
                      start_date: assignment.start_date,
                      end_date: assignment.end_date,
                      room_number: assignment.room_number,
                      currentDate: format(date, 'yyyy-MM-dd'),
                      accommodation: accommodation.name,
                      startsOnThisDay,
                      shouldRenderOnFirstDay
                    });
                  }

                  return matches;
                });

                if (dayIndex === 0 && filtered.length > 0) {
                  console.log('🎨 First day rendering - found assignments to render:', filtered.length, 'isDomReady:', isDomReady);
                }

                return filtered;
              })().map((assignment) => {
                if (!assignment.room_number) return null;

                // Calculate track position based on room index in actualRooms array
                // This handles non-sequential room numbers (e.g., "101", "A", custom names)
                const trackNumber = actualRooms.findIndex(room =>
                  String(room.room_number) === String(assignment.room_number)
                );

                // If room not found in array, skip rendering
                if (trackNumber === -1) return null;

                // Use the main getAssignmentStyle function for consistent calculation
                const styleData = getAssignmentStyle(assignment, trackNumber, numberOfRooms);

                if (assignment.user === 79) {
                  console.log('📏 Style calculated for user 79:', {
                    assignmentId: assignment.id,
                    styleData,
                    days_length: days.length,
                    first_visible_day: days[0] ? format(days[0], 'yyyy-MM-dd') : 'none',
                    last_visible_day: days[days.length - 1] ? format(days[days.length - 1], 'yyyy-MM-dd') : 'none'
                  });
                }

                // Override left position to 0 since we're rendering from start position
                const style = {
                  ...styleData,
                  left: '0px'
                };

                // Build tooltip with validation errors if any
                const userName = assignment.user_details
                  ? (assignment.user_details.first_name && assignment.user_details.last_name
                    ? `${assignment.user_details.first_name} ${assignment.user_details.last_name}`
                    : assignment.user_details.username || assignment.user_details.email || 'User')
                  : 'User';

                let tooltipText = `${userName} - ${assignment.start_date} to ${assignment.end_date}`;
                if (style.validationErrors && style.validationErrors.length > 0) {
                  tooltipText += `\n\n⚠️ ISSUES:\n• ${style.validationErrors.join('\n• ')}`;
                }

                return (
                  <DraggableAssignmentBar
                    key={`${assignment.id}-${renderTrigger}`}
                    assignment={assignment}
                    style={style}
                    tooltipText={tooltipText}
                    onDelete={handleDeleteAssignment}
                    onResize={(e, assignment, handle) => handleMouseDown(e, assignment, 'resize', handle)}
                  />
                );
              })}
            </div>
          ) : (
            <div className="flex-1">
              {/* Old assignment rendering removed - now rendered as continuous lines */}
            </div>
          )}
          
          {/* Bed availability info at bottom - TEMPORARILY COMMENTED OUT
          <div
            className="text-xs text-center flex items-center justify-center"
            style={{ height: `${CALENDAR_DIMENSIONS.BED_COUNTER_HEIGHT}px`, minHeight: `${CALENDAR_DIMENSIONS.BED_COUNTER_HEIGHT}px` }}
          >
            {(() => {
              if (dayCapacity === 0) {
                return <div className="text-red-500 text-xs">N/A</div>;
              }

              // Calculate actual occupancy for the NIGHT
              // Logic: We care about who sleeps there tonight
              // - Check-in day (start_date): person WILL sleep there tonight = counts as 1
              // - Check-out day (end_date): person WON'T sleep there tonight = counts as 0
              // - Middle day: person sleeps there = counts as 1
              // - Single day (start = end): person sleeps there = counts as 1
              const roomOccupancy = new Map(); // roomNumber -> is occupied tonight (0 or 1)

              dayAssignments.forEach(assignment => {
                const roomNum = assignment.room_number || '1';
                const assignmentStart = new Date(assignment.start_date);
                const assignmentEnd = new Date(assignment.end_date);
                const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
                const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());

                const isCheckInDay = currentDateOnly.getTime() === assignmentStartOnly.getTime();
                const isCheckOutDay = currentDateOnly.getTime() === assignmentEndOnly.getTime();

                // Check-out day only (not check-in same day) = room is FREE tonight
                // Everything else = room is OCCUPIED tonight
                let occupiesTonight = 0;
                if (isCheckOutDay && !isCheckInDay) {
                  // Check-out only - person leaves in the morning, room is free tonight
                  occupiesTonight = 0;
                } else {
                  // Check-in, middle day, or single day stay - person sleeps there tonight
                  occupiesTonight = 1;
                }

                // Mark room as occupied if anyone sleeps there tonight
                const currentOccupancy = roomOccupancy.get(roomNum) || 0;
                roomOccupancy.set(roomNum, Math.max(currentOccupancy, occupiesTonight));
              });

              // Count total rooms occupied tonight
              let totalOccupancy = 0;
              roomOccupancy.forEach((occupied) => {
                totalOccupancy += occupied;
              });

              const displayOccupancy = totalOccupancy;

              // Build tooltip with user names
              const tooltip = dayAssignments.length > 0
                ? dayAssignments.map(assignment =>
                    assignment.user_details
                      ? (assignment.user_details.first_name && assignment.user_details.last_name
                          ? `${assignment.user_details.first_name} ${assignment.user_details.last_name}`
                          : assignment.user_details.username || assignment.user_details.email || 'User')
                      : 'User'
                  ).join(', ')
                : `${dayCapacity} beds available`;

              // Color logic:
              // - Full occupancy (e.g., 3/3): GREEN - best for us
              // - Partial occupancy (e.g., 2/3): ORANGE - not ideal, wasted capacity
              // - No occupancy (0/x): ORANGE - unfavorable, empty rooms
              const isFull = displayOccupancy >= dayCapacity;

              return (
                <div
                  className={`${displayOccupancy > 0
                    ? isFull
                      ? "text-green-600 font-semibold cursor-help"  // Full = green (good!)
                      : "text-orange-600 font-semibold cursor-help" // Partial = orange (not ideal)
                    : "text-orange-600 font-semibold cursor-help"   // Empty = orange (unfavorable - 0/x)
                  }`}
                  title={tooltip}
                >
                  {displayOccupancy > 0
                    ? `${displayOccupancy}/${dayCapacity}`
                    : dayCapacity
                  }
                </div>
              );
            })()}
          </div>
          */}
        </div>
      </td>
    );
  };

  const AddAccommodationForm = () => {
    // Get selected project data
    const currentProject = projects.find(p => String(p.id) === String(selectedProject));

    const [formData, setFormData] = useState({
      name: '',
      address: '',
      city: '',
      country: '',
      post_code: '',
      email: '',
      phone: '',
      comment: ''
    });

    const [rooms, setRooms] = useState([
      { room_number: '1', capacity: 1, availability_periods: [{ start_date: '', end_date: '' }] }
    ]);

    const [roomCount, setRoomCount] = useState(1);

    // Map preview state
    const [mapCoordinates, setMapCoordinates] = useState(null);
    const [isGeocodingPreview, setIsGeocodingPreview] = useState(false);
    const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);
    const geocodeTimeoutRef = useRef(null);
    const [initialProjectCoordinates, setInitialProjectCoordinates] = useState(null);
    const [clickedCoordinates, setClickedCoordinates] = useState(null); // Coordinates from map click

    // Handle map click - reverse geocode to get address
    const handleMapClick = async (coords) => {
      setClickedCoordinates(coords);
      setMapCoordinates(coords);
      setIsReverseGeocoding(true);

      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.lat}&lon=${coords.lng}&addressdetails=1`,
          {
            headers: {
              'Accept': 'application/json',
              'User-Agent': 'WindunoApp/1.0'
            }
          }
        );
        const data = await response.json();

        if (data && data.address) {
          const addr = data.address;
          // Build street address from components
          const streetParts = [];
          if (addr.road) streetParts.push(addr.road);
          if (addr.house_number) streetParts.push(addr.house_number);

          setFormData(prev => ({
            ...prev,
            address: streetParts.join(' ') || addr.suburb || addr.neighbourhood || '',
            city: addr.city || addr.town || addr.village || addr.municipality || '',
            country: addr.country || '',
            post_code: addr.postcode || ''
          }));
        }
      } catch (error) {
        console.warn('Reverse geocoding error:', error);
      } finally {
        setIsReverseGeocoding(false);
      }
    };

    // Use project coordinates or geocode project address on mount for initial map position
    useEffect(() => {
      if (!currentProject) return;

      // If project already has coordinates, use them
      if (currentProject.latitude && currentProject.longitude &&
          !isNaN(parseFloat(currentProject.latitude)) && !isNaN(parseFloat(currentProject.longitude))) {
        setInitialProjectCoordinates({
          lat: parseFloat(currentProject.latitude),
          lng: parseFloat(currentProject.longitude)
        });
        return;
      }

      // Otherwise geocode the address
      const projectAddress = `${currentProject.street_address || ''} ${currentProject.city || ''} ${currentProject.country || ''}`.trim();
      if (!projectAddress || projectAddress.length < 3) return;

      const geocodeProject = async () => {
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(projectAddress)}&limit=1`,
            {
              headers: {
                'Accept': 'application/json',
                'User-Agent': 'WindunoApp/1.0'
              }
            }
          );
          const data = await response.json();
          if (data && data.length > 0) {
            const lat = parseFloat(data[0].lat);
            const lng = parseFloat(data[0].lon);
            setInitialProjectCoordinates({ lat, lng });

            // Save coordinates to project for faster loading next time
            try {
              const token = localStorage.getItem('access_token');
              await axios.patch(
                `${API_BASE_URL}/projects/${currentProject.id}/`,
                { latitude: lat, longitude: lng },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              console.log(`Saved coordinates for project ${currentProject.name}: ${lat}, ${lng}`);
            } catch (saveError) {
              console.warn(`Failed to save project coordinates:`, saveError.message);
            }
          }
        } catch (error) {
          console.warn('Project geocoding error:', error);
        }
      };

      geocodeProject();
    }, [currentProject]);

    // Geocode address when it changes (debounced)
    // Skip if address was just set by map click (reverse geocoding)
    useEffect(() => {
      const address = `${formData.address || ''} ${formData.city || ''} ${formData.country || ''}`.trim();
      if (!address || address.length < 5) {
        // Only clear coordinates if not from map click
        if (!clickedCoordinates) {
          setMapCoordinates(null);
        }
        return;
      }

      // Skip forward geocoding if we just did reverse geocoding from map click
      if (clickedCoordinates) {
        // Reset clicked coordinates after a short delay to allow future typing to trigger geocoding
        const resetTimeout = setTimeout(() => {
          setClickedCoordinates(null);
        }, 500);
        return () => clearTimeout(resetTimeout);
      }

      // Debounce geocoding - wait 1 second after user stops typing
      if (geocodeTimeoutRef.current) {
        clearTimeout(geocodeTimeoutRef.current);
      }

      geocodeTimeoutRef.current = setTimeout(async () => {
        setIsGeocodingPreview(true);
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
            {
              headers: {
                'Accept': 'application/json',
                'User-Agent': 'WindunoApp/1.0'
              }
            }
          );
          const data = await response.json();
          if (data && data.length > 0) {
            setMapCoordinates({
              lat: parseFloat(data[0].lat),
              lng: parseFloat(data[0].lon)
            });
          } else {
            setMapCoordinates(null);
          }
        } catch (error) {
          console.warn('Geocoding error:', error);
          setMapCoordinates(null);
        }
        setIsGeocodingPreview(false);
      }, 1000);

      return () => {
        if (geocodeTimeoutRef.current) {
          clearTimeout(geocodeTimeoutRef.current);
        }
      };
    }, [formData.address, formData.city, formData.country, clickedCoordinates]);

    const handleRoomCountChange = (count) => {
      const newCount = parseInt(count) || 0;
      setRoomCount(newCount);

      if (newCount > 0) {
        const newRooms = [];
        const firstPeriod = rooms[0]?.availability_periods?.[0] || { start_date: '', end_date: '' };
        for (let i = 1; i <= newCount; i++) {
          newRooms.push({
            room_number: String(i),
            capacity: 1,
            availability_periods: [{ start_date: firstPeriod.start_date, end_date: firstPeriod.end_date }]
          });
        }
        setRooms(newRooms);
      }
    };

    const addRoom = () => {
      const nextNumber = rooms.length + 1;
      const firstPeriod = rooms[0]?.availability_periods?.[0] || { start_date: '', end_date: '' };
      setRooms([...rooms, {
        room_number: String(nextNumber),
        capacity: 1,
        availability_periods: [{ start_date: firstPeriod.start_date, end_date: firstPeriod.end_date }]
      }]);
      setRoomCount(nextNumber);
    };

    const removeRoom = (index) => {
      if (rooms.length > 1) {
        const newRooms = rooms.filter((_, i) => i !== index);
        setRooms(newRooms);
        setRoomCount(newRooms.length);
      }
    };

    const updateRoom = (index, field, value) => {
      const updatedRooms = [...rooms];
      updatedRooms[index] = { ...updatedRooms[index], [field]: value };
      setRooms(updatedRooms);
    };

    const addPeriod = (roomIndex) => {
      const updatedRooms = [...rooms];
      updatedRooms[roomIndex].availability_periods.push({ start_date: '', end_date: '' });
      setRooms(updatedRooms);
    };

    const removePeriod = (roomIndex, periodIndex) => {
      const updatedRooms = [...rooms];
      if (updatedRooms[roomIndex].availability_periods.length > 1) {
        updatedRooms[roomIndex].availability_periods = updatedRooms[roomIndex].availability_periods.filter((_, i) => i !== periodIndex);
        setRooms(updatedRooms);
      }
    };

    const updatePeriod = (roomIndex, periodIndex, field, value) => {
      const updatedRooms = [...rooms];
      updatedRooms[roomIndex].availability_periods[periodIndex] = {
        ...updatedRooms[roomIndex].availability_periods[periodIndex],
        [field]: value
      };

      // If updating first period of first room, copy to other rooms' first period if empty
      if (roomIndex === 0 && periodIndex === 0) {
        for (let i = 1; i < updatedRooms.length; i++) {
          if (updatedRooms[i].availability_periods[0] && !updatedRooms[i].availability_periods[0][field]) {
            updatedRooms[i].availability_periods[0] = {
              ...updatedRooms[i].availability_periods[0],
              [field]: value
            };
          }
        }
      }

      setRooms(updatedRooms);
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      try {
        if (!selectedProject) {
          alert('Please select a project before adding accommodation.');
          return;
        }

        // Validate rooms
        if (rooms.length === 0) {
          alert('Please add at least one room.');
          return;
        }

        for (const room of rooms) {
          if (!room.room_number) {
            alert('Please fill in room number for all rooms.');
            return;
          }
          for (const period of room.availability_periods) {
            if (!period.start_date || !period.end_date) {
              alert('Please fill in all availability period dates.');
              return;
            }
          }
        }

        // Create accommodation data (company is set automatically by backend)
        const accommodationData = {
          name: formData.name,
          address: formData.address || null,
          city: formData.city || null,
          country: formData.country || null,
          post_code: formData.post_code || null,
          email: formData.email || null,
          phone: formData.phone || null,
          comment: formData.comment || null,
          project: selectedProject,
          // Save coordinates if available from map preview
          latitude: mapCoordinates?.lat || null,
          longitude: mapCoordinates?.lng || null
        };

        // Create accommodation
        const accommodationRes = await axios.post(`${API_BASE_URL}/accommodation/`, accommodationData, { headers });
        const accommodationId = accommodationRes.data.id;

        // Create rooms and their availability periods
        for (const room of rooms) {
          const roomData = {
            accommodation: accommodationId,
            room_number: room.room_number,
            capacity: parseInt(room.capacity) || 1,
            is_active: true
          };
          const roomRes = await axios.post(`${API_BASE_URL}/rooms/`, roomData, { headers });
          const roomId = roomRes.data.id;

          // Create availability periods for this room
          for (const period of room.availability_periods) {
            await axios.post(`${API_BASE_URL}/room-availability-periods/`, {
              room: roomId,
              start_date: period.start_date,
              end_date: period.end_date
            }, { headers });
          }
        }

        setShowAddAccommodation(false);
        fetchData();
      } catch (error) {
        console.error('Error adding accommodation:', error);
        if (error.response) {
          console.error('Response data:', error.response.data);
          console.error('Response status:', error.response.status);
          alert(`Error: ${JSON.stringify(error.response.data)}`);
        }
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 z-50 p-4 flex items-center justify-center">
        <div className="bg-white rounded-xl flex flex-col w-full h-full max-w-7xl max-h-[95vh] border border-slate-200 shadow-2xl">
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-brand-50 to-white rounded-t-xl flex-shrink-0">
            <h3 className="text-lg font-bold text-slate-900">Add New Accommodation {currentProject ? `- ${currentProject.name}` : ''}</h3>
          </div>
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden p-6">
            <div className="flex gap-4 flex-1 overflow-hidden">
              {/* Left side - Accommodation Details */}
              <div className="w-72 flex-shrink-0 border-r border-slate-200 pr-4 overflow-y-auto">
                <h4 className="font-bold text-sm text-slate-700 mb-3">Accommodation Details</h4>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600">Name *</label>
                    <input
                      type="text"
                      placeholder="Hotel name"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Address</label>
                    <input
                      type="text"
                      placeholder="Street address"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-slate-600">City</label>
                      <input
                        type="text"
                        placeholder="City"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        value={formData.city}
                        onChange={(e) => setFormData({...formData, city: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600">Post Code</label>
                      <input
                        type="text"
                        placeholder="Post code"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        value={formData.post_code}
                        onChange={(e) => setFormData({...formData, post_code: e.target.value})}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Country</label>
                    <input
                      type="text"
                      placeholder="Country"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      value={formData.country}
                      onChange={(e) => setFormData({...formData, country: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Email</label>
                    <input
                      type="email"
                      placeholder="contact@hotel.com"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Phone</label>
                    <input
                      type="tel"
                      placeholder="+48 123 456 789"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Number of Rooms *</label>
                    <input
                      type="number"
                      min="1"
                      placeholder="5"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      value={roomCount}
                      onChange={(e) => handleRoomCountChange(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Notes</label>
                    <textarea
                      placeholder="Key lockbox code, contact info, etc."
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      rows={3}
                      value={formData.comment}
                      onChange={(e) => setFormData({...formData, comment: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Middle - Rooms */}
              <div className="w-96 flex-shrink-0 border-r border-slate-200 pr-4 flex flex-col overflow-hidden">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-bold text-sm text-slate-700">Rooms ({rooms.length})</h4>
                  <button
                    type="button"
                    onClick={addRoom}
                    className="px-2.5 py-1 text-xs font-bold bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors"
                  >
                    + Add Room
                  </button>
                </div>

                <div className="space-y-2 overflow-y-auto pr-2 flex-1">
                  {rooms.map((room, roomIndex) => (
                    <div key={roomIndex} className="border border-slate-200 rounded-lg p-2.5 bg-slate-50">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-16 flex-shrink-0">
                          <label className="text-xs font-medium text-slate-600">No.</label>
                          <input
                            type="text"
                            placeholder="101"
                            className="w-full px-2 py-1 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                            value={room.room_number}
                            onChange={(e) => updateRoom(roomIndex, 'room_number', e.target.value)}
                            required
                          />
                        </div>
                        {/* Beds field hidden - always defaults to 1 */}
                        <div className="flex-1"></div>
                        {rooms.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeRoom(roomIndex)}
                            className="text-red-500 hover:text-red-600 text-xs font-medium flex-shrink-0 transition-colors"
                          >
                            ✕ Remove
                          </button>
                        )}
                      </div>

                      {/* Availability periods for this room */}
                      <div className="ml-2 space-y-1">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-medium text-slate-600">Availability</label>
                          <button
                            type="button"
                            onClick={() => addPeriod(roomIndex)}
                            className="text-xs text-brand-500 hover:text-brand-600 font-medium transition-colors"
                          >
                            + Add
                          </button>
                        </div>
                        {room.availability_periods.map((period, periodIndex) => (
                          <div key={periodIndex} className="flex items-center gap-1.5">
                            <input
                              type="date"
                              className="flex-1 px-2 py-1 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                              value={period.start_date}
                              onChange={(e) => updatePeriod(roomIndex, periodIndex, 'start_date', e.target.value)}
                              required
                            />
                            <span className="text-xs text-slate-400">-</span>
                            <input
                              type="date"
                              className="flex-1 px-2 py-1 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                              value={period.end_date}
                              onChange={(e) => updatePeriod(roomIndex, periodIndex, 'end_date', e.target.value)}
                              required
                            />
                            {room.availability_periods.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removePeriod(roomIndex, periodIndex)}
                                className="text-red-500 hover:text-red-600 text-xs transition-colors"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right side - Map Preview */}
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-sm text-slate-700">Location Preview</h4>
                  <span className="text-xs text-brand-600 bg-brand-50 px-2 py-1 rounded-lg font-medium">
                    Click on map to set location
                  </span>
                </div>
                <div className="flex-1 border border-slate-200 rounded-xl overflow-hidden relative bg-slate-100">
                  {/* Loading overlay for geocoding */}
                  {(isGeocodingPreview || isReverseGeocoding) && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
                      <div className="flex items-center text-slate-500">
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        {isReverseGeocoding ? 'Getting address...' : 'Searching location...'}
                      </div>
                    </div>
                  )}
                  {mapCoordinates ? (
                    <MapContainer
                      center={[mapCoordinates.lat, mapCoordinates.lng]}
                      zoom={15}
                      style={{ width: '100%', height: '100%' }}
                      scrollWheelZoom={true}
                      key={`acc-${mapCoordinates.lat}-${mapCoordinates.lng}`}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <MapClickHandler onMapClick={handleMapClick} disabled={isReverseGeocoding} />
                      {/* Project marker */}
                      {initialProjectCoordinates && (
                        <Marker
                          position={[initialProjectCoordinates.lat, initialProjectCoordinates.lng]}
                          icon={createProjectIcon()}
                        >
                          <Popup>
                            <div className="font-bold text-red-600">{currentProject?.name || 'Project'}</div>
                            <div className="text-sm text-slate-500">Project Location</div>
                          </Popup>
                        </Marker>
                      )}
                      {/* Accommodation marker */}
                      <Marker position={[mapCoordinates.lat, mapCoordinates.lng]}>
                        <Popup>{formData.name || 'New Accommodation'}</Popup>
                      </Marker>
                    </MapContainer>
                  ) : initialProjectCoordinates ? (
                    <MapContainer
                      center={[initialProjectCoordinates.lat, initialProjectCoordinates.lng]}
                      zoom={12}
                      style={{ width: '100%', height: '100%' }}
                      scrollWheelZoom={true}
                      key={`project-${initialProjectCoordinates.lat}-${initialProjectCoordinates.lng}`}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <MapClickHandler onMapClick={handleMapClick} disabled={isReverseGeocoding} />
                      {/* Project marker */}
                      <Marker
                        position={[initialProjectCoordinates.lat, initialProjectCoordinates.lng]}
                        icon={createProjectIcon()}
                      >
                        <Popup>
                          <div className="font-bold text-red-600">{currentProject?.name || 'Project'}</div>
                          <div className="text-sm text-slate-500">Project Location</div>
                        </Popup>
                      </Marker>
                    </MapContainer>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-400">
                      <div className="text-center">
                        <MapPin className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Enter address or click on map</p>
                        <p className="text-xs mt-1">to set accommodation location</p>
                      </div>
                    </div>
                  )}
                </div>
                {mapCoordinates ? (
                  <div className="mt-2 text-xs text-center space-y-1">
                    <div className="text-slate-500">
                      📍 Accommodation location: {mapCoordinates.lat.toFixed(5)}, {mapCoordinates.lng.toFixed(5)}
                    </div>
                    {initialProjectCoordinates && (
                      <div className="text-red-500">
                        ⚡ Project location: {initialProjectCoordinates.lat.toFixed(5)}, {initialProjectCoordinates.lng.toFixed(5)}
                      </div>
                    )}
                  </div>
                ) : initialProjectCoordinates && (
                  <div className="mt-2 text-xs text-center space-y-1">
                    <div className="text-red-500">
                      ⚡ Project location: {currentProject?.name}
                    </div>
                    <div className="text-slate-400">
                      Enter accommodation address to see its location
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={() => setShowAddAccommodation(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 text-sm font-medium text-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 text-sm font-bold transition-colors shadow-lg shadow-brand-500/20"
              >
                Add Accommodation
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const EditAccommodationForm = () => {
    // Get selected project data
    const currentProject = projects.find(p => String(p.id) === String(selectedProject));

    const [formData, setFormData] = useState({
      name: editingAccommodation?.name || '',
      address: editingAccommodation?.address || '',
      city: editingAccommodation?.city || '',
      country: editingAccommodation?.country || '',
      post_code: editingAccommodation?.post_code || '',
      email: editingAccommodation?.email || '',
      phone: editingAccommodation?.phone || '',
      comment: editingAccommodation?.comment || ''
    });

    // Map preview state - initialize with existing coordinates if available
    const [mapCoordinates, setMapCoordinates] = useState(() => {
      if (editingAccommodation?.latitude && editingAccommodation?.longitude &&
          !isNaN(parseFloat(editingAccommodation.latitude)) && !isNaN(parseFloat(editingAccommodation.longitude))) {
        return {
          lat: parseFloat(editingAccommodation.latitude),
          lng: parseFloat(editingAccommodation.longitude)
        };
      }
      return null;
    });
    const [isGeocodingPreview, setIsGeocodingPreview] = useState(false);
    const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);
    const geocodeTimeoutRef = useRef(null);
    const [clickedCoordinates, setClickedCoordinates] = useState(null);

    // Project coordinates for map marker
    const projectCoordinates = useMemo(() => {
      if (currentProject?.latitude && currentProject?.longitude &&
          !isNaN(parseFloat(currentProject.latitude)) && !isNaN(parseFloat(currentProject.longitude))) {
        return {
          lat: parseFloat(currentProject.latitude),
          lng: parseFloat(currentProject.longitude)
        };
      }
      return null;
    }, [currentProject]);

    // Handle map click - reverse geocode to get address
    const handleMapClick = async (coords) => {
      setClickedCoordinates(coords);
      setMapCoordinates(coords);
      setIsReverseGeocoding(true);

      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.lat}&lon=${coords.lng}&addressdetails=1`,
          {
            headers: {
              'Accept': 'application/json',
              'User-Agent': 'WindunoApp/1.0'
            }
          }
        );
        const data = await response.json();

        if (data && data.address) {
          const addr = data.address;
          const streetParts = [];
          if (addr.road) streetParts.push(addr.road);
          if (addr.house_number) streetParts.push(addr.house_number);

          setFormData(prev => ({
            ...prev,
            address: streetParts.join(' ') || addr.suburb || addr.neighbourhood || '',
            city: addr.city || addr.town || addr.village || addr.municipality || '',
            country: addr.country || '',
            post_code: addr.postcode || ''
          }));
        }
      } catch (error) {
        console.warn('Reverse geocoding error:', error);
      } finally {
        setIsReverseGeocoding(false);
      }
    };

    // Track original address to detect changes
    const originalAddress = useMemo(() => {
      return `${editingAccommodation?.address || ''} ${editingAccommodation?.city || ''} ${editingAccommodation?.country || ''}`.trim();
    }, [editingAccommodation?.address, editingAccommodation?.city, editingAccommodation?.country]);

    // Geocode address when it changes (debounced)
    // Skip if address was just set by map click (reverse geocoding)
    useEffect(() => {
      const currentAddress = `${formData.address || ''} ${formData.city || ''} ${formData.country || ''}`.trim();

      // Skip if address hasn't changed and we have coordinates from DB
      if (currentAddress === originalAddress && editingAccommodation?.latitude && editingAccommodation?.longitude) {
        return;
      }

      if (!currentAddress || currentAddress.length < 5) {
        if (!clickedCoordinates) {
          setMapCoordinates(null);
        }
        return;
      }

      // Skip forward geocoding if we just did reverse geocoding from map click
      if (clickedCoordinates) {
        const resetTimeout = setTimeout(() => {
          setClickedCoordinates(null);
        }, 500);
        return () => clearTimeout(resetTimeout);
      }

      if (geocodeTimeoutRef.current) {
        clearTimeout(geocodeTimeoutRef.current);
      }

      geocodeTimeoutRef.current = setTimeout(async () => {
        setIsGeocodingPreview(true);
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(currentAddress)}&limit=1`,
            {
              headers: {
                'Accept': 'application/json',
                'User-Agent': 'WindunoApp/1.0'
              }
            }
          );
          const data = await response.json();
          if (data && data.length > 0) {
            setMapCoordinates({
              lat: parseFloat(data[0].lat),
              lng: parseFloat(data[0].lon)
            });
          } else {
            setMapCoordinates(null);
          }
        } catch (error) {
          console.warn('Geocoding error:', error);
          setMapCoordinates(null);
        }
        setIsGeocodingPreview(false);
      }, 1000);

      return () => {
        if (geocodeTimeoutRef.current) {
          clearTimeout(geocodeTimeoutRef.current);
        }
      };
    }, [formData.address, formData.city, formData.country, originalAddress, editingAccommodation?.latitude, editingAccommodation?.longitude, clickedCoordinates]);

    // Initialize rooms from editingAccommodation with availability_periods
    const [rooms, setRooms] = useState(() => {
      if (editingAccommodation?.rooms && editingAccommodation.rooms.length > 0) {
        return editingAccommodation.rooms.map(room => {
          // Use availability_periods if available, otherwise fallback to available_from/to
          let periods = [];
          if (room.availability_periods && room.availability_periods.length > 0) {
            periods = room.availability_periods.map(p => ({
              id: p.id,
              start_date: p.start_date || '',
              end_date: p.end_date || ''
            }));
          } else if (room.available_from || room.available_to) {
            periods = [{ start_date: room.available_from || '', end_date: room.available_to || '' }];
          } else {
            periods = [{ start_date: '', end_date: '' }];
          }

          return {
            id: room.id,
            room_number: room.room_number || '',
            capacity: room.capacity || 1,
            availability_periods: periods,
            is_active: room.is_active !== false
          };
        });
      }
      return [{ room_number: '1', capacity: 1, availability_periods: [{ start_date: '', end_date: '' }], is_active: true }];
    });

    const [roomCount, setRoomCount] = useState(rooms.length);

    const addRoom = () => {
      const nextNumber = rooms.length + 1;
      const firstPeriod = rooms[0]?.availability_periods?.[0] || { start_date: '', end_date: '' };
      setRooms([...rooms, {
        room_number: String(nextNumber),
        capacity: 1,
        availability_periods: [{ start_date: firstPeriod.start_date, end_date: firstPeriod.end_date }],
        is_active: true
      }]);
      setRoomCount(nextNumber);
    };

    const removeRoom = (index) => {
      if (rooms.length > 1) {
        const newRooms = rooms.filter((_, i) => i !== index);
        setRooms(newRooms);
        setRoomCount(newRooms.length);
      }
    };

    const updateRoom = (index, field, value) => {
      const updatedRooms = [...rooms];
      updatedRooms[index] = { ...updatedRooms[index], [field]: value };
      setRooms(updatedRooms);
    };

    const addPeriod = (roomIndex) => {
      const updatedRooms = [...rooms];
      updatedRooms[roomIndex].availability_periods.push({ start_date: '', end_date: '' });
      setRooms(updatedRooms);
    };

    const removePeriod = (roomIndex, periodIndex) => {
      const updatedRooms = [...rooms];
      if (updatedRooms[roomIndex].availability_periods.length > 1) {
        updatedRooms[roomIndex].availability_periods = updatedRooms[roomIndex].availability_periods.filter((_, i) => i !== periodIndex);
        setRooms(updatedRooms);
      }
    };

    const updatePeriod = (roomIndex, periodIndex, field, value) => {
      const updatedRooms = [...rooms];
      updatedRooms[roomIndex].availability_periods[periodIndex] = {
        ...updatedRooms[roomIndex].availability_periods[periodIndex],
        [field]: value
      };
      setRooms(updatedRooms);
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      try {
        // Validate rooms
        for (const room of rooms) {
          if (!room.room_number) {
            alert('Please fill in room number for all rooms.');
            return;
          }
          for (const period of room.availability_periods) {
            if (!period.start_date || !period.end_date) {
              alert('Please fill in all availability period dates.');
              return;
            }
          }
        }

        // Update accommodation data
        const accommodationData = {
          name: formData.name,
          address: formData.address || null,
          city: formData.city || null,
          country: formData.country || null,
          post_code: formData.post_code || null,
          email: formData.email || null,
          phone: formData.phone || null,
          comment: formData.comment || null,
          project: selectedProject,
          latitude: mapCoordinates?.lat || null,
          longitude: mapCoordinates?.lng || null
        };

        await axios.put(`${API_BASE_URL}/accommodation/${editingAccommodation.id}/`, accommodationData, { headers });

        // Handle rooms - update existing, create new, delete removed
        const existingRoomIds = editingAccommodation.rooms?.map(r => r.id) || [];
        const currentRoomIds = rooms.filter(r => r.id).map(r => r.id);

        // Delete removed rooms
        for (const roomId of existingRoomIds) {
          if (!currentRoomIds.includes(roomId)) {
            await axios.delete(`${API_BASE_URL}/rooms/${roomId}/`, { headers });
          }
        }

        // Update or create rooms and their availability periods
        for (const room of rooms) {
          const roomData = {
            accommodation: editingAccommodation.id,
            room_number: room.room_number,
            capacity: parseInt(room.capacity) || 1,
            is_active: room.is_active !== false
          };

          let roomId;
          if (room.id) {
            // Update existing room
            await axios.put(`${API_BASE_URL}/rooms/${room.id}/`, roomData, { headers });
            roomId = room.id;

            // Get existing periods for this room
            const existingPeriodIds = editingAccommodation.rooms
              ?.find(r => r.id === room.id)
              ?.availability_periods?.map(p => p.id) || [];
            const currentPeriodIds = room.availability_periods.filter(p => p.id).map(p => p.id);

            // Delete removed periods
            for (const periodId of existingPeriodIds) {
              if (!currentPeriodIds.includes(periodId)) {
                await axios.delete(`${API_BASE_URL}/room-availability-periods/${periodId}/`, { headers });
              }
            }
          } else {
            // Create new room
            const roomRes = await axios.post(`${API_BASE_URL}/rooms/`, roomData, { headers });
            roomId = roomRes.data.id;
          }

          // Update or create availability periods
          for (const period of room.availability_periods) {
            const periodData = {
              room: roomId,
              start_date: period.start_date,
              end_date: period.end_date
            };

            if (period.id) {
              await axios.put(`${API_BASE_URL}/room-availability-periods/${period.id}/`, periodData, { headers });
            } else {
              await axios.post(`${API_BASE_URL}/room-availability-periods/`, periodData, { headers });
            }
          }
        }

        setEditingAccommodation(null);
        fetchData();
      } catch (error) {
        console.error('Error updating accommodation:', error);
        if (error.response) {
          console.error('Response data:', error.response.data);
          console.error('Response status:', error.response.status);
          alert(`Error: ${JSON.stringify(error.response.data)}`);
        }
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 z-50 p-4 flex items-center justify-center">
        <div className="bg-white rounded-xl flex flex-col w-full h-full max-w-7xl max-h-[95vh] border border-slate-200 shadow-2xl">
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-brand-50 to-white rounded-t-xl flex-shrink-0">
            <h3 className="text-lg font-bold text-slate-900">Edit Accommodation {currentProject ? `- ${currentProject.name}` : ''}</h3>
          </div>
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden p-6">
            <div className="flex gap-4 flex-1 overflow-hidden">
              {/* Left side - Accommodation Details */}
              <div className="w-72 flex-shrink-0 border-r border-slate-200 pr-4 overflow-y-auto">
                <h4 className="font-bold text-sm text-slate-700 mb-3">Accommodation Details</h4>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600">Name *</label>
                    <input
                      type="text"
                      placeholder="Hotel name"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Address</label>
                    <input
                      type="text"
                      placeholder="Street address"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-slate-600">City</label>
                      <input
                        type="text"
                        placeholder="City"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        value={formData.city}
                        onChange={(e) => setFormData({...formData, city: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600">Post Code</label>
                      <input
                        type="text"
                        placeholder="Post code"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        value={formData.post_code}
                        onChange={(e) => setFormData({...formData, post_code: e.target.value})}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Country</label>
                    <input
                      type="text"
                      placeholder="Country"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      value={formData.country}
                      onChange={(e) => setFormData({...formData, country: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Email</label>
                    <input
                      type="email"
                      placeholder="contact@hotel.com"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Phone</label>
                    <input
                      type="tel"
                      placeholder="+48 123 456 789"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Notes</label>
                    <textarea
                      placeholder="Key lockbox code, contact info, etc."
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      rows={3}
                      value={formData.comment}
                      onChange={(e) => setFormData({...formData, comment: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Middle - Rooms */}
              <div className="w-96 flex-shrink-0 border-r border-slate-200 pr-4 flex flex-col overflow-hidden">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-bold text-sm text-slate-700">Rooms ({rooms.length})</h4>
                  <button
                    type="button"
                    onClick={addRoom}
                    className="px-2.5 py-1 text-xs font-bold bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors"
                  >
                    + Add Room
                  </button>
                </div>

                <div className="space-y-2 overflow-y-auto pr-2 flex-1">
                  {rooms.map((room, roomIndex) => (
                    <div key={room.id || roomIndex} className="border border-slate-200 rounded-lg p-2.5 bg-slate-50">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-16 flex-shrink-0">
                          <label className="text-xs font-medium text-slate-600">No.</label>
                          <input
                            type="text"
                            placeholder="101"
                            className="w-full px-2 py-1 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                            value={room.room_number}
                            onChange={(e) => updateRoom(roomIndex, 'room_number', e.target.value)}
                            required
                          />
                        </div>
                        {/* Beds field hidden - always defaults to 1 */}
                        <div className="flex-1"></div>
                        {rooms.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeRoom(roomIndex)}
                            className="text-red-500 hover:text-red-600 text-xs font-medium flex-shrink-0 transition-colors"
                          >
                            ✕ Remove
                          </button>
                        )}
                      </div>

                      {/* Availability periods for this room */}
                      <div className="ml-2 space-y-1">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-medium text-slate-600">Availability</label>
                          <button
                            type="button"
                            onClick={() => addPeriod(roomIndex)}
                            className="text-xs text-brand-500 hover:text-brand-600 font-medium transition-colors"
                          >
                            + Add Period
                          </button>
                        </div>
                        {room.availability_periods.map((period, periodIndex) => (
                          <div key={period.id || periodIndex} className="flex items-center gap-1.5">
                            <input
                              type="date"
                              className="flex-1 px-2 py-1 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                              value={period.start_date}
                              onChange={(e) => updatePeriod(roomIndex, periodIndex, 'start_date', e.target.value)}
                              required
                            />
                            <span className="text-xs text-slate-400">-</span>
                            <input
                              type="date"
                              className="flex-1 px-2 py-1 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                              value={period.end_date}
                              onChange={(e) => updatePeriod(roomIndex, periodIndex, 'end_date', e.target.value)}
                              required
                            />
                            {room.availability_periods.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removePeriod(roomIndex, periodIndex)}
                                className="text-red-500 hover:text-red-600 text-xs transition-colors"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right side - Map Preview */}
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-sm text-slate-700">Map Preview</h4>
                  <span className="text-xs text-brand-600 bg-brand-50 px-2 py-1 rounded-lg font-medium">
                    Click on map to change location
                  </span>
                </div>
                <div className="flex-1 border border-slate-200 rounded-xl overflow-hidden relative" style={{ minHeight: '300px' }}>
                  {/* Loading overlay */}
                  {(isGeocodingPreview || isReverseGeocoding) && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                      <div className="flex items-center text-slate-500">
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        {isReverseGeocoding ? 'Getting address...' : 'Geocoding...'}
                      </div>
                    </div>
                  )}
                  {mapCoordinates ? (
                    <MapContainer
                      key={`edit-${mapCoordinates.lat}-${mapCoordinates.lng}`}
                      center={[mapCoordinates.lat, mapCoordinates.lng]}
                      zoom={14}
                      style={{ height: '100%', width: '100%' }}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      />
                      <MapClickHandler onMapClick={handleMapClick} disabled={isReverseGeocoding} />
                      {/* Project marker */}
                      {projectCoordinates && (
                        <Marker
                          position={[projectCoordinates.lat, projectCoordinates.lng]}
                          icon={createProjectIcon()}
                        >
                          <Popup>
                            <div className="font-bold text-red-600">{currentProject?.name || 'Project'}</div>
                            <div className="text-sm text-slate-500">Project Location</div>
                          </Popup>
                        </Marker>
                      )}
                      {/* Accommodation marker */}
                      <Marker
                        position={[mapCoordinates.lat, mapCoordinates.lng]}
                        icon={createNumberedIcon(1)}
                      >
                        <Popup>{formData.name || 'Accommodation'}</Popup>
                      </Marker>
                    </MapContainer>
                  ) : projectCoordinates ? (
                    <MapContainer
                      key={`project-edit-${projectCoordinates.lat}-${projectCoordinates.lng}`}
                      center={[projectCoordinates.lat, projectCoordinates.lng]}
                      zoom={12}
                      style={{ height: '100%', width: '100%' }}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      />
                      <MapClickHandler onMapClick={handleMapClick} disabled={isReverseGeocoding} />
                      {/* Project marker */}
                      <Marker
                        position={[projectCoordinates.lat, projectCoordinates.lng]}
                        icon={createProjectIcon()}
                      >
                        <Popup>
                          <div className="font-bold text-red-600">{currentProject?.name || 'Project'}</div>
                          <div className="text-sm text-slate-500">Project Location</div>
                        </Popup>
                      </Marker>
                    </MapContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-slate-400">
                      <div className="text-center">
                        <div className="text-4xl mb-2">📍</div>
                        <p>Enter an address or click on map</p>
                        <p className="text-xs mt-1">to set accommodation location</p>
                      </div>
                    </div>
                  )}
                </div>
                {mapCoordinates ? (
                  <div className="mt-2 text-xs text-center space-y-1">
                    <div className="text-slate-500">
                      📍 Accommodation: {mapCoordinates.lat.toFixed(5)}, {mapCoordinates.lng.toFixed(5)}
                    </div>
                    {projectCoordinates && (
                      <div className="text-red-500">
                        ⚡ Project: {projectCoordinates.lat.toFixed(5)}, {projectCoordinates.lng.toFixed(5)}
                      </div>
                    )}
                  </div>
                ) : projectCoordinates && (
                  <div className="mt-2 text-xs text-center space-y-1">
                    <div className="text-red-500">
                      ⚡ Project location: {currentProject?.name}
                    </div>
                    <div className="text-slate-400">
                      Click on map to set accommodation location
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={() => setEditingAccommodation(null)}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 text-sm font-medium text-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 text-sm font-bold transition-colors shadow-lg shadow-brand-500/20"
              >
                Update Accommodation
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const AssignmentModal = () => {
    const [selectedRoomNumber, setSelectedRoomNumber] = useState(assignmentData?.roomNumber || null);
    const [periodOption, setPeriodOption] = useState('full'); // 'full', 'single', 'custom', 'uncovered_0', 'uncovered_1', etc.
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');

    // Determine if this is edit mode (moving existing assignment) or create mode (new assignment)
    const isEditMode = assignmentData?.isEditMode || false;
    const existingAssignment = assignmentData?.existingAssignment;

    // Get current accommodation details
    const currentAccommodation = accommodations.find(acc => acc.id === assignmentData?.accommodationId);
    const numberOfRooms = currentAccommodation?.capacity || 0;
    const needsRoomSelection = numberOfRooms > 0 && !assignmentData?.roomNumber;

    // For edit mode, also get source accommodation name
    const sourceAccommodation = isEditMode
      ? accommodations.find(acc => acc.id === assignmentData?.sourceAccommodationId)
      : null;

    // Calculate dates for options
    const fullPeriodStart = assignmentData?.startDate;
    const fullPeriodEnd = assignmentData?.endDate;
    const dropDate = assignmentData?.dropDate;
    const singleDayEnd = dropDate ? new Date(new Date(dropDate).setDate(dropDate.getDate() + 1)) : null;

    // Get uncovered periods (only for create mode - USER drag)
    // When moving existing assignment, don't show uncovered periods as primary options
    const isMovingExistingAssignment = assignmentData?.isMovingExistingAssignment || false;
    const uncoveredPeriods = (!isEditMode && !isMovingExistingAssignment) ? (assignmentData?.uncoveredPeriods || []) : [];
    const hasUncoveredPeriods = uncoveredPeriods.length > 0;
    // User is fully covered only if they have some coverage AND no uncovered periods
    const hasAnyCoverage = assignmentData?.hasAnyCoverage || false;
    const isFullyCovered = !isEditMode && !isMovingExistingAssignment && hasAnyCoverage && uncoveredPeriods.length === 0;

    // Check room availability for full period
    // This determines if "Full period" option should be enabled or disabled
    const roomAvailability = useMemo(() => {
      if (!assignmentData?.accommodationId || !assignmentData?.roomNumber || !fullPeriodStart || !fullPeriodEnd) {
        return { isFullPeriodAvailable: true, maxStart: null, maxEnd: null, blockedBy: null };
      }
      return findMaxAvailablePeriod(
        assignmentData.accommodationId,
        assignmentData.roomNumber,
        fullPeriodStart,
        fullPeriodEnd,
        assignmentData.conflictAssignmentId // Exclude the assignment we're replacing
      );
    }, [assignmentData?.accommodationId, assignmentData?.roomNumber, fullPeriodStart, fullPeriodEnd, assignmentData?.conflictAssignmentId]);

    const isFullPeriodBlocked = !roomAvailability.isFullPeriodAvailable;
    const blockedByUser = roomAvailability.blockedBy?.user_details
      ? `${roomAvailability.blockedBy.user_details.first_name || ''} ${roomAvailability.blockedBy.user_details.last_name || ''}`.trim() || 'another user'
      : 'another user';

    // Check room rental availability for full period (warning only, not blocking)
    const rentalAvailability = useMemo(() => {
      if (!assignmentData?.accommodationId || !assignmentData?.roomNumber || !fullPeriodStart || !fullPeriodEnd) {
        return { isAvailable: true, unavailableDays: [], availableRange: null };
      }
      const result = checkRoomRentalAvailability(
        assignmentData.accommodationId,
        assignmentData.roomNumber,
        fullPeriodStart,
        fullPeriodEnd
      );

      // If not fully available, find the available range within the period
      if (!result.isAvailable && result.unavailableDays.length > 0) {
        const accommodation = accommodations.find(acc => acc.id === assignmentData.accommodationId);
        const room = accommodation?.rooms?.find(r => String(r.room_number) === String(assignmentData.roomNumber));

        if (room) {
          // Find continuous available range that includes dropDate or starts from fullPeriodStart
          let rangeStart = null;
          let rangeEnd = null;
          let currentDay = new Date(fullPeriodStart.getFullYear(), fullPeriodStart.getMonth(), fullPeriodStart.getDate());
          const endDay = new Date(fullPeriodEnd.getFullYear(), fullPeriodEnd.getMonth(), fullPeriodEnd.getDate());

          while (currentDay <= endDay) {
            if (isRoomAvailableOnDate(room, currentDay)) {
              if (!rangeStart) {
                rangeStart = new Date(currentDay);
              }
              rangeEnd = new Date(currentDay);
            } else if (rangeStart) {
              // Found a gap - if we already have a range, stop here
              break;
            }
            currentDay.setDate(currentDay.getDate() + 1);
          }

          if (rangeStart && rangeEnd) {
            result.availableRange = { start: rangeStart, end: rangeEnd };
          }
        }
      }

      return result;
    }, [assignmentData?.accommodationId, assignmentData?.roomNumber, fullPeriodStart, fullPeriodEnd, accommodations]);

    const hasRentalWarning = !rentalAvailability.isAvailable;

    // Find the uncovered period that contains dropDate (for Single night and Custom period options)
    const getDropDateUncoveredPeriod = () => {
      if (!dropDate) return null;
      // If moving existing assignment, treat entire period as available for custom selection
      if (isMovingExistingAssignment) {
        return { start: fullPeriodStart, end: fullPeriodEnd };
      }
      // If user has no coverage, the entire card period is "uncovered"
      if (!hasAnyCoverage) {
        return { start: fullPeriodStart, end: fullPeriodEnd };
      }
      // If user has coverage, find the uncovered period containing dropDate
      const dropDateOnly = new Date(dropDate.getFullYear(), dropDate.getMonth(), dropDate.getDate());
      return uncoveredPeriods.find(period => {
        const periodStart = new Date(period.start.getFullYear(), period.start.getMonth(), period.start.getDate());
        const periodEnd = new Date(period.end.getFullYear(), period.end.getMonth(), period.end.getDate());
        return dropDateOnly >= periodStart && dropDateOnly <= periodEnd;
      }) || null;
    };
    const dropDateUncoveredPeriod = getDropDateUncoveredPeriod();
    // When moving existing assignment, always allow single night and custom period
    const canUseSingleNight = dropDateUncoveredPeriod !== null || isMovingExistingAssignment;
    const canUseCustomPeriod = dropDateUncoveredPeriod !== null || isMovingExistingAssignment || isFullPeriodBlocked || hasRentalWarning;

    // Min/max dates for custom period
    // When full period is blocked, use the max available period; otherwise use full period or uncovered period
    const getCustomDateRange = () => {
      if (isFullPeriodBlocked && roomAvailability.maxStart && roomAvailability.maxEnd) {
        // Room is partially blocked by another assignment - use max available range
        return {
          min: format(roomAvailability.maxStart, 'yyyy-MM-dd'),
          max: format(roomAvailability.maxEnd, 'yyyy-MM-dd')
        };
      }
      if (hasRentalWarning && rentalAvailability.availableRange) {
        // Room not rented for full period - use available rental range
        return {
          min: format(rentalAvailability.availableRange.start, 'yyyy-MM-dd'),
          max: format(rentalAvailability.availableRange.end, 'yyyy-MM-dd')
        };
      }
      if (isMovingExistingAssignment) {
        return {
          min: fullPeriodStart ? format(fullPeriodStart, 'yyyy-MM-dd') : '',
          max: fullPeriodEnd ? format(fullPeriodEnd, 'yyyy-MM-dd') : ''
        };
      }
      return {
        min: dropDateUncoveredPeriod?.start ? format(dropDateUncoveredPeriod.start, 'yyyy-MM-dd') : '',
        max: dropDateUncoveredPeriod?.end ? format(dropDateUncoveredPeriod.end, 'yyyy-MM-dd') : ''
      };
    };
    const customDateRange = getCustomDateRange();
    const customMinDate = customDateRange.min;
    const customMaxDate = customDateRange.max;

    // Full period min/max (for reference in UI)
    const minDate = fullPeriodStart ? format(fullPeriodStart, 'yyyy-MM-dd') : '';
    const maxDate = fullPeriodEnd ? format(fullPeriodEnd, 'yyyy-MM-dd') : '';

    // Check availability when assignment data changes
    useEffect(() => {
      setSelectedRoomNumber(assignmentData?.roomNumber || null);

      // Determine default period option based on availability
      if (isFullPeriodBlocked) {
        // Full period is blocked by another assignment - default to custom with max available range
        setPeriodOption('custom');
      } else if (hasRentalWarning && rentalAvailability.availableRange) {
        // Room not rented for full period - default to custom with available rental range
        setPeriodOption('custom');
      } else if (isMovingExistingAssignment) {
        // Moving existing - default to full period
        setPeriodOption('full');
      } else if (!isEditMode && uncoveredPeriods.length > 0) {
        // Has uncovered periods - default to first uncovered
        setPeriodOption('uncovered_0');
      } else {
        // Default to full period
        setPeriodOption('full');
      }

      // Initialize custom dates
      if (isFullPeriodBlocked && roomAvailability.maxStart && roomAvailability.maxEnd) {
        // Set to max available range when blocked by assignment
        setCustomStartDate(format(roomAvailability.maxStart, 'yyyy-MM-dd'));
        setCustomEndDate(format(roomAvailability.maxEnd, 'yyyy-MM-dd'));
      } else if (hasRentalWarning && rentalAvailability.availableRange) {
        // Set to available rental range
        setCustomStartDate(format(rentalAvailability.availableRange.start, 'yyyy-MM-dd'));
        setCustomEndDate(format(rentalAvailability.availableRange.end, 'yyyy-MM-dd'));
      } else {
        setCustomStartDate(customMinDate || minDate);
        setCustomEndDate(customMaxDate || maxDate);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignmentData, minDate, maxDate, customMinDate, customMaxDate, isMovingExistingAssignment, isFullPeriodBlocked, roomAvailability, hasRentalWarning, rentalAvailability]);

    // Calculate effective dates based on selected option
    const getEffectiveDates = () => {
      // Handle uncovered period selection
      if (periodOption.startsWith('uncovered_')) {
        const index = parseInt(periodOption.split('_')[1], 10);
        if (uncoveredPeriods[index]) {
          return { start: uncoveredPeriods[index].start, end: uncoveredPeriods[index].end };
        }
      }

      switch (periodOption) {
        case 'single':
          return { start: dropDate, end: singleDayEnd };
        case 'custom':
          return {
            start: customStartDate ? new Date(customStartDate) : fullPeriodStart,
            end: customEndDate ? new Date(customEndDate) : fullPeriodEnd
          };
        case 'full':
        default:
          return { start: fullPeriodStart, end: fullPeriodEnd };
      }
    };

    const handleAssignment = async () => {
      // Validate room selection if needed
      if (needsRoomSelection && !selectedRoomNumber) {
        alert('Please select a room number');
        return;
      }

      const effectiveRoomNumber = selectedRoomNumber || assignmentData.roomNumber;
      const { start: effectiveStartDate, end: effectiveEndDate } = getEffectiveDates();

      // Validate custom dates
      if (periodOption === 'custom') {
        if (!customStartDate || !customEndDate) {
          alert('Please select both start and end dates for custom period');
          return;
        }
        if (new Date(customStartDate) > new Date(customEndDate)) {
          alert('Start date must be before or equal to end date');
          return;
        }
      }

      if (isEditMode && existingAssignment) {
        // Edit mode: Move existing assignment to new room/accommodation with selected period
        // Validate: Check if new room is available for the selected period
        if (!isRoomAvailableForPeriod(assignmentData.accommodationId, effectiveRoomNumber, effectiveStartDate, effectiveEndDate, existingAssignment.id)) {
          alert('Cannot move assignment. Room is already occupied during this period.');
          return;
        }

        // Move assignment to new room/accommodation with new dates
        handleMoveAssignment(existingAssignment, assignmentData.accommodationId, effectiveRoomNumber, effectiveStartDate, effectiveEndDate);
      } else {
        // Create mode: Create new assignment with selected period

        // If "Full period (override)" is selected and user has existing assignments,
        // delete all existing assignments for this card before creating the new one
        const existingAssignmentIds = assignmentData?.existingAssignmentIds || [];
        if (periodOption === 'full' && hasAnyCoverage && existingAssignmentIds.length > 0) {
          // Delete all existing accommodation assignments for this user's card
          existingAssignmentIds.forEach(assignmentId => {
            addPendingDelete(assignmentId);
          });
        }

        await handleAccommodationDrop(
          assignmentData.userId,
          assignmentData.accommodationId,
          effectiveStartDate,
          effectiveEndDate,
          assignmentData.conflictAssignmentId,
          effectiveRoomNumber
        );
      }

      setShowAssignmentModal(false);
      setAssignmentData(null);
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl w-[420px] border border-slate-200 shadow-2xl">
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-brand-50 to-white rounded-t-xl">
            <h3 className="text-lg font-bold text-slate-900">
              {isEditMode ? 'Move Accommodation Assignment' : 'Set Accommodation Period'}
            </h3>
          </div>
          <div className="p-6">
          <div className="space-y-4">
            <div>
              {isEditMode ? (
                <>
                  <p className="text-sm text-slate-600 mb-2">
                    Move <strong>{assignmentData?.userName}</strong> from{' '}
                    <strong>{sourceAccommodation?.name || 'Unknown'}</strong> (Room {assignmentData?.sourceRoomNumber})
                  </p>
                  <p className="text-sm text-slate-600 mb-2">
                    to <strong>{assignmentData?.accommodationName}</strong>
                    {assignmentData?.roomNumber && <span className="text-brand-600"> - Room {assignmentData.roomNumber}</span>}
                  </p>
                </>
              ) : (
                <p className="text-sm text-slate-600 mb-2">
                  Assign <strong>{assignmentData?.userName}</strong> to <strong>{assignmentData?.accommodationName}</strong>
                  {assignmentData?.roomNumber && <span className="text-brand-600"> - Room {assignmentData.roomNumber}</span>}
                </p>
              )}

              {/* Period selection - for both create and edit mode */}
              <div className="mt-3 space-y-2">
                <label className="block text-sm font-medium text-slate-700">
                  Select accommodation period:
                </label>

                {/* Info about fully covered user */}
                {isFullyCovered && (
                  <div className="text-sm text-green-600 p-2 bg-green-50 rounded border border-green-200">
                    This user already has full accommodation coverage for this period.
                  </div>
                )}

                {/* Uncovered periods - show as primary options if available */}
                {hasUncoveredPeriods && (
                  <>
                    <div className="text-xs text-orange-600 mb-1">
                      {uncoveredPeriods.length === 1
                        ? 'Remaining uncovered period:'
                        : `${uncoveredPeriods.length} uncovered periods remaining:`}
                    </div>
                    {uncoveredPeriods.map((period, index) => (
                      <label
                        key={index}
                        className={`flex items-start gap-2 p-2 border rounded cursor-pointer hover:bg-orange-50 ${
                          periodOption === `uncovered_${index}` ? 'border-orange-500 bg-orange-50' : 'border-orange-200'
                        }`}
                      >
                        <input
                          type="radio"
                          name="periodOption"
                          value={`uncovered_${index}`}
                          checked={periodOption === `uncovered_${index}`}
                          onChange={(e) => setPeriodOption(e.target.value)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-orange-700">
                            {uncoveredPeriods.length > 1 ? `Uncovered period ${index + 1}` : 'Uncovered period'}
                          </div>
                          <div className="text-xs text-orange-600">
                            {format(period.start, 'MMM d, yyyy')}
                            {period.start.getTime() !== period.end.getTime() && (
                              <> - {format(period.end, 'MMM d, yyyy')}</>
                            )}
                            {' '}({Math.round((period.end - period.start) / (1000 * 60 * 60 * 24)) + 1} days)
                          </div>
                        </div>
                      </label>
                    ))}
                    <div className="border-t pt-2 mt-2">
                      <div className="text-xs text-slate-500 mb-1">Other options:</div>
                    </div>
                  </>
                )}

                {/* Option: Full period - show differently based on coverage status and availability */}
                <label className={`flex items-start gap-2 p-2 border rounded ${
                  isFullPeriodBlocked
                    ? 'opacity-50 cursor-not-allowed bg-steel-50 border-steel-200'
                    : hasRentalWarning
                    ? 'cursor-pointer hover:bg-yellow-50 border-yellow-300'
                    : 'cursor-pointer hover:bg-steel-50'
                } ${hasUncoveredPeriods && !isFullPeriodBlocked ? 'opacity-75' : ''}`}>
                  <input
                    type="radio"
                    name="periodOption"
                    value="full"
                    checked={periodOption === 'full'}
                    onChange={(e) => setPeriodOption(e.target.value)}
                    className="mt-1"
                    disabled={isFullPeriodBlocked}
                  />
                  <div className="flex-1">
                    <div className={`text-sm font-medium ${isFullPeriodBlocked ? 'text-slate-400' : ''}`}>
                      {hasUncoveredPeriods && !isFullPeriodBlocked ? 'Full period (override)' : 'Full period'}
                      {isFullPeriodBlocked && <span className="text-red-400 ml-1">(unavailable)</span>}
                      {!isFullPeriodBlocked && hasRentalWarning && <span className="text-yellow-600 ml-1">(room not rented)</span>}
                    </div>
                    <div className={`text-xs ${isFullPeriodBlocked ? 'text-slate-400' : 'text-slate-500'}`}>
                      {fullPeriodStart && format(fullPeriodStart, 'MMM d, yyyy')}
                      {fullPeriodEnd && fullPeriodStart?.getTime() !== fullPeriodEnd?.getTime() && (
                        <> - {format(fullPeriodEnd, 'MMM d, yyyy')}</>
                      )}
                    </div>
                    {isFullPeriodBlocked && roomAvailability.blockedBy && (
                      <div className="text-xs text-red-500 mt-1">
                        {roomAvailability.blockPosition === 'before' ? (
                          <>Room occupied by {blockedByUser} until {format(new Date(roomAvailability.blockedBy.end_date), 'MMM d')}</>
                        ) : roomAvailability.blockPosition === 'full' ? (
                          <>Room fully occupied by {blockedByUser}</>
                        ) : (
                          <>Room occupied by {blockedByUser} from {format(new Date(roomAvailability.blockedBy.start_date), 'MMM d')}</>
                        )}
                      </div>
                    )}
                    {!isFullPeriodBlocked && hasRentalWarning && (
                      <div className="text-xs text-yellow-600 mt-1">
                        ⚠️ Room is not rented for {rentalAvailability.unavailableDays.length} day(s) in this period
                      </div>
                    )}
                  </div>
                </label>

                {/* Option: Single day (check-in on drop date, check-out next day) */}
                {/* Only show if drop date is uncovered (not already covered by existing assignment) */}
                {canUseSingleNight && (
                  <label className="flex items-start gap-2 p-2 border rounded cursor-pointer hover:bg-steel-50">
                    <input
                      type="radio"
                      name="periodOption"
                      value="single"
                      checked={periodOption === 'single'}
                      onChange={(e) => setPeriodOption(e.target.value)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium">Single night</div>
                      <div className="text-xs text-slate-500">
                        Check-in: {dropDate && format(dropDate, 'MMM d, yyyy')} / Check-out: {singleDayEnd && format(singleDayEnd, 'MMM d, yyyy')}
                      </div>
                    </div>
                  </label>
                )}

                {/* Option: Custom period - show when needed (uncovered period, moving, full period blocked, or rental warning) */}
                {canUseCustomPeriod && (
                  <label className={`flex items-start gap-2 p-2 border rounded cursor-pointer hover:bg-steel-50 ${
                    isFullPeriodBlocked || hasRentalWarning ? 'border-brand-300 bg-brand-50' : ''
                  }`}>
                    <input
                      type="radio"
                      name="periodOption"
                      value="custom"
                      checked={periodOption === 'custom'}
                      onChange={(e) => setPeriodOption(e.target.value)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium">
                        Custom period
                        {(isFullPeriodBlocked || hasRentalWarning) && <span className="text-brand-600 ml-1">(recommended)</span>}
                      </div>
                      <div className="text-xs text-slate-500">
                        {isFullPeriodBlocked ? (
                          <>Max available: {customMinDate && format(new Date(customMinDate), 'MMM d')} - {customMaxDate && format(new Date(customMaxDate), 'MMM d, yyyy')}</>
                        ) : hasRentalWarning && rentalAvailability.availableRange ? (
                          <>Rented period: {customMinDate && format(new Date(customMinDate), 'MMM d')} - {customMaxDate && format(new Date(customMaxDate), 'MMM d, yyyy')}</>
                        ) : (
                          <>Select dates within: {customMinDate && format(new Date(customMinDate), 'MMM d')} - {customMaxDate && format(new Date(customMaxDate), 'MMM d, yyyy')}</>
                        )}
                      </div>
                    </div>
                  </label>
                )}

                {/* Custom date inputs */}
                {periodOption === 'custom' && canUseCustomPeriod && (
                  <div className="ml-6 mt-2 p-2 bg-steel-50 rounded space-y-2">
                    <div className="flex gap-2 items-center">
                      <label className="text-xs text-slate-600 w-16">From:</label>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        min={customMinDate}
                        max={customMaxDate}
                        className="flex-1 px-2 py-1 border rounded text-sm"
                      />
                    </div>
                    <div className="flex gap-2 items-center">
                      <label className="text-xs text-slate-600 w-16">To:</label>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        min={customMinDate}
                        max={customMaxDate}
                        className="flex-1 px-2 py-1 border rounded text-sm"
                      />
                    </div>
                    <div className="text-xs text-slate-400">
                      Must be within: {customMinDate && format(new Date(customMinDate), 'MMM d')} - {customMaxDate && format(new Date(customMaxDate), 'MMM d, yyyy')}
                    </div>
                  </div>
                )}
              </div>

              {assignmentData?.hasConflict && (
                <p className="text-sm text-red-600 mt-2">
                  This will remove the existing accommodation assignment
                </p>
              )}
              {assignmentData?.isRoomNotRented && (
                <div className="text-sm text-orange-600 mt-2 p-2 bg-orange-50 rounded border border-orange-200">
                  <strong>Warning:</strong> You are planning accommodation in a room that is not yet rented for this period.
                  Please ensure the room is booked before the assignment date.
                </div>
              )}
            </div>

            {/* Room selection - only show if accommodation has rooms and room not pre-selected */}
            {needsRoomSelection && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Select Room: <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedRoomNumber || ''}
                  onChange={(e) => setSelectedRoomNumber(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border rounded"
                  required
                >
                  <option value="">-- Choose a room --</option>
                  {Array.from({ length: numberOfRooms }, (_, i) => i + 1).map(roomNum => {
                    // Check if room is occupied on the start date (exclude the assignment being moved)
                    const isOccupied = assignmentData?.startDate ? assignments.some(a => {
                      if (a.accommodation !== assignmentData.accommodationId) return false;
                      if (a.room_number !== roomNum) return false;
                      // In edit mode, exclude the assignment being moved
                      if (isEditMode && existingAssignment && a.id === existingAssignment.id) return false;

                      const startDateOnly = new Date(assignmentData.startDate.getFullYear(), assignmentData.startDate.getMonth(), assignmentData.startDate.getDate());
                      const assignmentStart = new Date(a.start_date);
                      const assignmentEnd = new Date(a.end_date);
                      const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
                      const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());

                      return startDateOnly >= assignmentStartOnly && startDateOnly <= assignmentEndOnly;
                    }) : false;

                    return (
                      <option key={roomNum} value={roomNum} disabled={isOccupied}>
                        Room {roomNum} {isOccupied ? '(Occupied)' : ''}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={() => {
                setShowAssignmentModal(false);
                setAssignmentData(null);
              }}
              className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 text-sm font-medium text-slate-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAssignment}
              className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 text-sm font-bold transition-colors shadow-lg shadow-brand-500/20"
            >
              {isEditMode ? 'Move' : 'Assign'}
            </button>
          </div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // CONFIRMATION MODAL - Custom styled confirmation dialog
  // ============================================================================
  const ConfirmationModal = () => {
    if (!confirmationModal) return null;

    const {
      title = 'Confirm Action',
      message,
      onConfirm,
      confirmText = 'Confirm',
      cancelText = 'Cancel',
      confirmButtonClass = 'bg-brand-500 hover:bg-brand-600',
      icon = 'info' // 'info', 'warning', 'error'
    } = confirmationModal;

    const iconColors = {
      info: 'text-brand-500 bg-brand-100',
      warning: 'text-orange-500 bg-orange-100',
      error: 'text-red-500 bg-red-100'
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
        <div className="bg-white rounded-xl w-[420px] shadow-2xl border border-slate-200">
          <div className="p-6">
          <div className="flex items-start gap-4">
            {/* Icon */}
            <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${iconColors[icon]}`}>
              {icon === 'warning' && (
                <AlertTriangle className="w-5 h-5" />
              )}
              {icon === 'info' && (
                <Home className="w-5 h-5" />
              )}
              {icon === 'error' && (
                <X className="w-5 h-5" />
              )}
            </div>

            {/* Content */}
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {title}
              </h3>
              <div className="text-sm text-slate-600">
                {message}
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={() => setConfirmationModal(null)}
              className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 text-sm font-medium text-slate-700 transition-colors"
            >
              {cancelText}
            </button>
            <button
              type="button"
              onClick={() => {
                if (onConfirm) onConfirm();
                setConfirmationModal(null);
              }}
              className={`px-4 py-2 text-white rounded-lg text-sm font-bold transition-colors shadow-lg ${confirmButtonClass}`}
            >
              {confirmText}
            </button>
          </div>
          </div>
        </div>
      </div>
    );
  };

  const ReservationModal = () => {
    // Initialize dates: startDate from drop, endDate = next day
    const initialStartDate = reservationModalData?.startDate
      ? format(reservationModalData.startDate, 'yyyy-MM-dd')
      : '';
    const initialEndDate = reservationModalData?.startDate
      ? format(new Date(new Date(reservationModalData.startDate).setDate(reservationModalData.startDate.getDate() + 1)), 'yyyy-MM-dd')
      : '';

    const [selectedUserId, setSelectedUserId] = useState('');
    const [reservationName, setReservationName] = useState('');
    const [startDate, setStartDate] = useState(initialStartDate);
    const [endDate, setEndDate] = useState(initialEndDate);
    const [validationErrors, setValidationErrors] = useState([]);

    // Validate the reservation period whenever dates or user changes
    const validateReservation = useCallback((checkStartDate, checkEndDate, userId) => {
      const errors = [];

      if (!checkStartDate || !checkEndDate) {
        return errors;
      }

      const start = new Date(checkStartDate);
      const end = new Date(checkEndDate);

      // Basic date validation
      if (end < start) {
        errors.push({ type: 'error', message: 'End date cannot be before start date' });
        return errors;
      }

      const accommodationId = reservationModalData?.accommodationId;
      const roomNumber = reservationModalData?.roomNumber;

      // Check 2: Room must not be occupied by another assignment
      const roomAvailable = isRoomAvailableForPeriod(accommodationId, roomNumber, start, end, null);
      if (!roomAvailable) {
        const availability = findMaxAvailablePeriod(accommodationId, roomNumber, start, end, null);
        const blockerName = availability.blockedBy?.user_details
          ? `${availability.blockedBy.user_details.first_name || ''} ${availability.blockedBy.user_details.last_name || ''}`.trim()
          : 'another assignment';
        errors.push({
          type: 'error',
          message: `Room is occupied by ${blockerName} (${format(new Date(availability.blockedBy?.start_date), 'MMM d')} - ${format(new Date(availability.blockedBy?.end_date), 'MMM d')})`
        });
      }

      // Check 3: User must not have another accommodation assignment in this period (only if user selected)
      if (userId) {
        // Convert userId to number for proper comparison (select value is string)
        const userIdNum = parseInt(userId, 10);
        const userConflict = checkUserAccommodationConflict(userIdNum, start, end, null);
        if (userConflict.hasConflict) {
          const conflictAccommodation = accommodations.find(a => a.id === userConflict.conflictingAssignment?.accommodation);
          errors.push({
            type: 'error',
            message: `User already has accommodation at ${conflictAccommodation?.name || 'another location'} (${format(new Date(userConflict.conflictingAssignment?.start_date), 'MMM d')} - ${format(new Date(userConflict.conflictingAssignment?.end_date), 'MMM d')})`
          });
        }
      }

      // Check 4: Room rental availability (warning only)
      const rentalAvailability = checkRoomRentalAvailability(accommodationId, roomNumber, start, end);
      if (!rentalAvailability.isAvailable) {
        errors.push({
          type: 'warning',
          message: `Room is not rented for some days in this period`
        });
      }

      return errors;
    }, [reservationModalData, isRoomAvailableForPeriod, findMaxAvailablePeriod, checkUserAccommodationConflict, checkRoomRentalAvailability, accommodations]);

    // Run validation when dates or user changes
    useEffect(() => {
      const errors = validateReservation(startDate, endDate, selectedUserId);
      setValidationErrors(errors);
    }, [startDate, endDate, selectedUserId, validateReservation]);

    const handleCreateReservation = () => {
      if (!selectedUserId) {
        setValidationErrors([{ type: 'error', message: 'Please select a user for this reservation' }]);
        return;
      }

      // Check for blocking errors (not warnings)
      const blockingErrors = validationErrors.filter(e => e.type === 'error');
      if (blockingErrors.length > 0) {
        return; // Don't allow creation with errors
      }

      // Find room ID from accommodation and room number
      const roomId = findRoomId(reservationModalData.accommodationId, reservationModalData.roomNumber);
      if (!roomId) {
        setValidationErrors([{ type: 'error', message: 'Could not find room. Please try again.' }]);
        return;
      }

      const assignmentData = {
        accommodation: reservationModalData.accommodationId,
        room: roomId,
        room_number: reservationModalData.roomNumber,
        user: selectedUserId,
        start_date: startDate,
        end_date: endDate,
        is_reservation: true,
        notes: reservationName || ''
      };

      console.log('Creating reservation locally:', assignmentData);

      // Add to pending creates for later save
      addPendingCreate(assignmentData);

      setShowReservationModal(false);
      setReservationModalData(null);
    };

    const hasBlockingErrors = validationErrors.some(e => e.type === 'error');

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl w-[420px] border border-slate-200 shadow-2xl">
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-purple-50 to-white rounded-t-xl">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-purple-600" />
              Create Reservation
            </h3>
          </div>

          <div className="p-6 space-y-4">
            {/* Accommodation & Room info */}
            <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
              <p className="text-sm text-slate-600">
                Accommodation: <strong className="text-slate-900">{reservationModalData?.accommodationName}</strong>
              </p>
              <p className="text-sm text-slate-600">
                Room: <strong className="text-slate-900">{reservationModalData?.roomNumber}</strong>
              </p>
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                Reservation Period:
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
                <span className="text-slate-400 text-sm">to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
            </div>

            {/* User Selection */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                Assign to User: <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              >
                <option value="">-- Select user --</option>
                {projectAssignments.map((userAssignment) => (
                  <option key={userAssignment.user_data.id} value={userAssignment.user_data.id}>
                    {userAssignment.user_data.first_name && userAssignment.user_data.last_name
                      ? `${userAssignment.user_data.first_name} ${userAssignment.user_data.last_name}`
                      : userAssignment.user_data.username || userAssignment.user_data.email}
                  </option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                Notes: <span className="text-slate-400">(optional)</span>
              </label>
              <input
                type="text"
                value={reservationName}
                onChange={(e) => setReservationName(e.target.value)}
                placeholder="e.g., Reserved for specific purpose"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>

            {/* Validation Errors/Warnings */}
            {validationErrors.length > 0 && (
              <div className="space-y-2">
                {validationErrors.map((error, idx) => (
                  <div
                    key={idx}
                    className={`p-2 rounded-lg text-sm flex items-start gap-2 ${
                      error.type === 'error'
                        ? 'bg-red-50 text-red-700 border border-red-200'
                        : 'bg-orange-50 text-orange-700 border border-orange-200'
                    }`}
                  >
                    {error.type === 'error' ? (
                      <X className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    )}
                    <span>{error.message}</span>
                  </div>
                ))}
              </div>
            )}

          <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={() => {
                setShowReservationModal(false);
                setReservationModalData(null);
              }}
              className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 text-sm font-medium text-slate-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreateReservation}
              disabled={hasBlockingErrors || !selectedUserId}
              className={`px-4 py-2 text-white rounded-lg text-sm font-bold transition-colors ${
                hasBlockingErrors || !selectedUserId
                  ? 'bg-slate-400 cursor-not-allowed'
                  : 'bg-purple-600 hover:bg-purple-700 shadow-lg shadow-purple-500/20'
              }`}
            >
              Create Reservation
            </button>
          </div>
          </div>
        </div>
      </div>
    );
  };

  const AccommodationDetailsModal = () => {
    const accommodation = selectedAccommodationDetails;
    if (!accommodation) return null;
    
    const getOpenStreetMapUrl = () => {
      if (!accommodation.address && !accommodation.city) return null;

      // If we have coordinates, use them directly
      if (accommodation.latitude && accommodation.longitude &&
          !isNaN(parseFloat(accommodation.latitude)) && !isNaN(parseFloat(accommodation.longitude))) {
        return `https://www.openstreetmap.org/?mlat=${accommodation.latitude}&mlon=${accommodation.longitude}&zoom=17`;
      }

      // Otherwise, search by address
      const searchQuery = [
        accommodation.address,
        accommodation.city,
        accommodation.country
      ].filter(Boolean).join(', ');

      return `https://www.openstreetmap.org/search?query=${encodeURIComponent(searchQuery)}`;
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl w-96 max-w-md border border-slate-200 shadow-2xl">
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-brand-50 to-white rounded-t-xl flex justify-between items-center">
            <h3 className="text-lg font-bold text-slate-900">{accommodation.name}</h3>
            <button
              onClick={() => {
                setShowAccommodationModal(false);
                setSelectedAccommodationDetails(null);
              }}
              className="text-slate-400 hover:text-slate-600 text-xl transition-colors"
            >
              ×
            </button>
          </div>
          <div className="p-6">
          <div className="space-y-3">
            {/* Capacity */}
            <div className="flex items-center space-x-2">
              <Users className="w-4 h-4 text-slate-500" />
              <span className="text-sm">
                <strong>Capacity:</strong> {accommodation.capacity || 'Not specified'} beds
              </span>
            </div>

            {/* Contact Phone */}
            {accommodation.contact_phone && (
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4 text-slate-500" />
                <span className="text-sm">
                  <strong>Contact:</strong> {accommodation.contact_phone}
                </span>
              </div>
            )}
            
            {/* Address */}
            {(accommodation.address || accommodation.city) && (
              <div className="flex items-start space-x-2">
                <MapPin className="w-4 h-4 text-slate-500 mt-0.5" />
                <div className="text-sm">
                  <strong>Address:</strong>
                  <div className="text-slate-700">
                    {accommodation.address && <div>{accommodation.address}</div>}
                    {accommodation.city && (
                      <div>
                        {accommodation.city}
                        {accommodation.post_code && ` ${accommodation.post_code}`}
                        {accommodation.country && `, ${accommodation.country}`}
                      </div>
                    )}
                  </div>
                  {getOpenStreetMapUrl() && (
                    <a
                      href={getOpenStreetMapUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center mt-1 text-brand-600 hover:text-brand-800 text-xs underline"
                    >
                      <MapPin className="w-3 h-3 mr-1" />
                      View on Map
                    </a>
                  )}
                </div>
              </div>
            )}
            
            {/* Price */}
            {accommodation.rental_price && (
              <div className="flex items-center space-x-2">
                <Euro className="w-4 h-4 text-slate-500" />
                <span className="text-sm">
                  <strong>Price:</strong> €{accommodation.rental_price}/day/person
                </span>
              </div>
            )}
            
            {/* Rental Period */}
            {accommodation.rental_start_date && accommodation.rental_end_date && (
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-slate-500" />
                <span className="text-sm">
                  <strong>Rental Period:</strong> {format(new Date(accommodation.rental_start_date), 'MMM d, yyyy')} - {format(new Date(accommodation.rental_end_date), 'MMM d, yyyy')}
                </span>
              </div>
            )}

            {/* Notes */}
            {accommodation.notes && (
              <div className="flex items-start space-x-2">
                <ClipboardList className="w-4 h-4 text-slate-500 mt-0.5" />
                <div className="text-sm">
                  <strong>Notes:</strong>
                  <p className="text-slate-700 whitespace-pre-wrap">{accommodation.notes}</p>
                </div>
              </div>
            )}
            
            {/* Current occupancy info */}
            <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <div className="text-sm font-bold text-slate-700 mb-2">Current Occupancy</div>
              <div className="text-xs text-slate-600">
                {accommodation.current_assignments?.length || 0} of {accommodation.capacity || 0} beds occupied
              </div>
              {accommodation.current_assignments && accommodation.current_assignments.length > 0 && (
                <div className="mt-2">
                  <div className="text-xs font-medium text-slate-700 mb-1">Current guests:</div>
                  <div className="space-y-1">
                    {accommodation.current_assignments.map((assignment, index) => (
                      <div key={assignment.id} className="text-xs text-slate-600">
                        • {assignment.user_details ?
                            (assignment.user_details.first_name && assignment.user_details.last_name ?
                              `${assignment.user_details.first_name} ${assignment.user_details.last_name}` :
                              assignment.user_details.username || assignment.user_details.email || 'User') :
                            'User'} ({format(new Date(assignment.start_date), 'MMM d')} - {format(new Date(assignment.end_date), 'MMM d')})
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          </div>

          <div className="flex justify-end px-6 py-4 border-t border-slate-200">
            <button
              onClick={() => {
                setShowAccommodationModal(false);
                setSelectedAccommodationDetails(null);
              }}
              className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 text-sm font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const DayDetailsModal = () => {
    if (!selectedDay) return null;

    const peopleForDay = getPeopleForDay(selectedDay);

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl w-96 max-w-md max-h-[80vh] border border-slate-200 shadow-2xl flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-brand-50 to-white rounded-t-xl flex justify-between items-center flex-shrink-0">
            <h3 className="text-lg font-bold text-slate-900">
              People for {format(selectedDay, 'MMM d, yyyy')}
            </h3>
            <button
              onClick={() => {
                setShowDayDetailsModal(false);
                setSelectedDay(null);
              }}
              className="text-slate-400 hover:text-slate-600 text-xl transition-colors"
            >
              ×
            </button>
          </div>

          <div className="p-6 overflow-y-auto flex-1">
          <div className="space-y-3">
            {peopleForDay.length === 0 ? (
              <p className="text-slate-500 text-center">No people need accommodation on this day</p>
            ) : (
              peopleForDay.map((person, index) => (
                <div key={index} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                  <div className="font-bold text-sm text-slate-900">
                    {person.user_data.first_name} {person.user_data.last_name}
                  </div>
                  <div className="text-sm text-slate-600 mt-1">
                    {person.reasonForDay?.type === 'project' ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-brand-100 text-brand-800 font-medium">
                        📋 Project: {person.reasonForDay.role}
                      </span>
                    ) : person.reasonForDay?.type === 'travel' ? (
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        person.reasonForDay.travel_type === 'check_in'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-orange-100 text-orange-800'
                      }`}>
                        ✈️ {person.reasonForDay.travel_type === 'check_in' ? 'Check-in' : 'Check-out'} Travel
                      </span>
                    ) : (
                      <span className="text-slate-500">Unknown reason</span>
                    )}
                  </div>

                  {/* Show current accommodation if any */}
                  {(() => {
                    const currentAssignment = assignments.find(assignment => {
                      const assignmentDate = new Date(assignment.start_date);
                      const endAssignmentDate = new Date(assignment.end_date);
                      const currentDateOnly = new Date(selectedDay.getFullYear(), selectedDay.getMonth(), selectedDay.getDate());
                      const assignmentStartOnly = new Date(assignmentDate.getFullYear(), assignmentDate.getMonth(), assignmentDate.getDate());
                      const assignmentEndOnly = new Date(endAssignmentDate.getFullYear(), endAssignmentDate.getMonth(), endAssignmentDate.getDate());

                      return assignment.user === person.user_data.id &&
                             currentDateOnly >= assignmentStartOnly &&
                             currentDateOnly <= assignmentEndOnly;
                    });

                    if (currentAssignment) {
                      const accommodation = accommodations.find(acc => acc.id === currentAssignment.accommodation);
                      return (
                        <div className="text-xs text-emerald-600 mt-1 font-medium">
                          🏠 Assigned to: {accommodation?.name || 'Unknown accommodation'}
                        </div>
                      );
                    } else {
                      return (
                        <div className="text-xs text-red-600 mt-1 font-medium">
                          ⚠️ Not assigned to accommodation
                        </div>
                      );
                    }
                  })()}
                </div>
              ))
            )}
          </div>
          </div>

          <div className="flex justify-end px-6 py-4 border-t border-slate-200 flex-shrink-0">
            <button
              onClick={() => {
                setShowDayDetailsModal(false);
                setSelectedDay(null);
              }}
              className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 text-sm font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const AccommodationRow = ({ accommodation, index }) => {
    const ref = useRef(null);
    const [, drop] = useDrop({
      accept: 'ACCOMMODATION_ROW',
      hover(item, monitor) {
        if (!ref.current) {
          return;
        }
        const dragIndex = item.index;
        const hoverIndex = index;
        if (dragIndex === hoverIndex) {
          return;
        }
        moveAccommodation(dragIndex, hoverIndex);
        item.index = hoverIndex;
      },
    });

    const [{ isDragging }, drag] = useDrag({
      type: 'ACCOMMODATION_ROW',
      item: () => ({ id: accommodation.id, index }),
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    const dragRef = useRef(null);
    drag(dragRef);
    drop(ref);

    // Get all assignments for this accommodation
    const accommodationAssignments = assignments.filter(a => a.accommodation === accommodation.id);
    const numberOfRooms = accommodation.capacity || 0;

    const rowHeight = getAccommodationRowHeight(accommodation.capacity);

    return (
      <tr key={accommodation.id} ref={ref} style={{ opacity: isDragging ? 0.5 : 1, height: `${rowHeight}px`, position: 'relative' }}>
        <td
          className="border p-2 bg-steel-50 relative cursor-pointer hover:bg-steel-100 sticky left-0 z-20"
          style={{ width: '200px', minWidth: '200px', height: `${rowHeight}px`, boxShadow: '2px 0 4px rgba(0,0,0,0.1)' }}
          title={`${accommodation.name}\r\n${accommodation.address ? `📍 ${accommodation.address}` : ''}\r\nDouble-click for details`}
          onDoubleClick={() => {
            setSelectedAccommodationDetails(accommodation);
            setShowAccommodationModal(true);
          }}
        >
          <div className="flex justify-between items-start h-full">
            <div className="flex items-center h-full">
              <div ref={dragRef} className="cursor-move p-1 h-full flex items-center">
                <GripVertical className="text-slate-400" />
              </div>
              <div className="flex-1 ml-1">
                <div className="font-semibold truncate">{accommodation.name}</div>
                <div className="text-xs text-slate-500 truncate">{accommodation.address}</div>
                <div className="text-xs text-slate-600">
                  <Users className="inline w-3 h-3" /> {accommodation.capacity} beds
                </div>
              </div>
            </div>
            <button
              onClick={() => setEditingAccommodation(accommodation)}
              className="ml-2 p-1 text-slate-400 hover:text-slate-600 text-xs"
              title="Edit accommodation"
            >
              ✏️
            </button>
          </div>
        </td>
        {days.map((day, dayIndex) => (
          <AccommodationCell
            key={day.toISOString()}
            accommodation={accommodation}
            date={day}
            dayIndex={dayIndex}
            totalDays={days.length}
            assignments={accommodationAssignments}
            numberOfRooms={numberOfRooms}
            dragState={dragState}
            dragTargetPosition={dragTargetPosition}
            resizeTargetPosition={resizeTargetPosition}
          />
        ))}
      </tr>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-brand-500 mx-auto mb-4 animate-spin" />
          <p className="text-slate-500">Loading accommodation data...</p>
        </div>
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="h-full flex flex-col p-1 overflow-hidden">
        {/* Header - Fixed Height */}
        <div className="flex-shrink-0 bg-white rounded-xl shadow-sm border border-steel-200 p-4 mb-1">
          <div className="flex flex-wrap items-center gap-3">
            {/* Title and Project Select - Left Side */}
            {selectedProject ? (
              <div className="flex items-center space-x-3">
                <h2 className="text-3xl font-bold text-slate-800">Accommodation - project</h2>
                <select
                  className="px-4 py-2 border border-steel-300 rounded-xl bg-white focus:ring-2 focus:ring-brand-500 focus:border-transparent shadow-sm min-w-[200px]"
                  value={selectedProject || ''}
                  onChange={(e) => setSelectedProject(e.target.value)}
                >
                  <option value="">All Projects</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <>
                <h2 className="text-3xl font-bold text-slate-800">Accommodation Calendar</h2>
                <select
                  className="px-4 py-2 border border-steel-300 rounded-xl bg-white focus:ring-2 focus:ring-brand-500 focus:border-transparent shadow-sm min-w-[200px]"
                  value={selectedProject || ''}
                  onChange={(e) => setSelectedProject(e.target.value)}
                >
                  <option value="">All Projects</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </>
            )}

            {/* Spacer to push buttons to the right */}
            <div className="flex-1"></div>

            {/* Action Buttons - Right Side */}

            {/* Last Save Info - Only when project selected */}
            {selectedProject && lastSavedAt && (
              <button
                onClick={() => setShowSaveHistoryModal(true)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border transition-colors bg-emerald-50 border-emerald-200 hover:bg-emerald-100 cursor-pointer"
              >
                <Clock className="h-3.5 w-3.5 text-emerald-600" />
                <span className="text-xs text-slate-600">
                  <span className="font-semibold">Saved:</span> {formatDateTime(lastSavedAt)}
                  {lastSaveInfo && <span className="ml-1 text-slate-500">({lastSaveInfo.user})</span>}
                </span>
              </button>
            )}

            {/* Refresh Button - Only when project selected */}
            {selectedProject && (
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className={`px-2.5 py-1.5 rounded-lg border transition-colors flex items-center gap-1.5 ${
                  isRefreshing
                    ? 'bg-slate-100 border-slate-300 cursor-not-allowed'
                    : 'bg-white border-slate-300 hover:bg-brand-50 hover:border-brand-300'
                }`}
                title="Refresh data from server"
              >
                <RefreshCw className={`h-3.5 w-3.5 text-slate-600 ${isRefreshing ? 'animate-spin text-brand-500' : ''}`} />
                <span className="text-xs font-medium text-slate-700">Refresh</span>
              </button>
            )}

            {/* Reservation Card - Only when project selected */}
            {selectedProject && (
              <div className="flex-shrink-0">
                <ReservationCard />
              </div>
            )}

            {/* Map Button - Only when project selected */}
            {selectedProject && (
              <button
                onClick={() => setShowMapModal(true)}
                className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center transition-colors text-xs font-bold"
                title="Show all accommodations on map"
              >
                <MapIcon className="w-3.5 h-3.5 mr-1.5" />
                Map
              </button>
            )}

            {/* Add Accommodation Button - Only when project selected */}
            {selectedProject && (
              <button
                onClick={() => setShowAddAccommodation(true)}
                className="px-3 py-1.5 bg-brand-500 text-white rounded-lg hover:bg-brand-600 text-xs font-bold flex items-center transition-colors shadow-lg shadow-brand-500/20"
              >
                <Home className="w-3.5 h-3.5 mr-1.5" />
                Add
              </button>
            )}
          </div>
        </div>

        {/* Main Content Area - Takes Remaining Height */}
        <div className="flex-1 overflow-hidden">
          {!selectedProject ? (
            <AccommodationSummary
              projects={projects}
              customDateRange={customDateRange}
              onProjectSelect={(projectId) => setSelectedProject(projectId)}
              onDateRangeChange={(direction) => {
                const daysToMove = direction === 'prev' ? -7 : 7;
                const newStart = new Date(customDateRange.start);
                newStart.setDate(newStart.getDate() + daysToMove);
                const newEnd = new Date(customDateRange.end);
                newEnd.setDate(newEnd.getDate() + daysToMove);
                setCustomDateRange({ start: newStart, end: newEnd });
              }}
              onOpenDateRangeModal={() => {
                setTempDateRange({ start: customDateRange.start, end: customDateRange.end });
                setShowDateRangeModal(true);
              }}
            />
          ) : (
            <div className="flex h-full">
              {/* Left Panel - Tabbed View - Fixed Width, Scrollable */}
              <div className="w-56 pr-1 flex-shrink-0">
                <div className="border border-steel-200 rounded-xl bg-white shadow-sm flex flex-col h-full">
                {/* Tabs Header - Sticky */}
                <div className="sticky top-0 bg-white border-b border-steel-200 rounded-t-lg z-10">
                  <div className="flex">
                    <button
                      onClick={() => setActiveTab('team')}
                      className={`flex-1 py-2 px-2 text-xs font-medium transition-colors ${
                        activeTab === 'team'
                          ? 'text-brand-700 border-b-2 border-brand-700 bg-brand-50'
                          : 'text-slate-600 hover:text-brand-600 hover:bg-steel-50'
                      }`}
                    >
                      <div className="flex flex-col items-center">
                        <div>👥 Project Team</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {format(startDate, 'MMM d')} - {format(endDate, 'MMM d, yyyy')}
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Tab Content - Scrollable */}
                <div className="flex-1 overflow-y-auto">
                  {/* Project Team Tab */}
                  {activeTab === 'team' && (
                    <div className="p-1.5">
                      {filteredDay && (
                        <div className="mb-2">
                          <div className="flex items-center justify-between mb-1">
                            <div className="text-xs text-brand-600 font-medium">
                              {format(filteredDay, 'MMM d')}
                            </div>
                            <button
                              onClick={() => setFilteredDay(null)}
                              className="text-xs px-1.5 py-0.5 bg-brand-100 hover:bg-brand-200 text-brand-700 rounded transition-colors"
                              title="Clear filter"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      )}
                      <div className="flex flex-col gap-1">
                        {(() => {
                          // Helper function to calculate accommodation coverage percentage
                          const calculateCoveragePercentage = (userWithAssignments) => {
                            const user = userWithAssignments.user_data;
                            const allAssignments = userWithAssignments.assignments;
                            const travelDays = userWithAssignments.travelDays || [];

                            // Calculate days that need accommodation (project days + travel days with needsAccommodation)
                            const daysNeedingAccommodation = new Set();

                            // Add project assignment days
                            if (allAssignments && allAssignments.length > 0) {
                              allAssignments.forEach(assignment => {
                                const assignStart = new Date(assignment.start_date);
                                const assignEnd = new Date(assignment.end_date);

                                let currentDay = new Date(assignStart.getFullYear(), assignStart.getMonth(), assignStart.getDate());
                                const endDay = new Date(assignEnd.getFullYear(), assignEnd.getMonth(), assignEnd.getDate());

                                while (currentDay <= endDay) {
                                  daysNeedingAccommodation.add(currentDay.getTime());
                                  currentDay.setDate(currentDay.getDate() + 1);
                                }
                              });
                            }

                            // Add travel days that need accommodation
                            travelDays.forEach(travel => {
                              if (travel.needsAccommodation) {
                                const travelDate = new Date(travel.travel_date);
                                const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
                                daysNeedingAccommodation.add(travelDateOnly.getTime());
                              }
                            });

                            if (daysNeedingAccommodation.size === 0) return 0;

                            // Count covered days
                            let coveredDays = 0;

                            daysNeedingAccommodation.forEach(dayTime => {
                              const dayOnly = new Date(dayTime);

                              // Check if user has accommodation on this day
                              const hasAccommodation = assignments.some(a => {
                                if (a.user !== user.id && a.user_id !== user.id) return false;

                                const assignStart = new Date(a.start_date);
                                const assignEnd = new Date(a.end_date);
                                const assignStartOnly = new Date(assignStart.getFullYear(), assignStart.getMonth(), assignStart.getDate());
                                const assignEndOnly = new Date(assignEnd.getFullYear(), assignEnd.getMonth(), assignEnd.getDate());

                                return dayOnly >= assignStartOnly && dayOnly <= assignEndOnly;
                              });

                              if (hasAccommodation) {
                                coveredDays++;
                              }
                            });

                            const totalDays = daysNeedingAccommodation.size;
                            return totalDays > 0 ? Math.round((coveredDays / totalDays) * 100) : 0;
                          };

                          // Helper function to check if user has accommodation overflow
                          // (accommodation extends beyond project assignment dates)
                          const hasAccommodationOverflow = (userWithAssignments) => {
                            const userId = userWithAssignments.user_data?.id;
                            if (!userId) return false;

                            // Calculate rotation start/end for this user
                            const allUserAssignments = userWithAssignments.assignments || [];
                            const travelDays = userWithAssignments.travelDays || [];

                            if (allUserAssignments.length === 0) return false;

                            let rotationStart = new Date(Math.min(...allUserAssignments.map(a => new Date(a.start_date))));
                            let rotationEnd = new Date(Math.max(...allUserAssignments.map(a => new Date(a.end_date))));

                            // Include travel days
                            if (travelDays.length > 0) {
                              const travelDates = travelDays.map(t => new Date(t.travel_date));
                              const earliestTravel = new Date(Math.min(...travelDates));
                              const latestTravel = new Date(Math.max(...travelDates));
                              if (earliestTravel < rotationStart) rotationStart = earliestTravel;
                              if (latestTravel > rotationEnd) rotationEnd = latestTravel;
                            }

                            const rotationStartOnly = new Date(rotationStart.getFullYear(), rotationStart.getMonth(), rotationStart.getDate());
                            const rotationEndOnly = new Date(rotationEnd.getFullYear(), rotationEnd.getMonth(), rotationEnd.getDate());

                            // Get relevant accommodation assignments for this user that overlap with rotation
                            const relevantAccommodationAssignments = assignments.filter(a => {
                              if (a.user !== userId && a.user_id !== userId) return false;
                              const accStart = new Date(a.start_date);
                              const accEnd = new Date(a.end_date);
                              const accStartOnly = new Date(accStart.getFullYear(), accStart.getMonth(), accStart.getDate());
                              const accEndOnly = new Date(accEnd.getFullYear(), accEnd.getMonth(), accEnd.getDate());
                              return accStartOnly <= rotationEndOnly && accEndOnly >= rotationStartOnly;
                            });

                            // Check if any extends beyond rotation
                            // Skip reservations - they don't need project assignment coverage
                            return relevantAccommodationAssignments.some(a => {
                              // Reservations are exempt from project assignment validation
                              if (a.is_reservation) return false;

                              const accStart = new Date(a.start_date);
                              const accEnd = new Date(a.end_date);
                              const accStartOnly = new Date(accStart.getFullYear(), accStart.getMonth(), accStart.getDate());
                              const accEndOnly = new Date(accEnd.getFullYear(), accEnd.getMonth(), accEnd.getDate());
                              return accStartOnly < rotationStartOnly || accEndOnly > rotationEndOnly;
                            });
                          };

                          // Sort users:
                          // 1. Users with accommodation overflow (problems) - FIRST
                          // 2. Then by coverage percentage (ascending - lowest coverage first)
                          const usersToDisplay = filteredDay ? getPeopleForDay(filteredDay) : projectAssignments;
                          const sortedUsers = [...usersToDisplay].sort((a, b) => {
                            const aHasOverflow = hasAccommodationOverflow(a);
                            const bHasOverflow = hasAccommodationOverflow(b);
                            const aCoverage = calculateCoveragePercentage(a);
                            const bCoverage = calculateCoveragePercentage(b);

                            // Debug logging
                            console.log('🔄 Sort comparison:', {
                              aUser: a.user_data?.first_name + ' ' + a.user_data?.last_name,
                              bUser: b.user_data?.first_name + ' ' + b.user_data?.last_name,
                              aHasOverflow,
                              bHasOverflow,
                              aCoverage,
                              bCoverage
                            });

                            // Users with overflow should be at the top
                            if (aHasOverflow && !bHasOverflow) return -1;
                            if (!aHasOverflow && bHasOverflow) return 1;

                            // If both have or both don't have overflow, sort by coverage
                            // Sort ascending - users with lower coverage appear first
                            return aCoverage - bCoverage;
                          });

                          return sortedUsers.map((userWithAssignments, index) => (
                            <UserCard
                              key={`user-${index}`}
                              user={userWithAssignments.user_data}
                              projectAssignment={userWithAssignments}
                              allAssignments={userWithAssignments.assignments}
                              travelDays={userWithAssignments.travelDays || []}
                              accommodationAssignments={assignments}
                            />
                          ));
                        })()}
                        {filteredDay ? (
                          getPeopleForDay(filteredDay).length === 0 && (
                            <p className="text-slate-500 text-sm">No team members need accommodation on {format(filteredDay, 'MMM d, yyyy')}</p>
                          )
                        ) : (
                          projectAssignments.length === 0 && (
                            <p className="text-slate-500 text-sm">No team members assigned</p>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

              {/* Calendar - Takes Remaining Width, Scrollable */}
              <div className="flex-1 flex flex-col h-full bg-white rounded-xl border border-steel-200 shadow-sm overflow-hidden">
              <div
                className="flex justify-between items-center bg-white z-20 py-1 px-2 border-b border-steel-200 shadow-sm flex-shrink-0"
              >
                <button
                  onClick={() => handleDateChange('prev')}
                  className="p-1 hover:bg-steel-100 rounded flex-shrink-0"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <h3 className="text-sm font-semibold whitespace-nowrap">
                  <button
                    onClick={() => {
                      setTempDateRange({ start: startDate, end: endDate });
                      setShowDateRangeModal(true);
                    }}
                    className="hover:bg-steel-100 px-2 py-0.5 rounded transition-colors cursor-pointer"
                    title="Click to change date range"
                  >
                    {format(startDate, 'MMM d')} - {format(endDate, 'MMM d, yyyy')}
                  </button>
                  {selectedProject && (
                    <span className="text-xs font-normal text-slate-600 ml-2">
                      ({projects.find(p => p.id == selectedProject)?.name || 'Project'})
                    </span>
                  )}
                </h3>
                <button
                  onClick={() => handleDateChange('next')}
                  className="p-1 hover:bg-steel-100 rounded flex-shrink-0"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              <div className="flex-1 overflow-auto relative" style={{ maxHeight: 'calc(100vh - 120px)' }}>
                <table className="border-collapse w-max" ref={timelineCallbackRef} style={{ tableLayout: 'fixed' }}>
                  <thead className="sticky top-0 z-20 bg-white">
                    {/* Week numbers row */}
                    <tr>
                      <th className="border bg-steel-200 sticky left-0 z-30" style={{ width: '200px', minWidth: '200px', boxShadow: '2px 0 4px rgba(0,0,0,0.1)' }}></th>
                      {weeks.map((week, weekIndex) => {
                        const weekNumber = getWeekNumber(week[0]);
                        return (
                          <th
                            key={weekIndex}
                            className="border bg-steel-200 text-xs font-medium text-slate-700 py-1 relative"
                            colSpan={week.length}
                            style={{
                              borderLeft: '1px solid #9ca3af',
                              borderRight: '1px solid #9ca3af'
                            }}
                          >
                            Week {weekNumber}
                          </th>
                        );
                      })}
                    </tr>
                    {/* Days row */}
                    <tr>
                      <th className="border p-2 bg-steel-100 sticky left-0 z-30" style={{ width: '200px', minWidth: '200px', boxShadow: '2px 0 4px rgba(0,0,0,0.1)' }}>Accommodation</th>
                      {days.map(day => (
                        <th
                          key={day.toISOString()}
                          className={`border p-1 text-xs ${
                            isToday(day) ? 'bg-brand-100' : (day.getDay() === 0 || day.getDay() === 6) ? 'bg-amber-100' : 'bg-steel-100'
                          }`}
                          style={{
                            width: getDayWidth() + 'px',
                            minWidth: getDayWidth() + 'px',
                            maxWidth: getDayWidth() + 'px',
                            boxSizing: 'border-box'
                          }}
                        >
                          <div>{format(day, 'd')}</div>
                          <div className="font-normal">{format(day, 'EEE')}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {accommodations.map((accommodation, index) => (
                      <AccommodationRow
                        key={accommodation.id}
                        index={index}
                        accommodation={accommodation}
                      />
                    ))}
                  </tbody>
                  {/* Summary row - sticky at bottom */}
                  <tfoot className="sticky bottom-0 z-20">
                    <tr style={{ height: '36px' }} className="bg-steel-100 border-t-2 border-steel-400">
                      <td className="border p-1 font-semibold text-xs bg-steel-100 sticky left-0 z-30" style={{ boxShadow: '2px 0 4px rgba(0,0,0,0.1)' }}>
                        Daily Summary
                      </td>
                      {days.map(day => {
                        const currentDateOnly = new Date(day.getFullYear(), day.getMonth(), day.getDate());

                        // Count total available beds across all accommodations for this day
                        let totalCapacity = 0;
                        let totalOccupied = 0;

                        accommodations.forEach(accommodation => {
                          // Check each room's availability using helper function
                          const rooms = accommodation.rooms || [];
                          rooms.forEach(room => {
                            if (isRoomAvailableOnDate(room, day)) {
                              totalCapacity += room.capacity || 1;
                            }
                          });

                          // Count occupied beds using half-day logic
                          // Check-out day only = 0 (room free tonight)
                          // Check-in day, middle day, or single-day = 1 (room occupied tonight)
                          const dayAssignments = assignments.filter(a => {
                            if (a.accommodation !== accommodation.id) return false;
                            const assignmentStart = new Date(a.start_date);
                            const assignmentEnd = new Date(a.end_date);
                            const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
                            const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());
                            return currentDateOnly >= assignmentStartOnly && currentDateOnly <= assignmentEndOnly;
                          });

                          // Apply half-day logic: count how many people sleep there TONIGHT
                          dayAssignments.forEach(assignment => {
                            const assignmentStart = new Date(assignment.start_date);
                            const assignmentEnd = new Date(assignment.end_date);
                            const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
                            const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());

                            const isCheckOutDay = currentDateOnly.getTime() === assignmentEndOnly.getTime();
                            const isCheckInDay = currentDateOnly.getTime() === assignmentStartOnly.getTime();
                            const isSingleDay = assignmentStartOnly.getTime() === assignmentEndOnly.getTime();

                            // Single day assignment = sleeps there tonight (1)
                            // Check-out day only (not check-in) = leaves in morning, room free tonight (0)
                            // Check-in day or middle day = sleeps there tonight (1)
                            if (isSingleDay) {
                              totalOccupied += 1;
                            } else if (isCheckOutDay && !isCheckInDay) {
                              totalOccupied += 0; // Check-out only - room free tonight
                            } else {
                              totalOccupied += 1; // Check-in or middle day - sleeps tonight
                            }
                          });
                        });
                        
                        // Count people who need accommodation TONIGHT (using half-day logic)
                        // The key insight: check-out travel EXTENDS the need for accommodation by 1 day
                        // If assignment ends on day 10 and check-out travel is on day 11,
                        // then day 10 is NOT a check-out day (user still sleeps there)
                        // But travel must be at most 1 day after assignment end to be related
                        const peopleNeedingAccommodation = projectAssignments.filter(userWithAssignments => {
                          // Find the user's check-out travel date (if any)
                          const checkOutTravel = userWithAssignments.travelDays && userWithAssignments.travelDays.find(
                            travel => travel.travel_type === 'check_out'
                          );
                          const checkOutTravelDate = checkOutTravel
                            ? new Date(new Date(checkOutTravel.travel_date).getFullYear(),
                                       new Date(checkOutTravel.travel_date).getMonth(),
                                       new Date(checkOutTravel.travel_date).getDate())
                            : null;

                          // Check if user has project assignment where they need to sleep TONIGHT
                          const needsAccommodationFromProject = userWithAssignments.assignments.some(assignment => {
                            const assignmentStart = new Date(assignment.start_date);
                            const assignmentEnd = new Date(assignment.end_date);
                            const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
                            const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());

                            // Check if current day is within assignment range
                            if (currentDateOnly < assignmentStartOnly || currentDateOnly > assignmentEndOnly) {
                              return false;
                            }

                            // Apply half-day logic
                            const isLastDayOfAssignment = currentDateOnly.getTime() === assignmentEndOnly.getTime();
                            const isFirstDayOfAssignment = currentDateOnly.getTime() === assignmentStartOnly.getTime();
                            const isSingleDay = assignmentStartOnly.getTime() === assignmentEndOnly.getTime();

                            // Check if check-out travel is exactly 1 day after assignment end (related to this assignment)
                            const dayAfterAssignment = new Date(assignmentEndOnly);
                            dayAfterAssignment.setDate(dayAfterAssignment.getDate() + 1);
                            const hasCheckOutTravelNextDay = checkOutTravelDate &&
                              checkOutTravelDate.getTime() === dayAfterAssignment.getTime();

                            // It's only actual check-out if it's the last day AND there's no check-out travel the next day
                            const isActualCheckOutDay = isLastDayOfAssignment && !hasCheckOutTravelNextDay;

                            // Single day without travel next day = leaving same day (no accommodation)
                            // Single day with travel next day = stays overnight
                            if (isSingleDay && !hasCheckOutTravelNextDay) return false;
                            if (isSingleDay && hasCheckOutTravelNextDay) return true;
                            if (isActualCheckOutDay && !isFirstDayOfAssignment) return false; // Leaving in morning
                            return true; // Check-in, middle day, or extended by travel
                          });

                          // Check if user has travel check-IN on this day (needs accommodation tonight)
                          // Check-in travel = arriving, needs to sleep tonight (1)
                          const hasCheckInTravelOnDay = userWithAssignments.travelDays && userWithAssignments.travelDays.some(travel => {
                            if (travel.travel_type !== 'check_in') return false;
                            const travelDate = new Date(travel.travel_date);
                            const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
                            return travelDateOnly.getTime() === currentDateOnly.getTime();
                          });

                          return needsAccommodationFromProject || hasCheckInTravelOnDay;
                        }).length;
                        
                        const availableBeds = totalCapacity - totalOccupied;
                        const unassignedPeople = peopleNeedingAccommodation - totalOccupied;
                        
                        return (
                          <td
                            key={day.toISOString()}
                            className={`border p-0.5 text-center cursor-pointer hover:bg-brand-100 transition-colors ${
                              peopleNeedingAccommodation > totalCapacity ? 'bg-red-300' :
                              (peopleNeedingAccommodation === totalCapacity && totalOccupied === peopleNeedingAccommodation) ? 'bg-green-100' :
                              'bg-yellow-100'
                            }`}
                            style={{ width: '40px', minWidth: '40px', maxWidth: '40px', boxSizing: 'border-box' }}
                            title={`Click to see people for ${format(day, 'MMM d, yyyy')}\r\n${peopleNeedingAccommodation > totalCapacity ? '⚠️ ' : ''}Total capacity: ${totalCapacity}\r\nOccupied: ${totalOccupied}\r\nAvailable: ${availableBeds}\r\n${peopleNeedingAccommodation > totalCapacity ? '⚠️ ' : ''}People needing accommodation: ${peopleNeedingAccommodation}\r\nUnassigned people: ${unassignedPeople > 0 ? unassignedPeople : 0}`}
                            onClick={() => handleDayClick(day)}
                          >
                            <div style={{ fontSize: '10px', lineHeight: '1.2' }}>
                              <div className={`font-bold ${totalOccupied >= peopleNeedingAccommodation ? 'text-green-600' : 'text-red-600'}`}>
                                {totalOccupied}/{peopleNeedingAccommodation}
                              </div>
                              <div className={`font-semibold ${availableBeds === 0 ? 'text-green-600' : 'text-orange-600'}`} style={{ fontSize: '9px' }}>
                                {availableBeds} free
                              </div>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Modal Components */}
        {selectedProject && showAddAccommodation && <AddAccommodationForm />}
        {selectedProject && editingAccommodation && <EditAccommodationForm />}
        {selectedProject && showAssignmentModal && <AssignmentModal />}
        {selectedProject && showReservationModal && <ReservationModal />}
        {selectedProject && showAccommodationModal && <AccommodationDetailsModal />}
        {selectedProject && showDayDetailsModal && <DayDetailsModal />}
        {confirmationModal && <ConfirmationModal />}

        {/* Accommodations Map Modal */}
        {showMapModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl w-full max-w-7xl mx-4 flex flex-col border border-slate-200 shadow-2xl" style={{ height: '95vh' }}>
              {/* Header */}
              <div className="flex justify-between items-center px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-white rounded-t-xl flex-shrink-0">
                <h2 className="text-xl font-bold text-slate-900">
                  Accommodations Map - {projects.find(p => p.id == selectedProject)?.name}
                </h2>
                <button
                  onClick={() => {
                    setShowMapModal(false);
                    setHoveredAccommodationId(null);
                    setSelectedMapAccommodation(null);
                  }}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {accommodations.length === 0 ? (
                <div className="text-center py-8 flex-1">
                  <MapPin className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-500">No accommodations found for this project</p>
                </div>
              ) : (
                <div className="flex flex-row flex-1 min-h-0 gap-4 p-6">
                  {/* Left side - List of accommodations */}
                  <div className="w-1/3 flex flex-col min-h-0">
                    <div className="mb-2 flex-shrink-0">
                      <p className="text-sm text-slate-600">
                        Found {accommodations.length} accommodation{accommodations.length !== 1 ? 's' : ''} for this project
                      </p>
                    </div>
                    <h3 className="font-bold text-sm text-slate-700 mb-2 flex-shrink-0">Accommodations List:</h3>
                    <div className="overflow-y-auto flex-1 space-y-2 pr-2">
                      {accommodations.map((acc, index) => (
                        <div
                          key={acc.id}
                          className={`flex items-start p-3 rounded-lg cursor-pointer transition-all duration-200 border ${
                            hoveredAccommodationId === acc.id
                              ? 'bg-brand-50 ring-2 ring-brand-500 shadow-md border-brand-300'
                              : 'bg-slate-50 hover:bg-slate-100 border-slate-200'
                          }`}
                          onMouseEnter={() => setHoveredAccommodationId(acc.id)}
                          onMouseLeave={() => setHoveredAccommodationId(null)}
                          onClick={() => setSelectedMapAccommodation(acc)}
                        >
                          <div className="flex-shrink-0 mr-3">
                            <div className={`w-8 h-8 text-white rounded-full flex items-center justify-center text-sm font-bold transition-all duration-200 ${
                              hoveredAccommodationId === acc.id ? 'bg-brand-500 scale-110' : 'bg-brand-500'
                            }`}>
                              {index + 1}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{acc.name || `Accommodation ${index + 1}`}</div>
                            {(acc.address || acc.city) && (
                              <div className="text-sm text-slate-600 mt-1">
                                {acc.address && <div className="truncate">{acc.address}</div>}
                                {acc.city && <div className="truncate">{acc.city}{acc.country ? `, ${acc.country}` : ''}</div>}
                              </div>
                            )}
                            {acc.capacity && (
                              <div className="text-sm text-slate-500 mt-1">
                                Capacity: {acc.capacity} beds
                              </div>
                            )}
                          </div>
                          <a
                            href={(() => {
                              // Use coordinates if available
                              if (acc.latitude && acc.longitude &&
                                  !isNaN(parseFloat(acc.latitude)) && !isNaN(parseFloat(acc.longitude))) {
                                return `https://www.openstreetmap.org/?mlat=${acc.latitude}&mlon=${acc.longitude}&zoom=17`;
                              }
                              // Otherwise search by address
                              return `https://www.openstreetmap.org/search?query=${encodeURIComponent(
                                `${acc.address || ''} ${acc.city || ''} ${acc.country || ''}`.trim()
                              )}`;
                            })()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-shrink-0 ml-2 text-brand-600 hover:text-brand-800"
                            onClick={(e) => e.stopPropagation()}
                            title="Open in OpenStreetMap"
                          >
                            <MapPin className="h-5 w-5" />
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right side - Interactive OpenStreetMap */}
                  <div className="w-2/3 border rounded-xl overflow-hidden relative">
                    {(() => {
                      // Get current project data
                      const currentProject = projects.find(p => p.id == selectedProject);

                      // Get project coordinates if available
                      let projectCoords = null;
                      if (currentProject?.latitude && currentProject?.longitude &&
                          !isNaN(parseFloat(currentProject.latitude)) && !isNaN(parseFloat(currentProject.longitude))) {
                        projectCoords = { lat: parseFloat(currentProject.latitude), lng: parseFloat(currentProject.longitude) };
                      }

                      // Helper to get coordinates (from DB or geocoded)
                      const getCoords = (acc) => {
                        if (acc.latitude && acc.longitude &&
                            !isNaN(parseFloat(acc.latitude)) && !isNaN(parseFloat(acc.longitude))) {
                          return { lat: parseFloat(acc.latitude), lng: parseFloat(acc.longitude) };
                        }
                        return geocodedCoordinates[acc.id] || null;
                      };

                      // Find accommodations with valid coordinates
                      const accommodationCoords = accommodations
                        .map(acc => getCoords(acc))
                        .filter(coords => coords !== null);

                      // Combine project and accommodation coordinates for map bounds
                      const withCoords = projectCoords
                        ? [projectCoords, ...accommodationCoords]
                        : accommodationCoords;

                      // Default center (Europe) if no coordinates
                      const defaultCenter = [52.5, 13.4];

                      return (
                        <MapContainer
                          center={withCoords.length > 0 ? [withCoords[0].lat, withCoords[0].lng] : defaultCenter}
                          zoom={6}
                          style={{ width: '100%', height: '100%' }}
                          scrollWheelZoom={true}
                        >
                          <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          />
                          <MapFitBounds coordinates={withCoords} />

                          {/* Project marker */}
                          {projectCoords && (
                            <Marker
                              position={[projectCoords.lat, projectCoords.lng]}
                              icon={createProjectIcon()}
                            >
                              <Popup>
                                <div className="p-1 max-w-xs">
                                  <div className="font-bold text-base mb-1 text-red-600">
                                    {currentProject?.name || 'Project'}
                                  </div>
                                  <div className="text-sm text-slate-600">Project Location</div>
                                  {currentProject?.country && (
                                    <div className="text-sm text-slate-500 mt-1">
                                      {currentProject.country}
                                    </div>
                                  )}
                                  <a
                                    href={`https://www.openstreetmap.org/?mlat=${projectCoords.lat}&mlon=${projectCoords.lng}&zoom=17`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-block mt-2 text-sm text-brand-600 hover:text-brand-800"
                                  >
                                    Open in OpenStreetMap
                                  </a>
                                </div>
                              </Popup>
                            </Marker>
                          )}

                          {accommodations.map((acc, index) => {
                            // Get coordinates from DB or geocoded cache
                            let coords = null;
                            if (acc.latitude && acc.longitude &&
                                !isNaN(parseFloat(acc.latitude)) && !isNaN(parseFloat(acc.longitude))) {
                              coords = { lat: parseFloat(acc.latitude), lng: parseFloat(acc.longitude) };
                            } else if (geocodedCoordinates[acc.id]) {
                              coords = geocodedCoordinates[acc.id];
                            }

                            // Skip if no coordinates available
                            if (!coords) return null;

                            const isHovered = hoveredAccommodationId === acc.id;
                            const isSelected = selectedMapAccommodation?.id === acc.id;

                            return (
                              <Marker
                                key={acc.id}
                                position={[coords.lat, coords.lng]}
                                icon={createNumberedIcon(index + 1, isHovered || isSelected)}
                                eventHandlers={{
                                  click: () => setSelectedMapAccommodation(acc),
                                  mouseover: () => setHoveredAccommodationId(acc.id),
                                  mouseout: () => setHoveredAccommodationId(null)
                                }}
                              >
                                <Popup>
                                  <div className="p-1 max-w-xs">
                                    <div className="font-bold text-base mb-1">
                                      {acc.name || 'Accommodation'}
                                    </div>
                                    {acc.address && (
                                      <div className="text-sm text-slate-600">{acc.address}</div>
                                    )}
                                    {acc.city && (
                                      <div className="text-sm text-slate-600">
                                        {acc.city}
                                        {acc.country ? `, ${acc.country}` : ''}
                                      </div>
                                    )}
                                    {acc.capacity && (
                                      <div className="text-sm text-slate-500 mt-2">
                                        Capacity: {acc.capacity} beds
                                      </div>
                                    )}
                                    {acc.phone && (
                                      <div className="text-sm text-brand-600 mt-1">
                                        📞 {acc.phone}
                                      </div>
                                    )}
                                    <a
                                      href={`https://www.openstreetmap.org/?mlat=${coords.lat}&mlon=${coords.lng}&zoom=17`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-block mt-2 text-sm text-brand-600 hover:text-brand-800"
                                    >
                                      Open in OpenStreetMap
                                    </a>
                                  </div>
                                </Popup>
                              </Marker>
                            );
                          })}
                        </MapContainer>
                      );
                    })()}

                    {/* Loading indicator for geocoding */}
                    {isGeocoding && (
                      <div className="absolute top-2 left-2 bg-brand-50 border border-brand-200 rounded-xl px-3 py-1 text-xs text-brand-700 flex items-center">
                        <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                        Locating addresses...
                      </div>
                    )}

                    {/* Warning for accommodations without coordinates */}
                    {!isGeocoding && accommodations.some(acc => {
                      const hasDbCoords = acc.latitude && acc.longitude &&
                        !isNaN(parseFloat(acc.latitude)) && !isNaN(parseFloat(acc.longitude));
                      const hasGeocodedCoords = geocodedCoordinates[acc.id];
                      return !hasDbCoords && !hasGeocodedCoords;
                    }) && (
                      <div className="absolute bottom-2 left-2 right-2 bg-yellow-50 border border-yellow-200 rounded-xl p-2 text-xs text-yellow-700">
                        <AlertTriangle className="h-3 w-3 inline mr-1" />
                        Some accommodations couldn't be located on the map.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Floating Action Bar for Unsaved Changes */}
      {hasUnsavedChanges && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ease-in-out">
          <div className="bg-white shadow-2xl rounded-xl border border-steel-200 px-6 py-4 flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="bg-yellow-100 rounded-full p-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-slate-900">
                  Unsaved changes
                </span>
                <span className="text-xs text-slate-500">
                  {pendingChanges.updated.length + pendingChanges.deleted.length + pendingChanges.created.length} changes
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-2 border-l border-steel-200 pl-4">
              <button
                onClick={saveAllChanges}
                className="px-5 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 font-medium text-sm transition-colors shadow-sm"
              >
                Save
              </button>
              <button
                onClick={cancelAllChanges}
                className="px-5 py-2 bg-steel-200 text-slate-700 rounded-xl hover:bg-steel-300 font-medium text-sm transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save History Modal */}
      {showSaveHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-steel-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-900">Save History</h3>
              <button
                onClick={() => setShowSaveHistoryModal(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {saveHistoryList.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No save history available
                </div>
              ) : (
                <div className="space-y-4">
                  {saveHistoryList.map((save, index) => {
                    const totalChanges = save.assignments_created + save.assignments_updated + save.assignments_deleted;
                    const savedBy = save.saved_by_data?.first_name && save.saved_by_data?.last_name
                      ? `${save.saved_by_data.first_name} ${save.saved_by_data.last_name}`
                      : save.saved_by_data?.username || save.saved_by_data?.email || 'Unknown User';

                    return (
                      <div key={save.id} className="border border-steel-200 rounded-xl p-4 hover:border-steel-300 transition-colors">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            <div className="flex items-center justify-center w-8 h-8 bg-green-100 text-green-700 rounded-full font-semibold text-sm">
                              #{saveHistoryList.length - index}
                            </div>
                            <div>
                              <div className="font-medium text-slate-900">{savedBy}</div>
                              <div className="text-sm text-slate-500">{formatDateTime(new Date(save.saved_at))}</div>
                            </div>
                          </div>
                        </div>

                        {/* Detailed changes */}
                        {save.change_details && (
                          <div className="mt-2 space-y-2">
                            {/* Created assignments */}
                            {save.change_details.created && save.change_details.created.length > 0 && (
                              <div className="text-xs">
                                {save.change_details.created.map((item, idx) => (
                                  <div key={idx} className="text-slate-600 py-0.5">
                                    <span className="font-semibold text-green-700">Created:</span> • <span className="font-medium">{item.user_name || `User #${item.user_id}`}</span> at <span className="font-medium">{item.accommodation_name || `Accommodation #${item.accommodation_id}`}</span>, Room {item.room_number}
                                    <span className="ml-2 text-slate-500">({item.start_date} to {item.end_date})</span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Updated assignments */}
                            {save.change_details.updated && save.change_details.updated.length > 0 && (
                              <div className="text-xs">
                                {save.change_details.updated.map((item, idx) => (
                                  <div key={idx} className="text-slate-600 py-0.5">
                                    <span className="font-semibold text-brand-700">Updated:</span> • <span className="font-medium">{item.user_name || `User #${item.user_id}`}</span> at <span className="font-medium">{item.accommodation_name || `Accommodation #${item.accommodation_id}`}</span>, Room {item.room_number}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Deleted assignments */}
                            {save.change_details.deleted && save.change_details.deleted.length > 0 && (
                              <div className="text-xs text-slate-600 py-0.5">
                                <span className="font-semibold text-red-700">Deleted:</span> • {save.change_details.deleted.length} assignment{save.change_details.deleted.length !== 1 ? 's' : ''} removed
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Date Range Modal */}
      {showDateRangeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">Set Date Range</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  value={tempDateRange.start ? format(tempDateRange.start, 'yyyy-MM-dd') : ''}
                  onChange={(e) => {
                    const newStart = new Date(e.target.value);
                    if (!isNaN(newStart.getTime())) {
                      setTempDateRange(prev => ({ ...prev, start: newStart }));
                    }
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">End Date</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  value={tempDateRange.end ? format(tempDateRange.end, 'yyyy-MM-dd') : ''}
                  onChange={(e) => {
                    const newEnd = new Date(e.target.value);
                    if (!isNaN(newEnd.getTime())) {
                      setTempDateRange(prev => ({ ...prev, end: newEnd }));
                    }
                  }}
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowDateRangeModal(false)}
                className="px-4 py-2 text-slate-600 hover:bg-steel-100 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (tempDateRange.start && tempDateRange.end) {
                    setCustomDateRange({
                      start: tempDateRange.start,
                      end: tempDateRange.end
                    });
                  }
                  setShowDateRangeModal(false);
                }}
                className="px-4 py-2 bg-brand-500 text-white rounded-xl hover:bg-brand-700 transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ghost element for dragging assignments - full-width clone */}
      {dragState && dragState.originalAssignment && dragState.assignmentWidth && (
        <div
          className="fixed pointer-events-none z-50 bg-brand-500 text-white rounded shadow-2xl border-2 border-brand-700"
          style={{
            left: `${mousePosition.x - dragState.clickOffsetX}px`, // Align with click offset
            top: `${mousePosition.y - 16}px`, // Center vertically on cursor
            width: `${dragState.assignmentWidth}px`, // Same width as original
            height: '32px', // Same height as assignment bar
            opacity: 0.85,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 8px',
            boxSizing: 'border-box'
          }}
        >
          <div className="text-xs font-semibold truncate flex-1">
            {dragState.originalAssignment.user_details?.first_name} {dragState.originalAssignment.user_details?.last_name}
          </div>
          <div className="text-xs opacity-90 whitespace-nowrap ml-2">
            {format(new Date(dragState.originalAssignment.start_date), 'MMM d')} - {format(new Date(dragState.originalAssignment.end_date), 'MMM d')}
          </div>
        </div>
      )}

      {/* Ghost element for resizing assignments */}
      {resizeState && resizeState.originalAssignment && resizeState.assignmentWidth && (() => {
        const rect = timelineRef.current?.getBoundingClientRect();
        if (!rect) return null;

        const dayWidth = getDayWidth(); // Use actual measured width
        const dayShift = Math.round(resizeState.deltaX / dayWidth);

        // Calculate new dates based on handle
        let newStartDate = new Date(resizeState.originalAssignment.start_date);
        let newEndDate = new Date(resizeState.originalAssignment.end_date);

        if (resizeState.handle === 'left') {
          newStartDate.setDate(newStartDate.getDate() + dayShift);
        } else if (resizeState.handle === 'right') {
          newEndDate.setDate(newEndDate.getDate() + dayShift);
        }

        // Check if user is available for the entire new date range
        const userId = resizeState.originalAssignment.user;
        const isAvailable = isUserAvailableInProject(userId, newStartDate, newEndDate);

        // Choose color based on availability
        const ghostBgColor = isAvailable ? 'bg-green-500' : 'bg-red-500';
        const ghostBorderColor = isAvailable ? 'border-green-700' : 'border-red-700';

        // Calculate new width and left position based on which handle is being dragged
        let newWidth, newLeft;

        if (resizeState.handle === 'left') {
          // LEFT handle: position moves, width changes inversely
          // If we drag left (negative deltaX), width increases
          // If we drag right (positive deltaX), width decreases
          newWidth = resizeState.assignmentWidth - resizeState.deltaX;
          newLeft = rect.left + resizeState.assignmentLeft + resizeState.deltaX;
        } else {
          // RIGHT handle: position stays, width changes directly
          // If we drag right (positive deltaX), width increases
          // If we drag left (negative deltaX), width decreases
          newWidth = resizeState.assignmentWidth + resizeState.deltaX;
          newLeft = rect.left + resizeState.assignmentLeft;
        }

        const newTop = rect.top + resizeState.assignmentTop;

        return (
          <div
            className={`fixed pointer-events-none z-50 text-white shadow-2xl border-2 ${ghostBgColor} ${ghostBorderColor}`}
            style={{
              left: `${newLeft}px`,
              top: `${newTop}px`,
              width: `${Math.max(40, newWidth)}px`, // Minimum width of 1 day
              height: `${resizeState.assignmentHeight}px`,
              opacity: 0.7,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 8px',
              boxSizing: 'border-box',
              borderRadius: '0px'
            }}
          >
            <div className="text-xs font-semibold truncate flex-1">
              {resizeState.originalAssignment.user_details?.first_name} {resizeState.originalAssignment.user_details?.last_name}
            </div>
            <div className="text-xs opacity-90 whitespace-nowrap ml-2">
              {format(newStartDate, 'MMM d')} - {format(newEndDate, 'MMM d')}
            </div>
          </div>
        );
      })()}
      </div>

      {/* Cell Click Dropdown - Quick User Assignment */}
      {cellClickDropdown && (
        <>
          {/* Backdrop to close dropdown */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setCellClickDropdown(null)}
          />
          {/* Dropdown */}
          <div
            className="fixed z-50 bg-white rounded-xl shadow-xl border border-steel-200 py-1 min-w-[250px] max-w-[350px] max-h-[300px] overflow-y-auto"
            style={{
              left: `${Math.min(cellClickDropdown.x, window.innerWidth - 360)}px`,
              top: `${Math.min(cellClickDropdown.y, window.innerHeight - 310)}px`
            }}
          >
            {/* Header */}
            <div className="px-3 py-2 border-b border-steel-100 bg-steel-50">
              <div className="text-xs font-semibold text-slate-700">
                Assign to Room {cellClickDropdown.roomNumber}
              </div>
              <div className="text-xs text-slate-500">
                {cellClickDropdown.accommodationName} • {format(cellClickDropdown.date, 'MMM d, yyyy')}
              </div>
            </div>

            {/* User List */}
            {(() => {
              const availableUsers = getAvailableUsersForDay(
                cellClickDropdown.date,
                cellClickDropdown.accommodationId,
                cellClickDropdown.roomNumber
              );

              if (availableUsers.length === 0) {
                return (
                  <div className="px-3 py-4 text-center text-slate-500 text-sm">
                    No users available on this day
                  </div>
                );
              }

              return availableUsers.map(user => (
                <button
                  key={user.id}
                  className="w-full px-3 py-2 text-left hover:bg-brand-50 flex items-center justify-between gap-2"
                  onClick={() => handleUserSelectFromDropdown(user)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">
                      {user.name}
                    </div>
                    <div className="text-xs text-slate-500">
                      {user.fullStartDate && user.fullEndDate ? (
                        <span>
                          {format(user.fullStartDate, 'MMM d')} - {format(user.fullEndDate, 'MMM d')}
                        </span>
                      ) : (
                        <span>No dates</span>
                      )}
                    </div>
                  </div>
                  {user.hasTravelOnDay && (
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      user.travelType === 'check_in' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                    }`}>
                      {user.travelType === 'check_in' ? '→ in' : '← out'}
                    </span>
                  )}
                </button>
              ));
            })()}
          </div>
        </>
      )}

    </DndProvider>
  );
};

export default AccommodationCalendar;